<template>
  <div class="dashboard-container">
    <!-- SIDEBAR -->
    <Sidebar @open-contacts="openContactsModal" />

    <!-- MAIN -->
    <main class="main">
      <!-- TOPBAR -->
      <div class="topbar">
        <div class="topbar-right">
          <div class="topbar-date">{{ getCurrentDate() }}</div>
          <div class="topbar-month">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            {{ getCurrentMonthYear() }}
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- CONTENT -->
      <div class="content">

      <!-- This Month Section -->
      <div class="section-card">
        <div class="section-header">
          <div class="section-title-container">
            <h2>This Month - {{ getCurrentMonth() }}</h2>
            <div class="property-selector">
              <label for="currentProperty">Property:</label>
              <select 
                id="currentProperty"
                v-model="selectedCurrentProperty" 
                @change="onCurrentPropertyChange"
                class="property-select-small"
                :disabled="loading"
              >
                <option v-for="property in properties" :key="property.id" :value="property.id">
                  {{ property.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
        
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Loading current month data...</p>
        </div>
        
        <div v-else class="properties-section">
          <div v-for="(reservations, unitName) in groupedCurrentMonth" :key="unitName" class="property-group">
            <div class="property-header">
              <h3>{{ unitName }}</h3>
              <div class="property-stats">
                <span class="stat">{{ reservations.length }} bookings</span>
                <span class="stat">{{ getTotalNights(reservations) }} nights</span>
                <span class="stat revenue">{{ formatCurrency(getTotalRevenue(reservations)) }}</span>
              </div>

              <div class="property-actions">
                <button @click="downloadPropertyExcel(unitName, 'current')" class="btn btn-sm btn-excel">
                  Excel
                </button>
                <button @click="downloadReportPropertyExcel(unitName, 'current')" class="btn btn-sm btn-report-excel">
                  Report Excel
                </button>
                <button @click="openReportModal(unitName, 'current')" class="btn btn-sm btn-report">
                  View Report
                </button>
              </div>
            </div>
            
            <div class="table-container">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Guest Name</th>
                    <th>Source</th>
                    <th>Check-in</th>
                    <th>Check-out</th>
                    <th>Nights</th>
                    <th>Total Amount</th>
                    <th>Services</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="reservation in reservations" :key="reservation.id" :class="{ 'segment-row': reservation.isSegment || reservation.is_segment }">
                <td class="guest-name">
                  {{ reservation.guest_name || reservation.guestName }}
                <span v-if="(reservation.isSegment || reservation.is_segment) && (reservation.segmentInfo?.total || reservation.segment_info?.total) > 1" class="segment-badge" :title="getSegmentTooltip(reservation)">
  SPLIT {{ reservation.segmentInfo?.part || reservation.segment_info?.part }}/{{ reservation.segmentInfo?.total || reservation.segment_info?.total }}
</span>
                </td>
                                  <td>
                      <span class="source-badge">{{ reservation.channel_name || reservation.otaChannelName || 'Direct' }}</span>
                    </td>
                    <td>{{ formatDate(reservation.arrival_date || reservation.arrivalDate) }}</td>
                    <td>{{ formatDate(reservation.departure_date || reservation.departureDate) }}</td>
                    <td class="text-center">{{ reservation.total_nights || reservation.totalNights }}</td>
                    <td class="amount">{{ formatCurrency(reservation.total_reservation_price || reservation.totalReservationPrice) }}</td>
                    <td class="amount">{{ formatCurrency(reservation.total_services_price || reservation.totalServicesPrice) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div v-if="Object.keys(groupedCurrentMonth).length === 0" class="empty-state">
            <p>No reservations found for this month</p>
          </div>
        </div>
      </div>

      <!-- Last Month Section -->
      <div class="section-card">
        <div class="section-header">
          <div class="section-title-container">
            <h2>Last Month - {{ getLastMonth() }}</h2>
            <div class="property-selector">
              <label for="lastMonthProperty">Property:</label>
              <select 
                id="lastMonthProperty"
                v-model="selectedLastMonthProperty" 
                @change="onLastMonthPropertyChange"
                class="property-select-small"
                :disabled="loadingLastMonth"
              >
                <option v-for="property in properties" :key="property.id" :value="property.id">
                  {{ property.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
        
        <div v-if="loadingLastMonth" class="loading-state">
          <div class="spinner"></div>
          <p>Loading last month data...</p>
        </div>
        
        <div v-else class="properties-section">
          <div v-for="(reservations, unitName) in groupedLastMonth" :key="unitName" class="property-group">
            <div class="property-header">
              <h3>{{ unitName }}</h3>
              <div class="property-stats">
                <span class="stat">{{ reservations.length }} bookings</span>
                <span class="stat">{{ getTotalNights(reservations) }} nights</span>
                <span class="stat revenue">{{ formatCurrency(getTotalRevenue(reservations)) }}</span>
              </div>
              <div class="property-actions">
                <button @click="downloadReportPropertyExcel(unitName, 'lastmonth')" class="btn btn-sm btn-report-excel">
                  Report Excel
                </button>
                <button @click="downloadPropertyExcel(unitName, 'lastmonth')" class="btn btn-sm btn-excel">
                  Excel
                </button>
                <button @click="openReportModal(unitName, 'lastmonth')" class="btn btn-sm btn-report">
                  View Report
                </button>
              </div>
            </div>
            
            <div class="table-container">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Guest Name</th>
                    <th>Source</th>
                    <th>Check-in</th>
                    <th>Check-out</th>
                    <th>Nights</th>
                    <th>Total Amount</th>
                    <th>Services</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="reservation in reservations" :key="reservation.id" :class="{ 'segment-row': reservation.isSegment || reservation.is_segment }">
  <td class="guest-name">
    {{ reservation.guest_name || reservation.guestName }}
    <span v-if="(reservation.isSegment || reservation.is_segment)" class="segment-badge" :title="getSegmentTooltip(reservation)">
      SPLIT {{ reservation.segmentInfo?.part || reservation.segment_info?.part }}/{{ reservation.segmentInfo?.total || reservation.segment_info?.total }}
    </span>
  </td>
                    <td>
                      <span class="source-badge">{{ reservation.channel_name || reservation.otaChannelName || 'Direct' }}</span>
                    </td>
                    <td>{{ formatDate(reservation.arrival_date || reservation.arrivalDate) }}</td>
                    <td>{{ formatDate(reservation.departure_date || reservation.departureDate) }}</td>
                    <td class="text-center">{{ reservation.total_nights || reservation.totalNights }}</td>
                    <td class="amount">{{ formatCurrency(reservation.total_reservation_price || reservation.totalReservationPrice) }}</td>
                    <td class="amount">{{ formatCurrency(reservation.total_services_price || reservation.totalServicesPrice) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div v-if="Object.keys(groupedLastMonth).length === 0" class="empty-state">
            <p>No reservations found for last month</p>
          </div>
        </div>
      </div>

      <!-- History Section -->
      <div class="section-card">
        <div class="section-header">
          <h2>Reservations History</h2>
        </div>
        
        <div class="filters-section">
          <div class="filter-group">
            <label>Property:</label>
            <select v-model="selectedProperty" class="filter-select">
              <option value="">All Properties</option>
              <option v-for="property in properties" :key="property.id" :value="property.id">
                {{ property.name }}
              </option>
            </select>
          </div>
          
          <div class="filter-group">
            <label>Month:</label>
            <select v-model="selectedMonth" class="filter-select">
              <option value="">Select a month</option>
              <option v-for="month in availableMonths" :key="month.value" :value="month.value">
                {{ month.label }}
              </option>
            </select>
          </div>
          
          <div class="filter-actions">
            <button @click="applyFilters" class="btn btn-search">Search</button>
            <button @click="clearFilters" class="btn btn-outline">Clear Filters</button>
          </div>
        </div>

        <!-- Historical Reports Section - Only show when results are available -->
        <div v-if="searchResults.length > 0 && appliedFilters.month" class="historical-reports-section">
          <div class="property-group">
            <div class="property-header">
              <h3>{{ getSelectedPropertyName() || 'All Properties' }}</h3>
              <div class="property-stats">
                <span class="stat">{{ searchResults.length }} bookings</span>
                <span class="stat">{{ getTotalNights(searchResults) }} nights</span>
                <span class="stat revenue">{{ formatCurrency(getTotalRevenue(searchResults)) }}</span>
              </div>

              <div class="property-actions">
                <button @click="downloadPropertyExcel(getSelectedPropertyName() || 'All_Properties', 'historical')" class="btn btn-sm btn-excel">
                  Excel
                </button>
                <button @click="downloadReportPropertyExcel(getSelectedPropertyName() || 'All_Properties', 'historical')" class="btn btn-sm btn-report-excel">
                  Report Excel
                </button>
                <button @click="openReportModal(getSelectedPropertyName() || 'All_Properties', 'historical')" class="btn btn-sm btn-report">
                  View Report
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div v-if="loadingHistory" class="loading-state">
          <div class="spinner"></div>
          <p>Loading reservation history...</p>
        </div>
        
        <div v-else class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Guest Name</th>
                <th>Source</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Nights</th>
                <th>Total Amount</th>
                <th>Services</th>
                <th>Booked At</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="reservation in paginatedHistory" :key="reservation.id" :class="{ 'segment-row': reservation.isSegment }">
                <td class="property-name">{{ reservation.unitName }}</td>
                <td class="guest-name">
                  {{ reservation.guestName }}
                  <span v-if="reservation.isSegment && reservation.segmentInfo.total > 1" class="segment-badge" :title="getSegmentTooltip(reservation)">
                    SPLIT {{ reservation.segmentInfo.part }}/{{ reservation.segmentInfo.total }}
                  </span>
                </td>
                <td>
                  <span class="source-badge">{{ reservation.otaChannelName || 'Direct' }}</span>
                </td>
                <td>{{ formatDate(reservation.arrivalDate) }}</td>
                <td>{{ formatDate(reservation.departureDate) }}</td>
                <td class="text-center">{{ reservation.totalNights }}</td>
                <td class="amount">{{ formatCurrency(reservation.totalReservationPrice) }}</td>
                <td class="amount">{{ formatCurrency(reservation.totalServicesPrice) }}</td>
                <td>{{ formatDate(reservation.bookedAt) }}</td>
              </tr>
            </tbody>
          </table>
          
          <div class="pagination" v-if="searchResults.length > itemsPerPage">
            <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn">
              Previous
            </button>
            <span class="pagination-info">
              Page {{ currentPage }} of {{ totalPages }} ({{ searchResults.length }} total)
            </span>
            <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-btn">
              Next
            </button>
          </div>
          
          <div v-if="searchResults.length === 0 && (appliedFilters.property || appliedFilters.month)" class="empty-state">
            <p>No reservations found for the selected filters</p>
          </div>

          <div v-if="searchResults.length === 0 && !appliedFilters.property && !appliedFilters.month" class="empty-state">
            <p>Use the search filters above to find reservations</p>
          </div>
        </div>
      </div>
      </div>
    </main>

    <!-- Report Modal -->
    <div v-if="showReportModal" class="report-modal-overlay" @click="closeReportModal">
      <div class="report-modal" @click.stop>
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="modal-title">
            <h2>{{ modalData.propertyName }} - {{ modalData.periodLabel }} Report</h2>
            <div class="modal-subtitle" v-if="hasUnsavedChanges">
              <span class="unsaved-indicator">●</span> Unsaved changes
            </div>
            <div class="modal-subtitle" v-else>
              <span class="saved-indicator">●</span> All changes saved
            </div>
          </div>
          <button @click="closeReportModal" class="close-btn">×</button>
        </div>

        <div class="modal-content">
          <!-- Settings Panel (Left Side) -->
          <div class="settings-panel">
            <div class="settings-header">
              <h3>Report Settings</h3>
              <p>Adjust parameters and see changes in real-time</p>
            </div>

            <div class="settings-form">
              <!-- Management Fee Configuration -->
              <div class="setting-group">
                <h4 class="subsection-title">Management Fee Configuration</h4>
                
                <div class="form-row">
                  <label class="form-label">Management Fee Rate (%)</label>
                  <div class="input-group">
                
                <input 
                    type="number" 
                    v-model="modalSettings.managementFeeRate" 
                    @input="handleNumericInput('managementFeeRate', $event); markAsModified(); calculateMetrics()"
                    @blur="ensureMinimumValue('managementFeeRate', 0)"
                    step="0.1" 
                    min="0" 
                    max="100"
                    class="form-input"
                  >
                    <span class="input-suffix">%</span>
                  </div>
                </div>

                <div class="form-row">
                  <label class="form-label">Calculation Base</label>
               <div class="radio-group">
  <label class="radio-option">
    <input 
      type="radio" 
      v-model="modalSettings.managementFeeBase" 
      value="revenue"
      @change="markAsModified(); calculateMetrics()"
    >
    <span>Total Revenue (Gross Sales)</span>
  </label>
  <label class="radio-option">
    <input 
      type="radio" 
      v-model="modalSettings.managementFeeBase" 
      value="net"
      @change="markAsModified(); calculateMetrics()"
    >
    <span>Net Income (after platform fees)</span>
  </label>
  <label class="radio-option">
    <input 
      type="radio" 
      v-model="modalSettings.managementFeeBase" 
      value="gross"
      @change="markAsModified(); calculateMetrics()"
    >
    <span>Gross Profit (after all expenses) - percentage</span>
  </label>
  <label class="radio-option">
    <input 
      type="radio" 
      v-model="modalSettings.managementFeeBase" 
      value="grossFixed"
      @change="markAsModified(); calculateMetrics()"
    >
    <span>Gross Profit (after all expenses) - fixed amount</span>
  </label>
</div>

                <!-- Fixed Management Fee Amount (conditional) -->
                <div v-if="modalSettings.managementFeeBase === 'grossFixed'" class="form-row">
                  <label class="form-label">
                    Fixed Management Fee Amount (£)
                    <span v-if="hasMonthSpecificFee" class="month-specific-badge" title="This value is specific to this month only">
                      (Month-specific)
                    </span>
                  </label>
                  <div class="input-group">
                    <span class="input-prefix">£</span>
                    <input
                      type="number"
                      v-model="modalSettings.managementFeeFixedAmount"
                      @input="handleNumericInput('managementFeeFixedAmount', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('managementFeeFixedAmount', 0)"
                      step="0.01"
                      min="0"
                      class="form-input"
                    >
                  </div>
                </div>

                </div>
              </div>

              <!-- VAT & Fixed Costs -->
              <div class="setting-group">
                <h4 class="subsection-title">VAT & Fixed Costs</h4>
                
                <div class="form-row">
                  <label class="form-label">VAT Rate (%)</label>
                  <div class="input-group">
                  <input 
                      type="number" 
                      v-model="modalSettings.vatRate" 
                      @input="handleNumericInput('vatRate', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('vatRate', 0)"
                      step="0.1" 
                      min="0" 
                      max="100"
                      class="form-input"
                    >
                    <span class="input-suffix">%</span>
                  </div>
                </div>

                <div class="form-row">
                  <label class="form-label">Maintenance (£)</label>
                  
                  <!-- Custom maintenance toggle -->
                  <div class="checkbox-row">
                    <label class="checkbox-option">
                      <input 
                        type="checkbox" 
                        v-model="modalSettings.useCustomMaintenance"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Use custom amount for this month</span>
                    </label>
                  </div>
                  
                  <div class="input-group">
                    <span class="input-prefix">£</span>
                    <input
                      type="number"
                      v-model="modalSettings.customMaintenanceAmount"
                      v-if="modalSettings.useCustomMaintenance"
                      @input="handleNumericInput('customMaintenanceAmount', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('customMaintenanceAmount', 0)"
                      step="0.01"
                      min="0"
                      class="form-input"
                      placeholder="Custom amount for this month"
                    >
                    <input
                      type="number"
                      v-model="modalSettings.maintenance"
                      v-else
                      @input="handleNumericInput('maintenance', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('maintenance', 0)"
                      step="0.01"
                      min="0"
                      class="form-input"
                      placeholder="Default amount from settings"
                    >
                  </div>
                  
                  <p v-if="!modalSettings.useCustomMaintenance" class="setting-description">
                    Using default amount (£{{ modalSettings.maintenance }}) from property settings
                  </p>
                </div>

                <div class="form-row">
                  <label class="form-label">Consumables (£)</label>

                  <!-- Custom consumables toggle -->
                  <div class="checkbox-row">
                    <label class="checkbox-option">
                      <input
                        type="checkbox"
                        v-model="modalSettings.useCustomConsumables"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Use custom amount for this month</span>
                    </label>
                  </div>

                  <div class="input-group">
                    <span class="input-prefix">£</span>
                    <input
                      type="number"
                      v-model="modalSettings.customConsumablesAmount"
                      v-if="modalSettings.useCustomConsumables"
                      @input="handleNumericInput('customConsumablesAmount', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('customConsumablesAmount', 0)"
                      step="0.01"
                      min="0"
                      class="form-input"
                      placeholder="Custom amount for this month"
                    >
                    <input
                      type="number"
                      v-model="modalSettings.consumables"
                      v-else
                      @input="handleNumericInput('consumables', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('consumables', 0)"
                      step="0.01"
                      min="0"
                      class="form-input"
                      placeholder="Default amount from settings"
                    >
                  </div>

                  <p v-if="!modalSettings.useCustomConsumables" class="setting-description">
                    Using default amount (£{{ modalSettings.consumables }}) from property settings
                  </p>
                </div>

                <div class="form-row">
                  <label class="form-label">Laundry & Linen Supply</label>

                  <!-- Laundry Calculation Mode -->
                  <div class="radio-group">
                    <label class="radio-option">
                      <input
                        type="radio"
                        value="fixed"
                        v-model="modalSettings.laundryCalculationMode"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Fixed Monthly Amount</span>
                    </label>
                    <label class="radio-option">
                      <input
                        type="radio"
                        value="per_booking"
                        v-model="modalSettings.laundryCalculationMode"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Per Booking (£ × bookings)</span>
                    </label>
                  </div>

                  <!-- Fixed Monthly Amount Mode -->
                  <div v-if="modalSettings.laundryCalculationMode === 'fixed'" class="input-group">
                    <span class="input-prefix">£</span>
                    <input
                      type="number"
                      v-model="modalSettings.laundry"
                      @input="handleNumericInput('laundry', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('laundry', 0)"
                      step="0.01"
                      min="0"
                      class="form-input"
                      placeholder="Fixed monthly amount"
                    >
                  </div>

                  <!-- Per Booking Mode -->
                  <div v-if="modalSettings.laundryCalculationMode === 'per_booking'" class="laundry-per-booking-container">
                    <!-- Cost per booking input -->
                    <div class="input-group">
                      <label class="form-sub-label">Cost per booking:</label>
                      <span class="input-prefix">£</span>
                      <input
                        type="number"
                        v-model="modalSettings.laundryPerBooking"
                        @input="handleNumericInput('laundryPerBooking', $event); markAsModified(); calculateMetrics()"
                        @blur="ensureMinimumValue('laundryPerBooking', 0)"
                        step="0.01"
                        min="0"
                        class="form-input"
                        placeholder="Amount per booking"
                      >
                    </div>

                    <!-- Booking counter -->
                    <div class="booking-counter-group">
                      <label class="form-sub-label">Number of bookings:</label>
                      <div class="counter-controls">
                        <button
                          type="button"
                          class="counter-btn"
                          @click="decrementLaundryBookings"
                          :disabled="laundryBookingCount <= 0"
                        >−</button>
                        <input
                          type="number"
                          v-model.number="laundryBookingCount"
                          @input="onLaundryBookingCountChange"
                          min="0"
                          class="counter-input"
                        >
                        <button
                          type="button"
                          class="counter-btn"
                          @click="incrementLaundryBookings"
                        >+</button>
                      </div>

                      <!-- Info about auto-detection -->
                      <p class="counter-info">
                        <span class="info-icon">ℹ️</span>
                        Auto-detected: {{ autoDetectedBookingCount }} booking{{ autoDetectedBookingCount !== 1 ? 's' : '' }} this month
                        <span v-if="laundryBookingCount !== autoDetectedBookingCount" class="adjustment-badge">
                          ({{ laundryBookingCount > autoDetectedBookingCount ? '+' : '' }}{{ laundryBookingCount - autoDetectedBookingCount }} manually adjusted)
                        </span>
                      </p>

                      <!-- Total calculation display -->
                      <div class="total-display">
                        <strong>Total Laundry:</strong>
                        £{{ (laundryBookingCount * parseFloat(modalSettings.laundryPerBooking || 0)).toFixed(2) }}
                        <span class="calculation-hint">
                          ({{ laundryBookingCount }} × £{{ parseFloat(modalSettings.laundryPerBooking || 0).toFixed(2) }})
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="form-row">
                  <label class="form-label">Other Expenses (£)</label>

                  <!-- Custom other expenses toggle -->
                  <div class="checkbox-row">
                    <label class="checkbox-option">
                      <input
                        type="checkbox"
                        v-model="modalSettings.useCustomOtherExpenses"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Use custom amount for this month</span>
                    </label>
                  </div>

                  <div class="input-group">
                    <span class="input-prefix">£</span>
                    <input
                      type="number"
                      v-model="modalSettings.customOtherExpensesAmount"
                      v-if="modalSettings.useCustomOtherExpenses"
                      @input="handleNumericInput('customOtherExpensesAmount', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('customOtherExpensesAmount', 0)"
                      step="0.01"
                      min="0"
                      class="form-input"
                      placeholder="Custom amount for this month"
                    >
                    <input
                      type="number"
                      v-model="modalSettings.otherExpenses"
                      v-else
                      @input="handleNumericInput('otherExpenses', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('otherExpenses', 0)"
                      step="0.01"
                      min="0"
                      class="form-input"
                      placeholder="Default amount from settings"
                    >
                  </div>

                  <p v-if="!modalSettings.useCustomOtherExpenses" class="setting-description">
                    Using default amount (£{{ modalSettings.otherExpenses }}) from property settings
                  </p>

                  <!-- Add Other Maintenance Button -->
                  <button @click="openExpenseModal" class="btn btn-add-expense" type="button">
                    + Add Other Maintenance
                  </button>

                  <!-- Liste des dépenses ajoutées -->
                  <div v-if="propertyExpenses.length > 0" class="expenses-list">
                    <div v-for="expense in propertyExpenses" :key="expense.id" class="expense-item">
                      <span class="expense-desc">{{ expense.description }}</span>
                      <span class="expense-amount">£{{ parseFloat(expense.amount).toFixed(2) }}</span>
                      <span v-if="expense.receipt_file" class="expense-receipt" title="Receipt attached">📎</span>
                      <button @click="deleteExpense(expense.id)" class="btn-delete" type="button" title="Delete expense">🗑️</button>
                    </div>
                    <div class="expense-total">
                      <strong>Total Additional Expenses: £{{ totalAdditionalExpenses.toFixed(2) }}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Cleaning Fee Configuration -->
              <div class="setting-group">
                <h4 class="subsection-title">Cleaning Fee Configuration</h4>

                <div class="form-row">
                  <label class="form-label">Cleaning Fee Mode</label>
                  <div class="radio-group">
                    <label class="radio-option">
                      <input
                        type="radio"
                        v-model="modalSettings.cleaningMode"
                        value="rentlio"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Use Rentlio Data</span>
                    </label>
                    <label class="radio-option">
                      <input
                        type="radio"
                        v-model="modalSettings.cleaningMode"
                        value="monthly"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Custom Rates per Platform</span>
                    </label>
                    <label class="radio-option">
                      <input
                        type="radio"
                        v-model="modalSettings.cleaningMode"
                        value="fixed"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Fixed Monthly Amount</span>
                    </label>
                    <label class="radio-option">
                      <input
                        type="radio"
                        v-model="modalSettings.cleaningMode"
                        value="per_booking"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Per Booking (£ × bookings)</span>
                    </label>
                  </div>
                </div>

                <!-- Custom Rates per Platform Mode -->
                <div v-if="modalSettings.cleaningMode === 'monthly'" class="cleaning-custom-rates">
                  <div class="form-row">
                    <label class="form-label">Booking.com Rate (£)</label>
                    <div class="input-group">
                      <span class="input-prefix">£</span>
                      <input
                        type="number"
                        v-model="modalSettings.bookingCleaningFee"
                        @input="handleNumericInput('bookingCleaningFee', $event); markAsModified(); calculateMetrics()"
                        @blur="ensureMinimumValue('bookingCleaningFee', 0)"
                        step="0.01"
                        min="0"
                        class="form-input"
                      >
                      <span class="input-suffix">per booking</span>
                    </div>
                  </div>

                  <div class="form-row">
                    <label class="form-label">Other Platforms Rate (£)</label>
                    <div class="input-group">
                      <span class="input-prefix">£</span>
                      <input
                        type="number"
                        v-model="modalSettings.otherCleaningFee"
                        @input="handleNumericInput('otherCleaningFee', $event); markAsModified(); calculateMetrics()"
                        @blur="ensureMinimumValue('otherCleaningFee', 0)"
                        step="0.01"
                        min="0"
                        class="form-input"
                      >
                      <span class="input-suffix">per booking</span>
                    </div>
                  </div>
                </div>

                <!-- Fixed Monthly Amount Mode -->
                <div v-if="modalSettings.cleaningMode === 'fixed'" class="input-group">
                  <span class="input-prefix">£</span>
                  <input
                    type="number"
                    v-model="modalSettings.cleaningFee"
                    @input="handleNumericInput('cleaningFee', $event); markAsModified(); calculateMetrics()"
                    @blur="ensureMinimumValue('cleaningFee', 0)"
                    step="0.01"
                    min="0"
                    class="form-input"
                    placeholder="Fixed monthly cleaning cost"
                  >
                </div>

                <!-- Per Booking Mode -->
                <div v-if="modalSettings.cleaningMode === 'per_booking'" class="cleaning-per-booking-container">
                  <!-- Cost per booking input -->
                  <div class="input-group">
                    <label class="form-sub-label">Cost per booking:</label>
                    <span class="input-prefix">£</span>
                    <input
                      type="number"
                      v-model="modalSettings.cleaningPerBooking"
                      @input="handleNumericInput('cleaningPerBooking', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('cleaningPerBooking', 0)"
                      step="0.01"
                      min="0"
                      class="form-input"
                      placeholder="Amount per booking"
                    >
                  </div>

                  <!-- Booking counter -->
                  <div class="booking-counter-group">
                    <label class="form-sub-label">Number of bookings:</label>
                    <div class="counter-controls">
                      <button
                        type="button"
                        class="counter-btn"
                        @click="decrementCleaningBookings"
                        :disabled="cleaningBookingCount <= 0"
                      >−</button>
                      <input
                        type="number"
                        v-model.number="cleaningBookingCount"
                        @input="onCleaningBookingCountChange"
                        min="0"
                        class="counter-input"
                      >
                      <button
                        type="button"
                        class="counter-btn"
                        @click="incrementCleaningBookings"
                      >+</button>
                    </div>

                    <!-- Info about auto-detection -->
                    <p class="counter-info">
                      <span class="info-icon">ℹ️</span>
                      Auto-detected: {{ autoDetectedBookingCount }} booking{{ autoDetectedBookingCount !== 1 ? 's' : '' }} this month
                      <span v-if="cleaningBookingCount !== autoDetectedBookingCount" class="adjustment-badge">
                        ({{ cleaningBookingCount > autoDetectedBookingCount ? '+' : '' }}{{ cleaningBookingCount - autoDetectedBookingCount }} manually adjusted)
                      </span>
                    </p>

                    <!-- Total calculation display -->
                    <div class="total-display">
                      <strong>Total Cleaning:</strong>
                      <span class="total-amount">£{{ (parseFloat(modalSettings.cleaningPerBooking || 0) * cleaningBookingCount).toFixed(2) }}</span>
                      <span class="calculation-detail">
                        (£{{ parseFloat(modalSettings.cleaningPerBooking || 0).toFixed(2) }} × {{ cleaningBookingCount }} bookings)
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Card Fee Configuration -->
              <div class="setting-group">
                <h4 class="subsection-title">Card Fee Configuration</h4>
                
                <div class="form-row">
                  <label class="form-label">Card Fee Mode</label>
                  <div class="radio-group">
                    <label class="radio-option">
                      <input 
                        type="radio" 
                        v-model="modalSettings.cardFeeMode" 
                        value="rentlio"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Use Rentlio Data</span>
                    </label>
                    <label class="radio-option">
                      <input 
                        type="radio" 
                        v-model="modalSettings.cardFeeMode" 
                        value="custom"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Custom Rate</span>
                    </label>
                  </div>
                </div>

                <div v-if="modalSettings.cardFeeMode === 'custom'" class="card-custom-rates">
                  <div class="form-row">
                    <label class="form-label">Card Fee Rate (%)</label>
                    <div class="input-group">
                   
                   <input 
                      type="number" 
                      v-model="modalSettings.customCardFeeRate" 
                      @input="handleNumericInput('customCardFeeRate', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('customCardFeeRate', 0)"
                      step="0.01" 
                      min="0" 
                      max="10"
                      class="form-input"
                    >


                      <span class="input-suffix">%</span>
                    </div>
                  </div>

                  <div class="form-row">
                    <label class="form-label">Fixed Fee (£)</label>
                    <div class="input-group">
                      <span class="input-prefix">£</span>
                      
                      <input 
                      type="number" 
                      v-model="modalSettings.customCardFeeFixed" 
                      @input="handleNumericInput('customCardFeeFixed', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('customCardFeeFixed', 0)"
                      step="0.01" 
                      min="0"
                      class="form-input"
                    >


                    </div>
                  </div>
                </div>
              </div>

              <!-- Platform Commission Configuration -->
              <div class="setting-group">
                <h4 class="subsection-title">Platform Commission Configuration</h4>

                <div class="form-row">
                  <label class="form-label">Commission Mode</label>
                  <div class="radio-group">
                    <label class="radio-option">
                      <input
                        type="radio"
                        v-model="modalSettings.platformFeeMode"
                        value="rentlio"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Use Rentlio Data</span>
                    </label>
                    <label class="radio-option">
                      <input
                        type="radio"
                        v-model="modalSettings.platformFeeMode"
                        value="custom"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Custom by Platform</span>
                    </label>
                  </div>
                </div>

                <div v-if="modalSettings.platformFeeMode === 'custom'" class="platform-custom-rates">
                  <!-- Booking.com -->
                  <div class="form-row checkbox-row">
                    <label class="checkbox-label">
                      <input
                        type="checkbox"
                        v-model="modalSettings.applyCustomBookingCom"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Apply custom for Booking.com</span>
                    </label>
                  </div>

                  <div v-if="modalSettings.applyCustomBookingCom" class="form-row indent">
                    <label class="form-label">Booking.com Commission (%)</label>
                    <div class="input-group">
                      <input
                        type="number"
                        v-model="modalSettings.bookingComCommissionRate"
                        @input="markAsModified(); calculateMetrics()"
                        step="0.01"
                        min="0"
                        max="100"
                        class="form-input"
                      >
                      <span class="input-suffix">%</span>
                    </div>
                  </div>

                  <!-- Airbnb -->
                  <div class="form-row checkbox-row">
                    <label class="checkbox-label">
                      <input
                        type="checkbox"
                        v-model="modalSettings.applyCustomAirbnb"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Apply custom for Airbnb</span>
                    </label>
                  </div>

                  <div v-if="modalSettings.applyCustomAirbnb" class="form-row indent">
                    <label class="form-label">Airbnb Commission (%)</label>
                    <div class="input-group">
                      <input
                        type="number"
                        v-model="modalSettings.airbnbCommissionRate"
                        @input="markAsModified(); calculateMetrics()"
                        step="0.01"
                        min="0"
                        max="100"
                        class="form-input"
                      >
                      <span class="input-suffix">%</span>
                    </div>
                  </div>

                  <!-- Other Platforms -->
                  <div class="form-row checkbox-row">
                    <label class="checkbox-label">
                      <input
                        type="checkbox"
                        v-model="modalSettings.applyCustomOther"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Apply custom for Other Platforms</span>
                    </label>
                  </div>

                  <div v-if="modalSettings.applyCustomOther" class="form-row indent">
                    <label class="form-label">Other Platforms Commission (%)</label>
                    <div class="input-group">
                      <input
                        type="number"
                        v-model="modalSettings.otherPlatformCommissionRate"
                        @input="markAsModified(); calculateMetrics()"
                        step="0.01"
                        min="0"
                        max="100"
                        class="form-input"
                      >
                      <span class="input-suffix">%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Settings Actions -->
            <div class="settings-actions">
              <button @click="resetToSaved" :disabled="!hasUnsavedChanges" class="btn btn-outline">
                Reset to Saved
              </button>
              <button @click="saveSettings" :disabled="!hasUnsavedChanges || savingSettings" class="btn btn-primary">
                <span v-if="savingSettings">Saving...</span>
                <span v-else>Save Settings</span>
              </button>
              <button
                v-if="modalSettings.managementFeeBase === 'grossFixed' && hasFixedAmountChanged"
                @click="saveForThisMonthOnly"
                :disabled="!hasFixedAmountChanged || savingSettings"
                class="btn btn-secondary"
                title="Save this fixed fee amount for this month only, without affecting other months"
              >
                <span v-if="savingSettings">Saving...</span>
                <span v-else>Save for This Month Only</span>
              </button>
            </div>
          </div>

          <!-- Report Preview (Right Side) -->
          <div class="preview-panel">
            <div class="preview-header">
              <h3>Report Preview</h3>
              <div class="preview-actions">
                <button @click="downloadPDF" :disabled="generatingPDF" class="btn btn-pdf">
                  <span v-if="generatingPDF">Generating...</span>
                  <span v-else>Download PDF</span>
                </button>
                <button @click="sendReportByEmailFromModal" class="btn btn-lock-report" style="margin-left: 10px;">
                  🔒 Lock Report
                </button>
              </div>
            </div>

<div class="preview-content" style="background: #ffffff; padding: 10px; overflow-y: auto; overflow-x: hidden;">
  <div class="pdf-preview-wrapper" style="
    width: 210mm; 
    min-height: 297mm; 
    background: white; 
    margin: 0 auto; 
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
    transform-origin: top center;
    transform: scale(1.3);
    border: 1px solid #e0e0e0;
  ">
    <div v-html="previewHTML" style="width: 100%; height: 100%;"></div>
  </div>
</div>
</div>
        </div>
      </div>
    </div>

    <!-- Professional Contacts Management Modal -->
    <div v-if="showContactsModal" class="contacts-modal-overlay" @click="closeContactsModal">
      <div class="contacts-modal-container" @click.stop>
        <!-- Header -->
        <div class="contacts-modal-header">
          <div class="header-content">
            <div class="header-icon">
              <div class="icon-circle">
                <span class="contacts-icon">👥</span>
              </div>
            </div>
            <div class="header-text">
              <h2>Email Contacts</h2>
              <p>Manage your email recipients for reports</p>
            </div>
          </div>
          <button @click="closeContactsModal" class="modal-close-btn">
            <span>&times;</span>
          </button>
        </div>

        <!-- Body -->
        <div class="contacts-modal-body">
          <!-- Add New Contact Section -->
          <div class="add-contact-card">
            <div class="card-header">
              <h3>➕ Add New Contact</h3>
              <p>Create a new email contact for sending reports</p>
            </div>
            <div class="card-content">
              <div class="add-contact-form">
                <div class="input-group">
                  <div class="input-container">
                    <label class="input-label">👤 Contact Name</label>
                    <input 
                      v-model="newContact.name" 
                      placeholder="Enter full name..." 
                      class="professional-input"
                      type="text"
                    >
                  </div>
                  <div class="input-container">
                    <label class="input-label">📧 Email Address</label>
                    <input 
                      v-model="newContact.email" 
                      placeholder="Enter email address..." 
                      class="professional-input"
                      type="email"
                    >
                  </div>
                </div>
                <button 
                  @click="addContact" 
                  :disabled="!newContact.name || !newContact.email"
                  class="add-contact-btn"
                >
                  <span class="btn-icon">✨</span>
                  <span>Add Contact</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Contacts List Section -->
          <div class="contacts-list-card">
            <div class="card-header">
              <div class="header-with-count">
                <h3>📋 Saved Contacts</h3>
                <span class="contact-count">{{ contacts.length }} contact{{ contacts.length !== 1 ? 's' : '' }}</span>
              </div>
            </div>
            
            <div class="card-content">
              <!-- Empty State -->
              <div v-if="contacts.length === 0" class="empty-contacts-state">
                <div class="empty-icon">📭</div>
                <h4>No contacts yet</h4>
                <p>Add your first email contact above to start sending reports</p>
              </div>
              
              <!-- Contacts Grid -->
              <div v-else class="contacts-grid">
                <div 
                  v-for="contact in contacts" 
                  :key="contact.id" 
                  class="contact-item"
                  :class="{ 'editing': editingContact?.id === contact.id }"
                >
                  <!-- Contact Avatar -->
                  <div class="contact-avatar">
                    {{ contact.name.charAt(0).toUpperCase() }}
                  </div>
                  
                  <!-- Contact Info / Edit Form -->
                  <div class="contact-details">
                    <div v-if="editingContact?.id === contact.id" class="edit-form">
                      <input 
                        v-model="editingContact.name" 
                        class="edit-input"
                        placeholder="Contact name..."
                      >
                      <input 
                        v-model="editingContact.email" 
                        class="edit-input"
                        type="email"
                        placeholder="Email address..."
                      >
                    </div>
                    <div v-else class="contact-info">
                      <div class="contact-name">{{ contact.name }}</div>
                      <div class="contact-email">{{ contact.email }}</div>
                      <div class="contact-meta">
                        Added {{ formatContactDate(contact.createdAt) }}
                      </div>
                    </div>
                  </div>
                  
                  <!-- Actions -->
                  <div class="contact-actions">
                    <div v-if="editingContact?.id === contact.id" class="edit-actions">
                      <button @click="saveEditContact" class="action-btn save-btn">
                        <span>💾</span>
                      </button>
                      <button @click="editingContact = null" class="action-btn cancel-btn">
                        <span>❌</span>
                      </button>
                    </div>
                    <div v-else class="view-actions">
                      <button @click="editContact(contact)" class="action-btn edit-btn">
                        <span>✏️</span>
                      </button>
                      <button @click="deleteContact(contact.id)" class="action-btn delete-btn">
                        <span>🗑️</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="contacts-modal-footer">
          <div class="footer-info">
            <span class="info-text">
              <span class="info-icon">💾</span>
              Contacts are saved locally in your browser
            </span>
          </div>
          <div class="footer-actions">
            <button @click="closeContactsModal" class="close-modal-btn">
              <span class="btn-icon">✓</span>
              <span>Done</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Professional Email Sending Modal -->
    <div v-if="showEmailModal" class="email-modal-overlay" @click="closeEmailModal">
      <div class="email-modal-container" @click.stop>
        <!-- Header -->
        <div class="email-modal-header">
          <div class="header-content">
            <div class="header-icon">
              <div class="icon-circle">
                <span class="email-icon">📧</span>
              </div>
            </div>
            <div class="header-text">
              <h2>Send Report via Email</h2>
              <p>Share your property report with stakeholders</p>
            </div>
          </div>
          <button @click="closeEmailModal" class="modal-close-btn">
            <span>&times;</span>
          </button>
        </div>

        <!-- Body -->
        <div class="email-modal-body">
          <!-- Report Preview Card -->
          <div class="report-preview-card">
            <div class="card-header">
              <h3>📊 Report Summary</h3>
            </div>
            <div class="card-content">
              <div class="report-details">
                <div class="detail-item">
                  <div class="detail-icon">🏨</div>
                  <div class="detail-content">
                    <span class="detail-label">Property</span>
                    <span class="detail-value">{{ emailData.propertyName }}</span>
                  </div>
                </div>
                <div class="detail-item">
                  <div class="detail-icon">📅</div>
                  <div class="detail-content">
                    <span class="detail-label">Period</span>
                    <span class="detail-value">{{ emailData.period }}</span>
                  </div>
                </div>
                <div class="detail-item">
                  <div class="detail-icon">📋</div>
                  <div class="detail-content">
                    <span class="detail-label">Report Type</span>
                    <span class="detail-value">{{ emailData.reportType }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recipient Selection -->
          <div class="recipient-section">
            <div class="section-title">
              <h3>👤 Select Recipient</h3>
              <p>Choose who will receive this report</p>
            </div>
            
            <div class="recipient-selector">
              <div class="contact-dropdown">
                <select v-model="emailData.contact" class="professional-select">
                  <option :value="null" disabled>Choose a contact...</option>
                  <option v-for="contact in contacts" :key="contact.id" :value="contact">
                    {{ contact.name }} • {{ contact.email }}
                  </option>
                </select>
              </div>
              
              <button @click="openContactsModal" class="manage-contacts-btn">
                <span class="btn-icon">👥</span>
                <span>Manage Contacts</span>
              </button>
            </div>

            <!-- Selected Contact Preview -->
            <div v-if="emailData.contact" class="selected-contact-preview">
              <div class="contact-card">
                <div class="contact-avatar">
                  {{ emailData.contact.name.charAt(0).toUpperCase() }}
                </div>
                <div class="contact-info">
                  <div class="contact-name">{{ emailData.contact.name }}</div>
                  <div class="contact-email">{{ emailData.contact.email }}</div>
                </div>
                <div class="contact-status">
                  <span class="status-badge">✓ Selected</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Email Options -->
          <div class="email-options-section">
            <div class="section-title">
              <h3>⚙️ Email Options</h3>
            </div>
            
            <div class="options-grid">
              <div class="option-card">
                <div class="option-header">
                  <span class="option-icon">📎</span>
                  <span class="option-title">Include Attachments</span>
                </div>
                <div class="option-content">
                  <label class="checkbox-container">
                    <input type="checkbox" v-model="emailOptions.includeExcel" checked>
                    <span class="checkmark"></span>
                    Excel Report
                  </label>
                  <label class="checkbox-container">
                    <input type="checkbox" v-model="emailOptions.includePDF">
                    <span class="checkmark"></span>
                    PDF Report
                  </label>
                </div>
              </div>

              <div class="option-card">
                <div class="option-header">
                  <span class="option-icon">💬</span>
                  <span class="option-title">Message</span>
                </div>
                <div class="option-content">
                  <textarea 
                    v-model="emailOptions.message" 
                    class="message-textarea"
                    placeholder="Add a personal message (optional)..."
                    rows="3"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="email-modal-footer">
          <div class="footer-info">
            <span class="info-text">
              <span class="info-icon">🔒</span>
              Your data is sent securely
            </span>
          </div>
          <div class="footer-actions">
            <button @click="closeEmailModal" class="cancel-btn">
              Cancel
            </button>
            <button 
              @click="sendReportByEmail" 
              :disabled="!emailData.contact || sendingEmail"
              :class="['send-btn', { 'disabled': !emailData.contact || sendingEmail }]"
            >
              <span v-if="sendingEmail">📧 Sending...</span>
              <span v-else>Send Report</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Email Sent Popup -->
    <div v-if="showEmailSentPopup" class="email-sent-popup-overlay">
      <div class="email-sent-popup">
        <div class="popup-icon">
          <div class="success-checkmark">
            <div class="check-icon">
              <span class="icon-line line-tip"></span>
              <span class="icon-line line-long"></span>
              <div class="icon-circle"></div>
              <div class="icon-fix"></div>
            </div>
          </div>
        </div>

        <div class="popup-content">
          <h2>Email Sent Successfully!</h2>
          <p>Your report has been sent to <strong>{{ emailData.contact?.name }}</strong></p>
          <p class="delivery-info">The email will arrive in the recipient's inbox within the next 5 minutes.</p>
        </div>

        <button @click="closeEmailSentPopup" class="popup-close-btn">
          Got it
        </button>
      </div>
    </div>

    <!-- Lock Report Modal (Milestone 2 - Xero Integration) -->
    <div v-if="showLockModal" class="lock-modal-overlay" @click="closeLockModal">
      <div class="lock-modal-container" @click.stop>
        <!-- Header -->
        <div class="lock-modal-header">
          <div class="header-icon">
            <div class="icon-circle">
              <span>🔒</span>
            </div>
          </div>
          <div class="header-text">
            <h2>Lock Report for Month-End</h2>
            <p>Prepare this report for sending via the Month-End screen</p>
          </div>
          <button @click="closeLockModal" class="modal-close-btn">
            <span>&times;</span>
          </button>
        </div>

        <!-- Body -->
        <div class="lock-modal-body">
          <!-- Report Summary Card -->
          <div class="report-summary-card">
            <div class="summary-row">
              <span class="summary-label">🏨 Property:</span>
              <span class="summary-value">{{ lockModalData.propertyName }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">📅 Period:</span>
              <span class="summary-value">{{ lockModalData.monthYear }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">👤 Owner:</span>
              <span class="summary-value">{{ lockModalData.ownerName }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">📧 Email:</span>
              <span class="summary-value email-highlight">{{ lockModalData.ownerEmail }}</span>
            </div>
          </div>

          <!-- Important Notice -->
          <div class="warning-box">
            <div class="warning-icon">⚠️</div>
            <div class="warning-content">
              <h3>Important:</h3>
              <ul>
                <li>This report will be <strong>locked</strong> and cannot be regenerated</li>
                <li>It will appear in the <strong>Month-End screen</strong> as "Ready to send"</li>
                <li>The email will be sent to <strong>{{ lockModalData.ownerEmail }}</strong></li>
                <li>To change this email, go to <strong>Property Settings → Owner Information</strong></li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="lock-modal-footer">
          <button @click="closeLockModal" class="cancel-btn">
            Cancel
          </button>
          <button @click="confirmLockReport" class="confirm-btn">
            🔒 Confirm & Lock Report
          </button>
        </div>
      </div>
    </div>

    <!-- Notification Modal (replaces alerts) -->
    <div v-if="showNotificationModal" class="notification-modal-overlay" @click="closeNotificationModal">
      <div class="notification-modal-container" @click.stop>
        <!-- Header with Icon -->
        <div class="notification-modal-header" :class="notificationData.type">
          <div class="notification-icon-circle" :class="notificationData.type">
            <span class="notification-icon">{{ notificationData.icon }}</span>
          </div>
          <button @click="closeNotificationModal" class="modal-close-btn">
            <span>&times;</span>
          </button>
        </div>

        <!-- Body -->
        <div class="notification-modal-body">
          <h2 class="notification-title">{{ notificationData.title }}</h2>
          <p class="notification-message" v-html="notificationData.message"></p>

          <!-- Details (optional) -->
          <div v-if="notificationData.details" class="notification-details">
            <div v-for="(value, key) in notificationData.details" :key="key" class="detail-row">
              <span class="detail-label">{{ key }}:</span>
              <span class="detail-value">{{ value }}</span>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="notification-modal-footer">
          <button @click="closeNotificationModal" class="notification-btn" :class="notificationData.type">
            OK
          </button>
        </div>
      </div>
    </div>

    <!-- Expense Modal -->
    <div v-if="showExpenseModal" class="expense-modal-overlay" @click="closeExpenseModal">
      <div class="expense-modal" @click.stop>
        <div class="expense-modal-header">
          <h3>Add Other Maintenance</h3>
          <button @click="closeExpenseModal" class="close-btn">×</button>
        </div>

        <div class="expense-modal-body">
          <div class="form-group">
            <label class="form-label">Description *</label>
            <input
              v-model="newExpense.description"
              type="text"
              class="form-input"
              placeholder="e.g. Plumber repair"
              maxlength="255"
            >
          </div>

          <!-- VAT Treatment Toggle -->
          <div class="form-group">
            <label class="form-label">VAT Treatment</label>
            <div class="vat-toggle-group">
              <label class="vat-toggle-option" :class="{ active: !newExpense.is_vat_applicable }">
                <input type="radio" :value="false" v-model="newExpense.is_vat_applicable" style="display:none;">
                No VAT &mdash; Agent / Disbursement
              </label>
              <label class="vat-toggle-option" :class="{ active: newExpense.is_vat_applicable }">
                <input type="radio" :value="true" v-model="newExpense.is_vat_applicable" style="display:none;">
                VAT Applicable &mdash; Supplied Service
              </label>
            </div>
            <p class="form-hint">Select "VAT Applicable" if this cost is supplied by your company and VAT must be charged to the owner.</p>
          </div>

          <!-- Amount entry (gross si pas de VAT ou gross avec VAT incluse) -->
          <div class="form-group" v-if="!newExpense.is_vat_applicable || newExpense.vat_treatment === 'gross'">
            <label class="form-label">Amount (£) *</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input
                v-model="newExpense.amount"
                type="number"
                class="form-input"
                placeholder="0.00"
                step="0.01"
                min="0"
              >
            </div>
            <p class="form-hint" v-if="newExpense.is_vat_applicable && newExpense.vat_treatment === 'gross'">Gross amount including VAT — the system will extract the net and VAT automatically.</p>
          </div>

          <!-- Method selector si VAT applicable -->
          <div class="form-group" v-if="newExpense.is_vat_applicable">
            <label class="form-label">Amount Entry Method</label>
            <select v-model="newExpense.vat_treatment" class="form-input">
              <option value="gross">Gross Amount (VAT included)</option>
              <option value="net_plus_vat">Net + VAT Separated</option>
            </select>
          </div>

          <!-- Net + VAT séparés -->
          <div v-if="newExpense.is_vat_applicable && newExpense.vat_treatment === 'net_plus_vat'" class="form-row-two">
            <div class="form-group">
              <label class="form-label">Net Amount (£) *</label>
              <div class="input-group">
                <span class="input-prefix">£</span>
                <input
                  v-model="newExpense.net_amount"
                  type="number"
                  class="form-input"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                >
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">VAT Amount (£) *</label>
              <div class="input-group">
                <span class="input-prefix">£</span>
                <input
                  v-model="newExpense.vat_amount"
                  type="number"
                  class="form-input"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                >
              </div>
            </div>
          </div>

          <!-- Expense Category -->
          <div class="form-group">
            <label class="form-label">Expense Category *</label>
            <select v-model="newExpense.expense_category" class="form-input">
              <option value="maintenance">Maintenance</option>
              <option value="cleaning">Cleaning</option>
              <option value="other">Other</option>
            </select>
            <p class="form-hint">
              "Cleaning" = no VAT (agent disbursement). "Maintenance" or "Other" = VAT applies if the company supplies the service.
            </p>
          </div>

          <div class="form-group">
            <label class="form-label">Receipt (Optional)</label>
            <input
              @change="handleReceiptUpload"
              type="file"
              class="form-input-file"
              accept="image/*,application/pdf"
              ref="receiptFileInput"
            >
            <p v-if="newExpense.receipt_filename" class="file-selected">
              ✓ File selected: {{ newExpense.receipt_filename }}
            </p>
          </div>
        </div>

        <div class="expense-modal-footer">
          <button @click="closeExpenseModal" class="btn btn-outline">Cancel</button>
          <button
            @click="saveExpense"
            :disabled="!newExpense.description || (!newExpense.amount && !(newExpense.is_vat_applicable && newExpense.vat_treatment === 'net_plus_vat' && newExpense.net_amount)) || savingExpense"
            class="btn btn-primary"
          >
            <span v-if="savingExpense">Saving...</span>
            <span v-else>Add Expense</span>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import * as XLSX from 'xlsx';
import SETTINGS from '@/config/settings.js';
import formulas from '@/config/formulas.js';
import Sidebar from '@/components/Sidebar.vue';

export default {
  name: 'ReservationsView',
  components: {
    Sidebar
  },
  data() {
    return {
      // Données existantes
      isCollapsed: false,
      loading: false,
      loadingLastMonth: false,
      loadingHistory: false,
      currentMonthReservations: [],
      lastMonthReservations: [],
      historyReservations: [],
      properties: [],
      selectedCurrentProperty: '31510',
      selectedLastMonthProperty: '31510', 
      selectedProperty: '',
      selectedMonth: '',
      appliedFilters: {
        property: '',
        month: ''
      },
      searchResultsMonth: '', // Store the month that searchResults correspond to
      searchResults: [],
      currentPage: 1,
      itemsPerPage: 20,
      baseUrl: SETTINGS.API.baseUrl,

      // Modal state
      showReportModal: false,
      modalData: {
        propertyName: '',
        periodLabel: '',
        reservations: [],
        realPropertyId: null // AJOUTER cette ligne

      },
      modalSettings: {
        managementFeeRate: 16,
        managementFeeBase: 'net',
        managementFeeFixedAmount: 500,
        vatRate: 20,
        maintenance: 0,
        useCustomMaintenance: false,
        customMaintenanceAmount: 0,
        consumables: 20,
        useCustomConsumables: false,
        customConsumablesAmount: 0,
        laundry: 0,
        laundryCalculationMode: 'fixed',
        laundryPerBooking: 15,
        otherExpenses: 0,
        useCustomOtherExpenses: false,
        customOtherExpensesAmount: 0,
        cleaningMode: 'rentlio',
        bookingCleaningFee: 150,
        otherCleaningFee: 120,
        cleaningFee: 0,
        cleaningPerBooking: 150,
        cardFeeMode: 'rentlio',
        customCardFeeRate: 2.9,
        customCardFeeFixed: 0.30,
        platformFeeMode: 'rentlio',
        applyCustomBookingCom: false,
        bookingComCommissionRate: 15.00,
        applyCustomAirbnb: false,
        airbnbCommissionRate: 3.00,
        applyCustomOther: false,
        otherPlatformCommissionRate: 10.00
      },
      originalSettings: {},
      allPropertySettings: {}, // Stocke tous les settings récupérés de l'API
      calculatedMetrics: {},
      previewHTML: '',
      hasUnsavedChanges: false,
      savingSettings: false,
      generatingPDF: false,
      hasMonthSpecificFee: false, // Indique si le fixed fee actuel est spécifique au mois

      // Email contacts management
      contacts: [],
      showContactsModal: false,
      showEmailModal: false,
      showEmailSentPopup: false,

      // Lock Report Modal (Milestone 2 - Xero Integration)
      showLockModal: false,
      lockModalData: {
        propertyName: '',
        monthYear: '',
        ownerName: '',
        ownerEmail: '',
        propertyId: null
      },
      selectedContact: null,
      newContact: {
        name: '',
        email: ''
      },
      editingContact: null,
      emailData: {
        contact: null,
        reportType: '',
        propertyName: '',
        period: ''
      },
      emailOptions: {
        includeExcel: false,
        includePDF: true,
        message: ''
      },
      sendingEmail: false,
      savedReportData: null, // Sauvegarde des données du rapport pour l'envoi d'email

      // Notification Modal (replaces alerts)
      showNotificationModal: false,
      notificationData: {
        type: 'success', // 'success', 'error', 'warning'
        icon: '✅',
        title: '',
        message: '',
        details: null
      },

      // Property Expenses (Other Maintenance)
      propertyExpenses: [],
      showExpenseModal: false,
      newExpense: {
        description: '',
        amount: 0,
        expense_category: 'maintenance',
        vat_treatment: 'gross',
        net_amount: 0,
        vat_rate: 20,
        vat_amount: 0,
        is_vat_applicable: false,
        receipt_file: null,
        receipt_filename: ''
      },
      savingExpense: false,

      // Laundry per-booking counter
      laundryBookingCount: 0,
      autoDetectedBookingCount: 0,

      // Cleaning per-booking counter
      cleaningBookingCount: 0
    };
  },

 computed: {
  groupedCurrentMonth() {
    const property = this.properties.find(p => p.id.toString() === this.selectedCurrentProperty.toString());
    const displayName = property ? property.name : 'Unknown Property';
    return { [displayName]: this.currentMonthReservations };
  },
  
  groupedLastMonth() {
    const property = this.properties.find(p => p.id.toString() === this.selectedLastMonthProperty.toString());
    const displayName = property ? property.name : 'Unknown Property';
    return { [displayName]: this.lastMonthReservations };
  },
  
  availableMonths() {
    const months = [];
    const now = new Date();

    // Ajouter les 12 derniers mois
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);

      // FIX: Utiliser getFullYear() et getMonth() au lieu de toISOString() pour éviter les problèmes de timezone
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() est 0-indexed
      const value = `${year}-${month}`; // Format YYYY-MM

      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

      let type = 'historical';
      if (i === 0) type = 'current';
      else if (i === 1) type = 'lastmonth';

      months.push({
        value: value,
        label: label,
        type: type
      });
    }

    return months;
  },

  paginatedHistory() {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.searchResults.slice(start, end);
  },

  totalPages() {
    return Math.ceil(this.searchResults.length / this.itemsPerPage);
  },

  hasFixedAmountChanged() {
    // Vérifie si seul le fixed amount a changé (pas les autres paramètres)
    if (!this.hasUnsavedChanges || this.modalSettings.managementFeeBase !== 'grossFixed') {
      return false;
    }
    // Compare uniquement le fixed amount avec l'original
    return parseFloat(this.modalSettings.managementFeeFixedAmount) !== parseFloat(this.originalSettings.managementFeeFixedAmount);
  },

  totalAdditionalExpenses() {
    return this.propertyExpenses.reduce((sum, expense) => {
      return sum + parseFloat(expense.amount || 0);
    }, 0);
  },

  calculatedVatAmount() {
    if (this.newExpense.vat_treatment === 'net_plus_vat') {
      const netAmount = parseFloat(this.newExpense.net_amount) || 0;
      const vatRate = parseFloat(this.newExpense.vat_rate) || 0;
      return (netAmount * (vatRate / 100)).toFixed(2);
    }
    return '0.00';
  },

  calculatedTotalAmount() {
    if (this.newExpense.vat_treatment === 'net_plus_vat') {
      const netAmount = parseFloat(this.newExpense.net_amount) || 0;
      const vatAmount = parseFloat(this.calculatedVatAmount) || 0;
      return (netAmount + vatAmount).toFixed(2);
    }
    return '0.00';
  }
},

  methods: {
    // Méthodes existantes
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
    },
    getSegmentTooltip(reservation) {
  if (!reservation.isSegment || !reservation.segmentInfo) {
    return '';
  }
  
  const info = reservation.segmentInfo;
  return `Multi-month reservation split - Part ${info.part} of ${info.total}\n` +
         `Original reservation: ${info.originalTotalNights} nights, £${info.originalTotalPrice}\n` +
         `Original ID: ${info.originalId}`;
},
    
    logout() {
      localStorage.removeItem('token');
      this.$router.push('/login');
    },
    
    formatCurrency(amount) {
      return SETTINGS.formatCurrency(amount || 0);
    },
    
    formatDate(timestamp) {
      return SETTINGS.formatDate(timestamp);
    },
    
    getCurrentMonth() {
      return new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    },
    
    getLastMonth() {
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      return lastMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    },

    getCurrentDate() {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const now = new Date();
      return `${days[now.getDay()]}, ${now.getDate()} ${months[now.getMonth()]} ${now.getFullYear()}`;
    },

    getCurrentMonthYear() {
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const now = new Date();
      return `${months[now.getMonth()]} ${now.getFullYear()}`;
    },


    getTotalNights(reservations) {
      return reservations.reduce((total, r) => total + r.totalNights, 0);
    },
    
    getTotalRevenue(reservations) {
      return reservations.reduce((total, r) => total + r.totalReservationPrice, 0);
    },

    getSelectedPropertyName() {
      if (!this.selectedProperty) {
        return null;
      }
      const property = this.properties.find(p => p.id.toString() === this.selectedProperty.toString());
      return property ? property.name : null;
    },

    getSelectedMonthLabel() {
      if (!this.selectedMonth) {
        return 'Historical Month';
      }
      const selectedMonthData = this.availableMonths.find(m => m.value === this.selectedMonth);
      return selectedMonthData ? selectedMonthData.label : 'Historical Month';
    },


    // Modal methods


    closeReportModal() {
      if (this.hasUnsavedChanges) {
        if (!confirm('You have unsaved changes. Are you sure you want to close?')) {
          return;
        }
      }

      this.showReportModal = false;
      this.modalData = {};
      this.modalSettings = {};
      this.originalSettings = {};
      this.calculatedMetrics = {};
      this.previewHTML = '';
      this.hasUnsavedChanges = false;
    },

    markAsModified() {
      this.hasUnsavedChanges = true;
    },

    // Laundry booking counter methods
    incrementLaundryBookings() {
      this.laundryBookingCount++;
      this.markAsModified();
      this.calculateMetrics();
    },

    decrementLaundryBookings() {
      if (this.laundryBookingCount > 0) {
        this.laundryBookingCount--;
        this.markAsModified();
        this.calculateMetrics();
      }
    },

    onLaundryBookingCountChange() {
      // Ensure it's a valid number
      if (isNaN(this.laundryBookingCount) || this.laundryBookingCount < 0) {
        this.laundryBookingCount = 0;
      }
      this.markAsModified();
      this.calculateMetrics();
    },

    // Cleaning booking counter methods
    incrementCleaningBookings() {
      this.cleaningBookingCount++;
      this.markAsModified();
      this.calculateMetrics();
    },

    decrementCleaningBookings() {
      if (this.cleaningBookingCount > 0) {
        this.cleaningBookingCount--;
        this.markAsModified();
        this.calculateMetrics();
      }
    },

    onCleaningBookingCountChange() {
      // Ensure it's a valid number
      if (isNaN(this.cleaningBookingCount) || this.cleaningBookingCount < 0) {
        this.cleaningBookingCount = 0;
      }
      this.markAsModified();
      this.calculateMetrics();
    },

    autoDetectBookingCount() {
      // Count bookings excluding extensions
      const reservations = this.modalData.reservations || [];
      const count = reservations.filter(r => {
        const guestName = (r.guestName || r.guest_name || '').toLowerCase();
        return !guestName.includes('extension');
      }).length;

      this.autoDetectedBookingCount = count;
      this.laundryBookingCount = count;
      this.cleaningBookingCount = count;
    },

    calculateMetrics() {
      if (!this.modalData.reservations || this.modalData.reservations.length === 0) {
        return;
      }

      const reservations = this.modalData.reservations;
      const settings = this.modalSettings;

      let totalRevenue = 0;
      let totalNights = 0;
      let totalPlatformFees = 0;
      let totalCardFees = 0;
      let totalOutgoings = 0;

      reservations.forEach(reservation => {
        const amount = parseFloat(reservation.totalReservationPrice || 0);
        const fees = this.calculateCustomCommission(reservation, settings);
        
        let cardFee = 0;
        if (settings.cardFeeMode === 'rentlio') {
          cardFee = (reservation.otaChannelName === 'Booking.com') ? parseFloat(reservation.cardFee || 0) : 0;
        } else {
          cardFee = (reservation.otaChannelName === 'Booking.com') ? 
            (amount * settings.customCardFeeRate / 100) : 0;
        }
        
        let outgoings = 0;
        const isExtension = (reservation.guestName || '').toLowerCase().includes('extension');

        if (isExtension) {
          outgoings = 0;
        } else {
          if (settings.cleaningMode === 'rentlio') {
            outgoings = parseFloat(reservation.totalServicesPrice || 0);
          } else if (settings.cleaningMode === 'monthly') {
            // Custom Rates: Appliquer les frais uniquement au dernier segment
            const isLastSegment = !reservation.isSegment ||
              (reservation.segmentInfo?.part === reservation.segmentInfo?.total);

            if (isLastSegment) {
              const platform = reservation.otaChannelName || '';
              if (platform === 'Booking.com') {
                outgoings = settings.bookingCleaningFee;
              } else {
                outgoings = settings.otherCleaningFee;
              }
            } else {
              // Segment intermédiaire, pas de frais de nettoyage
              outgoings = 0;
            }
          } else if (settings.cleaningMode === 'fixed') {
            // Fixed mode: cleaning is not per booking, handled separately
            outgoings = 0;
          } else if (settings.cleaningMode === 'per_booking') {
            // Per booking mode: cleaning is not per booking in loop, handled separately
            outgoings = 0;
          }
        }

        totalRevenue += amount;
       totalNights += parseInt(reservation.totalNights || 0);
       totalPlatformFees += fees;
       totalCardFees += cardFee;
       totalOutgoings += outgoings;
     });

     // Calculate cleaning as supplied service (like laundry) when in fixed/per_booking mode
     // Otherwise it stays as a cost in totalOutgoings (rentlio/monthly modes)
     let cleaningSupplied = 0;
     if (settings.cleaningMode === 'fixed') {
       cleaningSupplied = parseFloat(settings.cleaningFee || 0);
       // Supplied service - will be added to subTotal with VAT
     } else if (settings.cleaningMode === 'per_booking') {
       const cleaningPerBooking = parseFloat(settings.cleaningPerBooking || 0);
       cleaningSupplied = this.cleaningBookingCount * cleaningPerBooking;
       // Supplied service - will be added to subTotal with VAT
     }
     // Note: For 'rentlio' and 'monthly' modes, cleaning is already calculated
     // in the booking loop and included in totalOutgoings as a cost

     const netIncome = totalRevenue - totalPlatformFees - totalCardFees;
     const grossProfit = netIncome - totalOutgoings;

     let managementFees = 0;
      if (settings.managementFeeBase === 'revenue') {
        managementFees = totalRevenue * (settings.managementFeeRate / 100);
      } else if (settings.managementFeeBase === 'gross') {
        managementFees = grossProfit * (settings.managementFeeRate / 100);
      } else if (settings.managementFeeBase === 'grossFixed') {
        managementFees = parseFloat(settings.managementFeeFixedAmount || 0);
      } else {
        managementFees = netIncome * (settings.managementFeeRate / 100);
      }

     const maintenance = settings.useCustomMaintenance
       ? parseFloat(settings.customMaintenanceAmount || 0)
       : parseFloat(settings.maintenance || 0);
     const consumables = settings.useCustomConsumables
       ? parseFloat(settings.customConsumablesAmount || 0)
       : parseFloat(settings.consumables || 0);
     // Calculate laundry based on mode
     let laundry = 0;
     if (settings.laundryCalculationMode === 'per_booking') {
       const laundryPerBooking = parseFloat(settings.laundryPerBooking || 0);
       laundry = this.laundryBookingCount * laundryPerBooking;
     } else {
       // Fixed mode
       laundry = parseFloat(settings.laundry || 0);
     }
     const otherExpenses = settings.useCustomOtherExpenses
       ? parseFloat(settings.customOtherExpensesAmount || 0)
       : parseFloat(settings.otherExpenses || 0);

     // Calculate VAT using formulas.js (handles business model and VAT toggles)
     const vatBreakdown = formulas.calculateVATBreakdown({
       vatRate: settings.vatRate,
       businessModel: settings.vatBusinessModel,
       managementFees: managementFees,
       consumables: consumables,
       laundry: laundry,
       laundryVatApplicable: settings.laundryVatApplicable,
       cleaning: cleaningSupplied,
       cleaningVatApplicable: settings.cleaningVatApplicable,
       expenses: []  // Expenses handled separately
     });

     const vat = vatBreakdown.total_vat;
     const subTotal = vatBreakdown.total_taxable;

     // Ajouter le total des dépenses supplémentaires (Other Maintenance)
     const additionalExpenses = this.totalAdditionalExpenses;

     const settlementAmount = grossProfit - subTotal - vat - maintenance - otherExpenses - additionalExpenses;

     this.calculatedMetrics = {
       totalRevenue,
       totalNights,
       totalPlatformFees,
       totalCardFees,
       totalOutgoings,
       netIncome,
       grossProfit,
       managementFees,
       vat,
       maintenance,
       consumables,
       laundry,
       cleaning: cleaningSupplied,
       otherExpenses,
       additionalExpenses,
       settlementAmount,
       averagePerNight: reservations.length > 0 ? reservations.reduce((sum, r) => sum + (r.totalReservationPrice / r.totalNights), 0) / reservations.length : 0
     };

     this.generatePreviewHTML();
   },

generatePreviewHTML() {
  if (!this.modalData.reservations || !this.calculatedMetrics) {
    return;
  }

  const reservations = this.modalData.reservations;
  const calculations = this.calculatedMetrics;
  const settings = this.modalSettings;

  const currentDate = new Date();
  // Use the actual periodLabel instead of calculating dates
  // periodLabel already contains the correct month/year (e.g., "December 2025")
  let monthYear;
  if (this.modalData.periodLabel === 'Current Month') {
    monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  } else if (this.modalData.periodLabel === 'Last Month') {
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
    monthYear = lastMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  } else {
    // For historical months, use the periodLabel directly (e.g., "December 2025")
    monthYear = this.modalData.periodLabel;
  }

  const bookingsRows = reservations.map(reservation => {
    const amount = parseFloat(reservation.totalReservationPrice || 0);
    const fees = this.calculateCustomCommission(reservation, this.modalSettings);

    let cardFee = 0;
    if (settings.cardFeeMode === 'rentlio') {
      cardFee = (reservation.otaChannelName === 'Booking.com') ? parseFloat(reservation.cardFee || 0) : 0;
    } else {
      cardFee = (reservation.otaChannelName === 'Booking.com') ?
        (amount * settings.customCardFeeRate / 100) : 0;
    }

    let outgoings = 0;
    const isExtension = (reservation.guestName || '').toLowerCase().includes('extension');

    if (isExtension) {
      outgoings = 0;
    } else {
      if (settings.cleaningMode === 'rentlio') {
        outgoings = parseFloat(reservation.totalServicesPrice || 0);
      } else {
        // Custom Rates: Appliquer les frais uniquement au dernier segment
        const isLastSegment = !reservation.isSegment ||
          (reservation.segmentInfo?.part === reservation.segmentInfo?.total);

        if (isLastSegment) {
          const platform = reservation.otaChannelName || '';
          if (platform === 'Booking.com') {
            outgoings = settings.bookingCleaningFee || 150;
          } else {
            outgoings = settings.otherCleaningFee || 120;
          }
        } else {
          // Segment intermédiaire, pas de frais de nettoyage
          outgoings = 0;
        }
      }
    }
    
    const netIncome = amount - fees - cardFee;
    const grossProfit = netIncome - outgoings;

    return `
      <tr>
        <td style="text-align: left;">${reservation.guestName || ''}</td>
        <td style="text-align: left;">${reservation.otaChannelName || 'Direct'}</td>
        <td style="text-align: center;">${reservation.totalNights || 0}</td>
        <td style="text-align: right;">£${amount.toFixed(2)}</td>
        <td style="text-align: right;">£${fees > 0 ? fees.toFixed(2) : '0'}</td>
        <td style="text-align: right;">£${cardFee > 0 ? cardFee.toFixed(2) : '0'}</td>
        <td style="text-align: right;">£${netIncome.toFixed(2)}</td>
      </tr>
    `;
  }).join('');

  const totalNights = reservations.reduce((sum, r) => sum + (r.totalNights || 0), 0);
  const totalAmount = parseFloat(calculations.totalRevenue.toFixed(2));
  const totalFees = calculations.totalPlatformFees;
  const totalCardFees = calculations.totalCardFees;
  const totalNetIncome = calculations.netIncome;
  const totalOutgoings = calculations.totalOutgoings;
  const totalGrossProfit = calculations.grossProfit;
  const avgPerNight = reservations.length > 0 ? reservations.reduce((sum, r) => sum + (r.totalReservationPrice / r.totalNights), 0) / reservations.length : 0;

  let managementFeeLabel = '';
  if (settings.managementFeeBase === 'revenue') {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Total Revenue)`;
  } else if (settings.managementFeeBase === 'gross') {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Gross Profit)`;
  } else if (settings.managementFeeBase === 'grossFixed') {
    managementFeeLabel = `MANAGEMENT FEES (Fixed Amount)`;
  } else {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Net Income)`;
  }

  // HTML STRICTEMENT IDENTIQUE AU PDF
  this.previewHTML = `
    <div style="
      font-family: 'Nunito Sans', Arial, sans-serif;
      background: #F5F3EE;
      color: #1a1a1a;
      font-size: 9pt;
      line-height: 1.5;
      width: 210mm;
      min-height: 297mm;
      padding: 12mm 15mm;
      position: relative;
      box-sizing: border-box;
    ">
      <!-- HEADER -->
      <div style="font-size: 32pt; font-weight: 400; letter-spacing: -0.5px; line-height: 1.1; margin-bottom: 6px; color: #1a1a1a;">Monthly Report</div>
      <div style="font-size: 13pt; font-weight: 700; margin-bottom: 3px; color: #1a1a1a;">${this.modalData.propertyName}</div>
      <div style="font-size: 9pt; font-weight: 400; color: #1a1a1a; margin-bottom: 18px;">${this.modalData.propertyAddress || ''}</div>

      <hr style="border: none; border-top: 0.5pt solid #1a1a1a; margin: 14px 0;">

      <div style="font-size: 11pt; font-weight: 400; margin-bottom: 16px; color: #1a1a1a;">${monthYear}</div>

        <div style="
          font-size: 8.5pt;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin: 8px 0;
          padding-top: 18px;
          border-top: 0.5pt solid #1a1a1a;
          color: #1a1a1a;
        ">Booking Summary</div>
        
        <table style="
          width: 100%;
          border-collapse: collapse;
          margin: 6px 0 16px 0;
          font-size: 6.5pt;
          background: white;
          table-layout: fixed;
        ">
          <thead>
            <tr>
              <th style="font-weight: 700; text-align: left; padding: 3px 2px; border: 0.3pt solid #d0d0d0; background: white; font-size: 6.5pt; white-space: nowrap; width: 16%;">Booking Name</th>
              <th style="font-weight: 700; text-align: left; padding: 3px 2px; border: 0.3pt solid #d0d0d0; background: white; font-size: 6.5pt; white-space: nowrap; width: 11%;">Source</th>
              <th style="font-weight: 700; text-align: right; padding: 3px 4px 3px 2px; border: 0.3pt solid #d0d0d0; background: white; font-size: 6.5pt; white-space: nowrap; width: 9%;">No. Nights</th>
              <th style="font-weight: 700; text-align: right; padding: 3px 4px 3px 2px; border: 0.3pt solid #d0d0d0; background: white; font-size: 6.5pt; white-space: nowrap; width: 10%;">Amount</th>
              <th style="font-weight: 700; text-align: right; padding: 3px 4px 3px 2px; border: 0.3pt solid #d0d0d0; background: white; font-size: 6.5pt; white-space: nowrap; width: 10%;">Fees</th>
              <th style="font-weight: 700; text-align: right; padding: 3px 4px 3px 2px; border: 0.3pt solid #d0d0d0; background: white; font-size: 6.5pt; white-space: nowrap; width: 10%;">Card Fee</th>
              <th style="font-weight: 700; text-align: right; padding: 3px 4px 3px 2px; border: 0.3pt solid #d0d0d0; background: white; font-size: 6.5pt; white-space: nowrap; width: 14%;">Net Income</th>
            </tr>
          </thead>
          <tbody>
            ${bookingsRows.split('\n').map(line => line.trim()).filter(line => line).map(line => {
              let styledLine = line.replace(/<td/g, '<td style="text-align: right; padding: 2px 4px 2px 2px; border: 0.3pt solid #d0d0d0; background: white; font-size: 6.5pt; vertical-align: middle;"');
              styledLine = styledLine.replace(/style="text-align: right;/,  'style="text-align: left; padding: 2px 2px; border: 0.3pt solid #d0d0d0; background: white; font-size: 6.5pt; vertical-align: middle;"');
              styledLine = styledLine.replace(/style="text-align: left; padding: 2px 2px; border: 0.3pt solid #d0d0d0; background: white; font-size: 6.5pt; vertical-align: middle;"([^>]*>)([^<]*<\/td>.*?)<td style="text-align: right;/, 'style="text-align: left; padding: 2px 2px; border: 0.3pt solid #d0d0d0; background: white; font-size: 6.5pt; vertical-align: middle;"$1$2<td style="text-align: left; padding: 2px 2px; border: 0.3pt solid #d0d0d0; background: white; font-size: 6.5pt; vertical-align: middle;"');
              return styledLine;
            }).join('')}
            <tr style="border-top: 0.5pt solid #1a1a1a; font-weight: 700;">
              <td style="padding: 2px 2px; padding-top: 5px; text-align: left; border: 0.3pt solid #d0d0d0; font-size: 6.5pt; font-weight: 700;">Totals</td>
              <td style="padding: 2px 2px; padding-top: 5px; border: 0.3pt solid #d0d0d0; font-size: 6.5pt; font-weight: 700;"></td>
              <td style="padding: 2px 4px 2px 2px; padding-top: 5px; text-align: right; border: 0.3pt solid #d0d0d0; font-size: 6.5pt; font-weight: 700;">${totalNights}</td>
              <td style="padding: 2px 4px 2px 2px; padding-top: 5px; text-align: right; border: 0.3pt solid #d0d0d0; font-size: 6.5pt; font-weight: 700;">${totalAmount.toFixed(2)}</td>
              <td style="padding: 2px 4px 2px 2px; padding-top: 5px; text-align: right; border: 0.3pt solid #d0d0d0; font-size: 6.5pt; font-weight: 700;">${totalFees.toFixed(2)}</td>
              <td style="padding: 2px 4px 2px 2px; padding-top: 5px; text-align: right; border: 0.3pt solid #d0d0d0; font-size: 6.5pt; font-weight: 700;">${totalCardFees.toFixed(2)}</td>
              <td style="padding: 2px 4px 2px 2px; padding-top: 5px; text-align: right; border: 0.3pt solid #d0d0d0; font-size: 6.5pt; font-weight: 700;">${totalNetIncome.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
        
      <!-- INCOME & DIRECT EXPENSES -->
      <div style="font-size: 8.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin: 8px 0; padding-top: 18px; border-top: 0.5pt solid #1a1a1a; color: #1a1a1a;">Income & Direct Expenses</div>
      <div style="width: 100%; margin: 6px 0 14px 0;">
        <div style="display: table; width: 100%; padding: 3px 0;">
          <span style="display: table-cell; text-align: left; font-size: 9pt; width: 75%; font-weight: 400; text-transform: uppercase; letter-spacing: 0.3px;">Total Guest Payments</span>
          <span style="display: table-cell; text-align: center; font-size: 9pt; font-weight: 400; width: 25%;">£${calculations.totalRevenue.toFixed(2)}</span>
        </div>
        <div style="display: table; width: 100%; padding: 3px 0;">
          <span style="display: table-cell; text-align: left; font-size: 9pt; width: 75%; font-weight: 400; text-transform: uppercase; letter-spacing: 0.3px;">Platform Fees</span>
          <span style="display: table-cell; text-align: center; font-size: 9pt; font-weight: 400; width: 25%;">£${calculations.totalPlatformFees.toFixed(2)}</span>
        </div>
        <div style="display: table; width: 100%; padding: 3px 0;">
          <span style="display: table-cell; text-align: left; font-size: 9pt; width: 75%; font-weight: 400; text-transform: uppercase; letter-spacing: 0.3px;">Card Processing Fees</span>
          <span style="display: table-cell; text-align: center; font-size: 9pt; font-weight: 400; width: 25%;">£${calculations.totalCardFees.toFixed(2)}</span>
        </div>
        <div style="display: table; width: 100%; padding: 3px 0;">
          <span style="display: table-cell; text-align: left; font-size: 9pt; width: 75%; font-weight: 400; text-transform: uppercase; letter-spacing: 0.3px;">Cleaning</span>
          <span style="display: table-cell; text-align: center; font-size: 9pt; font-weight: 400; width: 25%;">£${calculations.totalOutgoings.toFixed(2)}</span>
        </div>
        <div style="display: table; width: 100%; padding: 3px 0; margin-top: 4px; padding-top: 6px;">
          <span style="display: table-cell; text-align: left; font-size: 9pt; width: 75%; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px;">Gross Profit</span>
          <span style="display: table-cell; text-align: center; font-size: 9pt; font-weight: 700; width: 25%;">£${calculations.grossProfit.toFixed(2)}</span>
        </div>
      </div>

      <!-- MANAGEMENT FEES & SUPPLIED SERVICES -->
      <div style="font-size: 8.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin: 8px 0; padding-top: 18px; border-top: 0.5pt solid #1a1a1a; color: #1a1a1a;">Management Fees & Supplied Services</div>
      <div style="width: 100%; margin: 6px 0 14px 0;">
        <div style="display: table; width: 100%; padding: 3px 0;">
          <span style="display: table-cell; text-align: left; font-size: 9pt; width: 75%; font-weight: 400; text-transform: uppercase; letter-spacing: 0.3px;">${managementFeeLabel}</span>
          <span style="display: table-cell; text-align: center; font-size: 9pt; font-weight: 400; width: 25%;">£${calculations.managementFees.toFixed(2)}</span>
        </div>
        <div style="display: table; width: 100%; padding: 3px 0;">
          <span style="display: table-cell; text-align: left; font-size: 9pt; width: 75%; font-weight: 400; text-transform: uppercase; letter-spacing: 0.3px;">Consumables & Guest Supplies</span>
          <span style="display: table-cell; text-align: center; font-size: 9pt; font-weight: 400; width: 25%;">£${calculations.consumables.toFixed(2)}</span>
        </div>
        <div style="display: table; width: 100%; padding: 3px 0;">
          <span style="display: table-cell; text-align: left; font-size: 9pt; width: 75%; font-weight: 400; text-transform: uppercase; letter-spacing: 0.3px;">Laundry & Linen Supply</span>
          <span style="display: table-cell; text-align: center; font-size: 9pt; font-weight: 400; width: 25%;">£${calculations.laundry.toFixed(2)}</span>
        </div>
        <div style="display: table; width: 100%; padding: 3px 0; margin-top: 4px; padding-top: 6px;">
          <span style="display: table-cell; text-align: left; font-size: 9pt; width: 75%; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px;">Sub Total</span>
          <span style="display: table-cell; text-align: center; font-size: 9pt; font-weight: 700; width: 25%;">£${(calculations.managementFees + calculations.consumables + calculations.laundry).toFixed(2)}</span>
        </div>
      </div>

      <!-- VAT SUMMARY -->
      <div style="font-size: 8.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin: 8px 0; padding-top: 18px; border-top: 0.5pt solid #1a1a1a; color: #1a1a1a;">VAT Summary</div>
      <div style="width: 100%; margin: 6px 0 14px 0;">
        <div style="display: table; width: 100%; padding: 3px 0; margin-top: 4px; padding-top: 6px;">
          <span style="display: table-cell; text-align: left; font-size: 9pt; width: 75%; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px;">VAT (20%)</span>
          <span style="display: table-cell; text-align: center; font-size: 9pt; font-weight: 700; width: 25%;">£${calculations.vat.toFixed(2)}</span>
        </div>
      </div>
      <div style="font-size: 7.5pt; font-style: italic; color: #555555; margin-top: 4px;">VAT applies only to services supplied by Sojourn Executive Ltd.</div>

      <!-- MAINTENANCE & OTHER COSTS -->
      <div style="font-size: 8.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin: 8px 0; padding-top: 18px; border-top: 0.5pt solid #1a1a1a; color: #1a1a1a;">Maintenance & Other Costs</div>
      <div style="width: 100%; margin: 6px 0 14px 0;">
        <div style="display: table; width: 100%; padding: 3px 0;">
          <span style="display: table-cell; text-align: left; font-size: 9pt; width: 75%; font-weight: 400; text-transform: uppercase; letter-spacing: 0.3px;">Maintenance</span>
          <span style="display: table-cell; text-align: center; font-size: 9pt; font-weight: 400; width: 25%;">£${calculations.maintenance.toFixed(2)}</span>
        </div>
        ${this.propertyExpenses.length > 0 ? `
        <div style="display: table; width: 100%; padding: 3px 0;">
          <span style="display: table-cell; text-align: left; font-size: 9pt; width: 75%; font-weight: 400; text-transform: uppercase; letter-spacing: 0.3px;">Other Expenses</span>
          <span style="display: table-cell; text-align: center; font-size: 9pt; font-weight: 400; width: 25%;">£${calculations.additionalExpenses.toFixed(2)}</span>
        </div>
        ` : ''}
      </div>

      <!-- SETTLEMENT SUMMARY -->
      <div style="font-size: 8.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin: 8px 0; padding-top: 18px; border-top: 0.5pt solid #1a1a1a; color: #1a1a1a;">Settlement Summary</div>
      <div style="width: 100%; margin: 6px 0 14px 0;">
        <div style="display: table; width: 100%; padding: 3px 0;">
          <span style="display: table-cell; text-align: left; font-size: 9pt; width: 75%; font-weight: 400; text-transform: uppercase; letter-spacing: 0.3px;">Gross Profit</span>
          <span style="display: table-cell; text-align: center; font-size: 9pt; font-weight: 400; width: 25%;">£${calculations.grossProfit.toFixed(2)}</span>
        </div>
        <div style="display: table; width: 100%; padding: 3px 0;">
          <span style="display: table-cell; text-align: left; font-size: 9pt; width: 75%; font-weight: 400; text-transform: uppercase; letter-spacing: 0.3px;">Less Management Fees & Supplied Services</span>
          <span style="display: table-cell; text-align: center; font-size: 9pt; font-weight: 400; width: 25%;">£${(calculations.managementFees + calculations.consumables + calculations.laundry).toFixed(2)}</span>
        </div>
        <div style="display: table; width: 100%; padding: 3px 0;">
          <span style="display: table-cell; text-align: left; font-size: 9pt; width: 75%; font-weight: 400; text-transform: uppercase; letter-spacing: 0.3px;">Less VAT on Fees</span>
          <span style="display: table-cell; text-align: center; font-size: 9pt; font-weight: 400; width: 25%;">£${calculations.vat.toFixed(2)}</span>
        </div>
        <div style="display: table; width: 100%; padding: 3px 0;">
          <span style="display: table-cell; text-align: left; font-size: 9pt; width: 75%; font-weight: 400; text-transform: uppercase; letter-spacing: 0.3px;">Less Maintenance & Other Costs</span>
          <span style="display: table-cell; text-align: center; font-size: 9pt; font-weight: 400; width: 25%;">£${(calculations.maintenance + calculations.additionalExpenses).toFixed(2)}</span>
        </div>
        <div style="position: relative; display: table; width: 100%; padding: 3px 0; margin-top: 4px; padding-top: 6px; padding-bottom: 6px;">
          <div style="content: ''; position: absolute; left: 75%; right: 0; top: 0; border-top: 0.5pt solid #1a1a1a;"></div>
          <div style="content: ''; position: absolute; left: 75%; right: 0; bottom: 0; border-top: 0.5pt solid #1a1a1a;"></div>
          <span style="display: table-cell; text-align: left; font-size: 10pt; width: 75%; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px;">Settlement to Owner</span>
          <span style="display: table-cell; text-align: center; font-size: 10pt; font-weight: 700; width: 25%;">£${calculations.settlementAmount.toFixed(2)}</span>
        </div>
      </div>


      <!-- FOOTER -->
      <div style="margin-top: 30px; border-top: 0.5pt solid #1a1a1a; padding-top: 10px;">
        <div style="display: table; width: 100%; margin-bottom: 16px; padding-top: 10px;">
          <div style="display: table-cell; vertical-align: bottom; width: 50%; font-size: 7.5pt; color: #555;">
            Prepared by<br>
            <strong>Sojourn Executive</strong>
          </div>
          <div style="display: table-cell; vertical-align: bottom; text-align: right; width: 50%; font-size: 7.5pt; color: #555;">
            Sojourn Executive Ltd<br>
            Tower House, Parkstone Rd, BH15 2JH<br>
            VAT No. 481 4033 10
          </div>
        </div>
        <div style="display: table; width: 100%; margin-top: 15px; padding-top: 10px;">
          <div style="display: table-cell; text-align: left; vertical-align: middle; font-size: 9pt; color: #1a1a1a; width: 20%;">${new Date().getFullYear()}</div>
          <div style="display: table-cell; text-align: center; vertical-align: middle; width: 60%;">
            <img src="/media/logos/sojourn-logo-hires.png" style="max-width: 200px; height: auto; margin: 0 auto; display: inline-block;" alt="Sojourn Executive Logo" />
          </div>
          <div style="display: table-cell; text-align: right; vertical-align: middle; font-size: 9pt; color: #1a1a1a; width: 20%;">1</div>
        </div>
      </div>
    </div>
  `;
},

   resetToSaved() {
     if (!confirm('Reset all settings to saved values?')) {
       return;
     }

     this.modalSettings = { ...this.originalSettings };
     this.hasUnsavedChanges = false;
     this.calculateMetrics();
   },

async saveSettings() {
  this.savingSettings = true;

  try {
    const propertyId = this.modalData.realPropertyId;

    if (!propertyId) {
      throw new Error('ID de propriété manquant');
    }

    const token = localStorage.getItem('token');
    if (!token) {
      this.$router.push('/login');
      return;
    }

    const headers = { 'Authorization': `Bearer ${token}` };

    // Map frontend field names to backend (camelCase to snake_case)
    // Ensure all numeric values are properly converted to numbers
    const payload = {
      property_id: propertyId,
      management_fee_rate: parseFloat(this.modalSettings.managementFeeRate) || 0,
      management_fee_base: this.modalSettings.managementFeeBase,
      management_fee_fixed_amount: parseFloat(this.modalSettings.managementFeeFixedAmount) || 0,
      vat_rate: parseFloat(this.modalSettings.vatRate) || 0,
      maintenance: parseFloat(this.modalSettings.maintenance) || 0,
      consumables: parseFloat(this.modalSettings.consumables) || 0,
      laundry_fee: parseFloat(this.modalSettings.laundry) || 0,
      laundry_calculation_mode: this.modalSettings.laundryCalculationMode,
      laundry_per_booking: parseFloat(this.modalSettings.laundryPerBooking) || 0,
      laundry_vat_applicable: this.modalSettings.laundryVatApplicable,
      other_expenses: parseFloat(this.modalSettings.otherExpenses) || 0,
      cleaning_mode: this.modalSettings.cleaningMode,
      booking_cleaning_fee: parseFloat(this.modalSettings.bookingCleaningFee) || 0,
      other_cleaning_fee: parseFloat(this.modalSettings.otherCleaningFee) || 0,
      cleaning_fee: parseFloat(this.modalSettings.cleaningFee) || 0,
      cleaning_per_booking: parseFloat(this.modalSettings.cleaningPerBooking) || 0,
      card_fee_mode: this.modalSettings.cardFeeMode,
      custom_card_fee_rate: parseFloat(this.modalSettings.customCardFeeRate) || 0,
      custom_card_fee_fixed: parseFloat(this.modalSettings.customCardFeeFixed) || 0,
      platform_fee_mode: this.modalSettings.platformFeeMode,
      apply_custom_booking_com: this.modalSettings.applyCustomBookingCom,
      booking_com_commission_rate: parseFloat(this.modalSettings.bookingComCommissionRate) || 0,
      apply_custom_airbnb: this.modalSettings.applyCustomAirbnb,
      airbnb_commission_rate: parseFloat(this.modalSettings.airbnbCommissionRate) || 0,
      apply_custom_other: this.modalSettings.applyCustomOther,
      other_platform_commission_rate: parseFloat(this.modalSettings.otherPlatformCommissionRate) || 0
    };

    const response = await axios.post(
      `${this.baseUrl}/api/pms/settings/`,
      payload,
      { headers }
    );

    if (response.data.success) {
      // Reload settings from backend to ensure cache is synchronized with database
      await this.reloadPropertySettingsFromDB(propertyId);

      // Save monthly laundry booking count override if in per_booking mode
      if (this.modalSettings.laundryCalculationMode === 'per_booking') {
        const periodLabel = this.modalData.periodLabel || '';
        const monthMap = {
          'January': 1, 'February': 2, 'March': 3, 'April': 4,
          'May': 5, 'June': 6, 'July': 7, 'August': 8,
          'September': 9, 'October': 10, 'November': 11, 'December': 12
        };

        let month, year;

        if (periodLabel === 'Current Month') {
          const now = new Date();
          month = now.getMonth() + 1;
          year = now.getFullYear();
        } else if (periodLabel === 'Last Month') {
          const now = new Date();
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          month = lastMonth.getMonth() + 1;
          year = lastMonth.getFullYear();
        } else {
          const [monthName, yearStr] = periodLabel.split(' ');
          month = monthMap[monthName];
          year = parseInt(yearStr);
        }

        if (month && year) {
          await this.saveMonthlyLaundryOverride(propertyId, year, month);
        }
      }

      // Save monthly cleaning booking count override if in per_booking mode
      if (this.modalSettings.cleaningMode === 'per_booking') {
        const periodLabel = this.modalData.periodLabel || '';
        const monthMap = {
          'January': 1, 'February': 2, 'March': 3, 'April': 4,
          'May': 5, 'June': 6, 'July': 7, 'August': 8,
          'September': 9, 'October': 10, 'November': 11, 'December': 12
        };

        let month, year;

        if (periodLabel === 'Current Month') {
          const now = new Date();
          month = now.getMonth() + 1;
          year = now.getFullYear();
        } else if (periodLabel === 'Last Month') {
          const now = new Date();
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          month = lastMonth.getMonth() + 1;
          year = lastMonth.getFullYear();
        } else {
          const [monthName, yearStr] = periodLabel.split(' ');
          month = monthMap[monthName];
          year = parseInt(yearStr);
        }

        if (month && year) {
          await this.saveMonthlyCleaningOverride(propertyId, year, month);
        }
      }

      this.hasUnsavedChanges = false;

      alert(`Settings saved for ${this.modalData.propertyName}!`);
    } else {
      throw new Error(response.data.message || 'Save failed');
    }

  } catch (error) {
    console.error('Error saving settings:', error);
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      this.$router.push('/login');
    } else {
      alert('Save failed. Please try again.');
    }
  } finally {
    this.savingSettings = false;
  }
},

// Vérifier s'il existe un montant fixe spécifique pour ce mois
async checkMonthSpecificFee(propertyId, periodLabel) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const headers = { 'Authorization': `Bearer ${token}` };

    // Parse year and month from periodLabel
    const [monthName, yearStr] = periodLabel.split(' ');
    const monthMap = {
      'January': 1, 'February': 2, 'March': 3, 'April': 4,
      'May': 5, 'June': 6, 'July': 7, 'August': 8,
      'September': 9, 'October': 10, 'November': 11, 'December': 12,
      'Current': new Date().getMonth() + 1,
      'Last': new Date().getMonth() === 0 ? 12 : new Date().getMonth()
    };

    let month, year;

    if (periodLabel === 'Current Month') {
      const now = new Date();
      month = now.getMonth() + 1;
      year = now.getFullYear();
    } else if (periodLabel === 'Last Month') {
      const now = new Date();
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      month = lastMonth.getMonth() + 1;
      year = lastMonth.getFullYear();
    } else if (monthName && yearStr) {
      month = monthMap[monthName];
      year = parseInt(yearStr);
    }

    if (!month || !year || !propertyId) {
      console.warn('Could not parse month/year from period label:', periodLabel);
      return;
    }

    // Appeler l'API pour vérifier s'il existe un montant spécifique
    const response = await axios.get(
      `${this.baseUrl}/api/pms/report-fixed-fee/`,
      {
        params: { property_id: propertyId, year: year, month: month },
        headers
      }
    );

    if (response.data.success && response.data.data && response.data.data.fixed_amount !== null) {
      // Un montant spécifique existe pour ce mois
      this.modalSettings.managementFeeFixedAmount = parseFloat(response.data.data.fixed_amount);
      this.originalSettings.managementFeeFixedAmount = parseFloat(response.data.data.fixed_amount);
      this.hasMonthSpecificFee = true;
      console.log(`Month-specific fee loaded: £${response.data.data.fixed_amount} for ${monthName} ${year}`);
    } else {
      // Pas de montant spécifique, utiliser le défaut
      this.hasMonthSpecificFee = false;
    }

  } catch (error) {
    console.error('Error checking month-specific fee:', error);
    // Ne pas bloquer l'ouverture du modal en cas d'erreur
    this.hasMonthSpecificFee = false;
  }
},

async saveForThisMonthOnly() {
  this.savingSettings = true;

  try {
    const propertyId = this.modalData.realPropertyId;
    const periodLabel = this.modalData.periodLabel; // Ex: "November 2025"

    if (!propertyId || !periodLabel) {
      throw new Error('Missing property ID or period');
    }

    // Parse year and month from periodLabel (format: "Month YYYY")
    const [monthName, yearStr] = periodLabel.split(' ');
    const monthMap = {
      'January': 1, 'February': 2, 'March': 3, 'April': 4,
      'May': 5, 'June': 6, 'July': 7, 'August': 8,
      'September': 9, 'October': 10, 'November': 11, 'December': 12
    };
    const month = monthMap[monthName];
    const year = parseInt(yearStr);

    if (!month || !year) {
      throw new Error('Could not parse month/year from period label');
    }

    const token = localStorage.getItem('token');
    if (!token) {
      this.$router.push('/login');
      return;
    }

    const headers = { 'Authorization': `Bearer ${token}` };

    // Payload pour sauvegarder le fixed fee pour ce mois uniquement
    const payload = {
      property_id: propertyId,
      year: year,
      month: month,
      fixed_amount: parseFloat(this.modalSettings.managementFeeFixedAmount) || 0
    };

    const response = await axios.post(
      `${this.baseUrl}/api/pms/report-fixed-fee/`,
      payload,
      { headers }
    );

    if (response.data.success) {
      this.hasMonthSpecificFee = true;
      this.originalSettings.managementFeeFixedAmount = this.modalSettings.managementFeeFixedAmount;
      this.hasUnsavedChanges = false;

      alert(`Fixed Management Fee of £${this.modalSettings.managementFeeFixedAmount} saved for ${monthName} ${year} only!\n\nOther months will continue to use the default setting.`);
    } else {
      throw new Error(response.data.message || 'Save failed');
    }

  } catch (error) {
    console.error('Error saving month-specific fixed fee:', error);
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      this.$router.push('/login');
    } else {
      alert('Failed to save for this month only. Please try again.');
    }
  } finally {
    this.savingSettings = false;
  }
},

// ========== Property Expenses Methods ==========

async loadPropertyExpenses(propertyId, year, month) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const headers = { 'Authorization': `Bearer ${token}` };

    const response = await axios.get(
      `${this.baseUrl}/api/pms/property-expenses/`,
      {
        params: { property_id: propertyId, year: year, month: month },
        headers
      }
    );

    if (response.data.success) {
      this.propertyExpenses = response.data.data || [];
      console.log(`Loaded ${this.propertyExpenses.length} expenses for ${year}-${month}`);
    }
  } catch (error) {
    console.error('Error loading property expenses:', error);
    this.propertyExpenses = [];
  }
},

async loadMonthlyLaundryOverride(propertyId, year, month) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const headers = { 'Authorization': `Bearer ${token}` };

    const response = await axios.get(
      `${this.baseUrl}/api/pms/monthly-laundry-override/`,
      {
        params: { property_id: propertyId, year: year, month: month },
        headers
      }
    );

    if (response.data.success && response.data.data) {
      // Override exists for this month
      const override = response.data.data;
      this.laundryBookingCount = override.booking_count;
      this.autoDetectedBookingCount = override.auto_detected_count;
      console.log(`Loaded laundry override: ${override.booking_count} bookings (auto-detected: ${override.auto_detected_count})`);
    } else {
      // No override, use auto-detected value (already set by autoDetectBookingCount)
      console.log(`No laundry override found for ${year}-${month}, using auto-detected count`);
    }
  } catch (error) {
    console.error('Error loading monthly laundry override:', error);
    // Keep auto-detected value on error
  }
},

async saveMonthlyLaundryOverride(propertyId, year, month) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const headers = { 'Authorization': `Bearer ${token}` };

    const payload = {
      property_id: propertyId,
      year: year,
      month: month,
      booking_count: this.laundryBookingCount,
      auto_detected_count: this.autoDetectedBookingCount
    };

    const response = await axios.post(
      `${this.baseUrl}/api/pms/monthly-laundry-override/`,
      payload,
      { headers }
    );

    if (response.data.success) {
      console.log(`Saved laundry override: ${this.laundryBookingCount} bookings for ${year}-${month}`);
    }
  } catch (error) {
    console.error('Error saving monthly laundry override:', error);
  }
},

async loadMonthlyCleaningOverride(propertyId, year, month) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const headers = { 'Authorization': `Bearer ${token}` };

    const response = await axios.get(
      `${this.baseUrl}/api/pms/monthly-cleaning-override/`,
      {
        params: { property_id: propertyId, year: year, month: month },
        headers
      }
    );

    if (response.data.success && response.data.data) {
      // Override exists for this month
      const override = response.data.data;
      this.cleaningBookingCount = override.booking_count;
      console.log(`Loaded cleaning override: ${override.booking_count} bookings (auto-detected: ${override.auto_detected_count})`);
    } else {
      // No override, use auto-detected value (already set by autoDetectBookingCount)
      console.log(`No cleaning override found for ${year}-${month}, using auto-detected count`);
    }
  } catch (error) {
    console.error('Error loading monthly cleaning override:', error);
    // Keep auto-detected value on error
  }
},

async saveMonthlyCleaningOverride(propertyId, year, month) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const headers = { 'Authorization': `Bearer ${token}` };

    const payload = {
      property_id: propertyId,
      year: year,
      month: month,
      booking_count: this.cleaningBookingCount,
      auto_detected_count: this.autoDetectedBookingCount
    };

    const response = await axios.post(
      `${this.baseUrl}/api/pms/monthly-cleaning-override/`,
      payload,
      { headers }
    );

    if (response.data.success) {
      console.log(`Saved cleaning override: ${this.cleaningBookingCount} bookings for ${year}-${month}`);
    }
  } catch (error) {
    console.error('Error saving monthly cleaning override:', error);
  }
},

openExpenseModal() {
  this.showExpenseModal = true;
  this.newExpense = {
    description: '',
    amount: 0,
    expense_category: 'maintenance',
    is_vat_applicable: false,
    vat_treatment: 'gross',
    net_amount: 0,
    vat_amount: 0,
    vat_rate: 20,
    receipt_file: null,
    receipt_filename: ''
  };
},

closeExpenseModal() {
  this.showExpenseModal = false;
  this.newExpense = {
    description: '',
    amount: 0,
    expense_category: 'maintenance',
    is_vat_applicable: false,
    vat_treatment: 'gross',
    net_amount: 0,
    vat_amount: 0,
    vat_rate: 20,
    receipt_file: null,
    receipt_filename: ''
  };
  // Reset file input
  if (this.$refs.receiptFileInput) {
    this.$refs.receiptFileInput.value = '';
  }
},

handleReceiptUpload(event) {
  const file = event.target.files[0];
  if (file) {
    this.newExpense.receipt_file = file;
    this.newExpense.receipt_filename = file.name;
  }
},

async saveExpense() {
  const hasAmount = this.newExpense.amount ||
    (this.newExpense.is_vat_applicable && this.newExpense.vat_treatment === 'net_plus_vat' && this.newExpense.net_amount);

  if (!this.newExpense.description || !hasAmount) {
    alert('Please fill in description and amount');
    return;
  }

  this.savingExpense = true;

  try {
    const propertyId = this.modalData.realPropertyId;
    const periodLabel = this.modalData.periodLabel;

    if (!propertyId || !periodLabel) {
      throw new Error('Missing property ID or period');
    }

    // Parse year and month from periodLabel
    const [monthName, yearStr] = periodLabel.split(' ');
    const monthMap = {
      'January': 1, 'February': 2, 'March': 3, 'April': 4,
      'May': 5, 'June': 6, 'July': 7, 'August': 8,
      'September': 9, 'October': 10, 'November': 11, 'December': 12,
      'Current': new Date().getMonth() + 1,
      'Last': new Date().getMonth() === 0 ? 12 : new Date().getMonth()
    };

    let month, year;

    if (periodLabel === 'Current Month') {
      const now = new Date();
      month = now.getMonth() + 1;
      year = now.getFullYear();
    } else if (periodLabel === 'Last Month') {
      const now = new Date();
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      month = lastMonth.getMonth() + 1;
      year = lastMonth.getFullYear();
    } else if (monthName && yearStr) {
      month = monthMap[monthName];
      year = parseInt(yearStr);
    }

    if (!month || !year) {
      throw new Error('Could not parse month/year from period label');
    }

    const token = localStorage.getItem('token');
    if (!token) {
      this.$router.push('/login');
      return;
    }

    const headers = { 'Authorization': `Bearer ${token}` };

    // Calculer le montant total selon le mode VAT
    let totalAmount = this.newExpense.amount;
    if (this.newExpense.is_vat_applicable && this.newExpense.vat_treatment === 'net_plus_vat') {
      totalAmount = parseFloat(this.newExpense.net_amount || 0) + parseFloat(this.newExpense.vat_amount || 0);
    }

    // Créer FormData pour l'upload de fichier
    const formData = new FormData();
    formData.append('property_id', propertyId);
    formData.append('year', year);
    formData.append('month', month);
    formData.append('description', this.newExpense.description);
    formData.append('amount', totalAmount);
    formData.append('is_vat_applicable', this.newExpense.is_vat_applicable ? 'true' : 'false');
    formData.append('vat_treatment', this.newExpense.vat_treatment || 'gross');
    formData.append('vat_rate', this.newExpense.vat_rate || 20);
    formData.append('expense_category', this.newExpense.expense_category || 'maintenance');

    if (this.newExpense.is_vat_applicable && this.newExpense.vat_treatment === 'net_plus_vat') {
      formData.append('net_amount', this.newExpense.net_amount || 0);
      formData.append('vat_amount', this.newExpense.vat_amount || 0);
    }

    if (this.newExpense.receipt_file) {
      formData.append('receipt_file', this.newExpense.receipt_file);
    }

    const response = await axios.post(
      `${this.baseUrl}/api/pms/property-expenses/create/`,
      formData,
      {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    if (response.data.success) {
      // Recharger la liste des dépenses
      await this.loadPropertyExpenses(propertyId, year, month);

      // Recalculer les métriques
      this.calculateMetrics();

      // Fermer le modal
      this.closeExpenseModal();
    } else {
      throw new Error(response.data.message || 'Failed to save expense');
    }

  } catch (error) {
    console.error('Error saving expense:', error);
    alert('Failed to save expense. Please try again.');
  } finally {
    this.savingExpense = false;
  }
},

async deleteExpense(expenseId) {
  if (!confirm('Are you sure you want to delete this expense?')) {
    return;
  }

  try {
    const token = localStorage.getItem('token');
    if (!token) {
      this.$router.push('/login');
      return;
    }

    const headers = { 'Authorization': `Bearer ${token}` };

    const response = await axios.delete(
      `${this.baseUrl}/api/pms/property-expenses/${expenseId}/`,
      { headers }
    );

    if (response.data.success) {
      // Recharger la liste
      const propertyId = this.modalData.realPropertyId;
      const periodLabel = this.modalData.periodLabel;

      // Parse year and month
      const [monthName, yearStr] = periodLabel.split(' ');
      const monthMap = {
        'January': 1, 'February': 2, 'March': 3, 'April': 4,
        'May': 5, 'June': 6, 'July': 7, 'August': 8,
        'September': 9, 'October': 10, 'November': 11, 'December': 12
      };

      let month, year;
      if (periodLabel === 'Current Month') {
        const now = new Date();
        month = now.getMonth() + 1;
        year = now.getFullYear();
      } else if (periodLabel === 'Last Month') {
        const now = new Date();
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        month = lastMonth.getMonth() + 1;
        year = lastMonth.getFullYear();
      } else if (monthName && yearStr) {
        month = monthMap[monthName];
        year = parseInt(yearStr);
      }

      if (month && year) {
        await this.loadPropertyExpenses(propertyId, year, month);
        this.calculateMetrics();
      }
    }
  } catch (error) {
    console.error('Error deleting expense:', error);
    alert('Failed to delete expense. Please try again.');
  }
},

   async downloadPDF() {
     this.generatingPDF = true;

     try {
       console.log('Preparing data for backend PDF generation with VAT system...');

       // Use the new prepareBookingsForBackend() function
       const reportData = this.prepareBookingsForBackend();

       // Add send_email flag (false for download)
       reportData.send_email = false;

       // Save monthly laundry override if in per_booking mode
       if (this.modalSettings.laundryCalculationMode === 'per_booking' && this.modalData.realPropertyId) {
         const periodLabel = this.modalData.periodLabel;
         const monthMap = {
           'January': 1, 'February': 2, 'March': 3, 'April': 4,
           'May': 5, 'June': 6, 'July': 7, 'August': 8,
           'September': 9, 'October': 10, 'November': 11, 'December': 12
         };

         let month, year;
         if (periodLabel === 'Current Month') {
           const now = new Date();
           month = now.getMonth() + 1;
           year = now.getFullYear();
         } else if (periodLabel === 'Last Month') {
           const now = new Date();
           const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
           month = lastMonth.getMonth() + 1;
           year = lastMonth.getFullYear();
         } else {
           const [monthName, yearStr] = periodLabel.split(' ');
           month = monthMap[monthName];
           year = parseInt(yearStr);
         }

         if (month && year) {
           await this.saveMonthlyLaundryOverride(this.modalData.realPropertyId, year, month);
         }
       }

       console.log('Sending request to /api/pms/generate-report-vat/');

       // Check if token exists
       const token = localStorage.getItem('token');
       if (!token) {
         alert('Votre session a expiré. Veuillez vous reconnecter.');
         this.$router.push('/login');
         return;
       }

       const response = await axios.post(`${this.baseUrl}/api/pms/generate-report-vat/`, reportData, {
         headers: {
           'Authorization': `Bearer ${token}`,
           'Content-Type': 'application/json'
         }
       });

       if (response.data.success && response.data.data.pdf_base64) {
         console.log('PDF received from backend, downloading...');

         // Decode base64 and create blob
         const pdfBase64 = response.data.data.pdf_base64;
         const byteCharacters = atob(pdfBase64);
         const byteNumbers = new Array(byteCharacters.length);
         for (let i = 0; i < byteCharacters.length; i++) {
           byteNumbers[i] = byteCharacters.charCodeAt(i);
         }
         const byteArray = new Uint8Array(byteNumbers);
         const blob = new Blob([byteArray], { type: 'application/pdf' });

         // Create download link
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = response.data.data.filename || `${this.modalData.propertyName}_${this.modalData.periodLabel}_Report.pdf`;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);

         console.log('PDF downloaded successfully');
       } else {
         throw new Error(response.data.message || 'Failed to generate PDF');
       }

     } catch (error) {
       console.error('Error generating PDF:', error);

       // Handle 401 Unauthorized error
       if (error.response?.status === 401) {
         alert('Votre session a expiré. Veuillez vous reconnecter.');
         this.$router.push('/login');
         return;
       }

       const errorMessage = error.response?.data?.message || error.message || 'Failed to generate PDF. Please try again.';
       alert(errorMessage);
     } finally {
       this.generatingPDF = false;
     }
   },

   generatePDFHTML() {
  // Utiliser EXACTEMENT le même HTML que la méthode generateHTMLContent existante
  const currentDate = new Date();
  // Use the actual periodLabel instead of calculating dates
  let monthYear;
  if (this.modalData.periodLabel === 'Current Month') {
    monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  } else if (this.modalData.periodLabel === 'Last Month') {
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
    monthYear = lastMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  } else {
    // For historical months, use the periodLabel directly (e.g., "December 2025")
    monthYear = this.modalData.periodLabel;
  }

  const calculations = this.calculatedMetrics;
  const settings = this.modalSettings;
  const reservations = this.modalData.reservations;

  // Générer les lignes du tableau EXACTEMENT comme dans l'original
  const bookingsRows = reservations.map(reservation => {
    const amount = parseFloat(reservation.totalReservationPrice || 0);
    const fees = this.calculateCustomCommission(reservation, this.modalSettings);

    let cardFee = 0;
    if (settings.cardFeeMode === 'rentlio') {
      cardFee = (reservation.otaChannelName === 'Booking.com') ? parseFloat(reservation.cardFee || 0) : 0;
    } else {
      cardFee = (reservation.otaChannelName === 'Booking.com') ?
        (amount * settings.customCardFeeRate / 100) : 0;
    }

    let outgoings = 0;
    const isExtension = (reservation.guestName || '').toLowerCase().includes('extension');

    if (isExtension) {
      outgoings = 0;
    } else {
      if (settings.cleaningMode === 'rentlio') {
        outgoings = parseFloat(reservation.totalServicesPrice || 0);
      } else {
        // Custom Rates: Appliquer les frais uniquement au dernier segment
        const isLastSegment = !reservation.isSegment ||
          (reservation.segmentInfo?.part === reservation.segmentInfo?.total);

        if (isLastSegment) {
          const platform = reservation.otaChannelName || '';
          if (platform === 'Booking.com') {
            outgoings = settings.bookingCleaningFee || 150;
          } else {
            outgoings = settings.otherCleaningFee || 120;
          }
        } else {
          // Segment intermédiaire, pas de frais de nettoyage
          outgoings = 0;
        }
      }
    }
    
    const netIncome = amount - fees - cardFee;
    const grossProfit = netIncome - outgoings;

    return `
      <tr>
        <td style="text-align: left;">${reservation.guestName || ''}</td>
        <td style="text-align: left;">${reservation.otaChannelName || 'Direct'}</td>
        <td style="text-align: center;">${reservation.totalNights || 0}</td>
        <td style="text-align: right;">£${amount.toFixed(2)}</td>
        <td style="text-align: right;">£${fees > 0 ? fees.toFixed(2) : '0'}</td>
        <td style="text-align: right;">£${cardFee > 0 ? cardFee.toFixed(2) : '0'}</td>
        <td style="text-align: right;">£${netIncome.toFixed(2)}</td>
      </tr>
    `;
  }).join('');

  const totalNights = reservations.reduce((sum, r) => sum + (r.totalNights || 0), 0);
  const totalAmount = parseFloat(calculations.totalRevenue.toFixed(2));
  const totalFees = calculations.totalPlatformFees;
  const totalCardFees = calculations.totalCardFees;
  const totalNetIncome = calculations.netIncome;
  const totalOutgoings = calculations.totalOutgoings;
  const totalGrossProfit = calculations.grossProfit;
  const avgPerNight = reservations.length > 0 ? reservations.reduce((sum, r) => sum + (r.totalReservationPrice / r.totalNights), 0) / reservations.length : 0;

  let managementFeeLabel = '';
  if (settings.managementFeeBase === 'revenue') {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Total Revenue)`;
  } else if (settings.managementFeeBase === 'gross') {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Gross Profit)`;
  } else if (settings.managementFeeBase === 'grossFixed') {
    managementFeeLabel = `MANAGEMENT FEES (Fixed Amount)`;
  } else {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Net Income)`;
  }

  // UTILISER EXACTEMENT LES MÊMES STYLES CSS QUE L'ORIGINAL
  return `
<!DOCTYPE html>
<html lang="en">
<head>
   <meta charset="UTF-8">
   <title>Monthly Report</title>
   <style>
       @page {
           size: A4;
           margin: 0;
       }
       
       * {
           margin: 0;
           padding: 0;
           box-sizing: border-box;
       }
       
       body {
           font-family: 'Playfair Display', 'Times New Roman', serif;
           background: #f8f9f9;
           color: #2c3e50;
           font-size: 12px;
           line-height: 1.4;
           width: 210mm;
           height: 297mm;
           padding: 15mm;
           position: relative;
       }
       
       .page-container {
           width: 100%;
           min-height: 267mm;
           display: flex;
           flex-direction: column;
           padding: 20px;
           box-shadow: 0 2px 4px rgba(0,0,0,0.1);
           position: relative;
       }
       
       .report-header {
           margin-bottom: 25px;
       }
       
       .report-title {
           font-size: 38px;
           font-weight: 400;
           color: #2c3e50;
           margin-bottom: 5px;
           letter-spacing: 1px;
       }
       
       .property-name {
           font-size: 22px;
           font-weight: 300;
           color: #34495e;
           margin-bottom: 15px;
           letter-spacing: 0.5px;
       }
       
       .divider {
           width: 100%;
           height: 1px;
           background: #bdc3c7;
           margin: 15px 0 12px 0;
       }
       
       .report-period {
           font-size: 13px;
           font-weight: 400;
           color: #7f8c8d;
           text-transform: uppercase;
           letter-spacing: 2px;
       }
       
       .section-title {
           font-size: 10px;
           font-weight: 600;
           color: #2c3e50;
           text-transform: uppercase;
           letter-spacing: 1px;
           margin: 20px 0 10px 0;
       }
       
       .data-table {
           width: 100%;
           border-collapse: collapse;
           font-size: 8px;
           margin-bottom: 15px;
           font-family: 'Arial', sans-serif;
       }
       
       .data-table th {
           background: #ecf0f1;
           padding: 5px 3px;
           text-align: center;
           font-weight: 600;
           border: 1px solid #bdc3c7;
           font-size: 7px;
           line-height: 1.2;
           color: #2c3e50;
       }
       
       .data-table td {
           padding: 3px 2px;
           border: 1px solid #d5dbdb;
           font-size: 7px;
           vertical-align: middle;
           color: #2c3e50;
       }
       
       .data-table .totals-row {
           background: #f8f9fa;
           font-weight: 700;
           border-top: 2px solid #34495e;
       }
       
       .data-table .totals-row td {
           border: 1px solid #34495e;
           font-weight: 700;
           color: #2c3e50;
       }
       
       .content-sections {
           margin: 15px 0;
       }
       
       .summary-section {
           margin-bottom: 20px;
           margin-top: 20px;
       }
       
       .summary-title {
           font-size: 10px;
           font-weight: 700;
           margin-bottom: 8px;
           text-transform: uppercase;
           color: #2c3e50;
           letter-spacing: 1px;
       }
       
       .summary-table {
           width: 100%;
           border-collapse: collapse;
           font-family: 'Arial', sans-serif;
       }
       
       .summary-table td {
           padding: 2px 0;
           font-size: 8px;
           color: #2c3e50;
           border: none;
       }
       
       .summary-table .label {
           text-align: left;
           width: 70%;
       }
       
       .summary-table .value {
           text-align: right;
           width: 30%;
           font-weight: 600;
       }
       
       .summary-table .total-row {
           border-top: 1px solid #34495e;
           padding-top: 4px;
           margin-top: 4px;
           font-weight: 700;
           font-size: 11px;
       }
       
       .summary-table .total-row td {
           padding-top: 4px;
           border-top: 1px solid #34495e;
       }
       
       .settlement-section {
           margin: 25px 0;
           padding: 12px;
           background: #f8f9fa;
           border: 1px solid #e9ecef;
           text-align: center;
       }
       
       .settlement-title {
           font-size: 11px;
           font-weight: 700;
           margin-bottom: 10px;
           text-transform: uppercase;
           letter-spacing: 1px;
           color: #2c3e50;
       }
       
       .settlement-table {
           width: 100%;
           border-collapse: collapse;
           font-family: 'Arial', sans-serif;
       }
       
       .settlement-table td {
           padding: 3px 0;
           font-size: 11px;
           color: #2c3e50;
           border: none;
       }
       
       .settlement-table .label {
           text-align: left;
           width: 70%;
       }
       
       .settlement-table .value {
           text-align: right;
           width: 30%;
           font-weight: 600;
       }
       
       .settlement-table .final-row {
           border-top: 2px solid #2c3e50;
           padding-top: 6px;
           margin-top: 6px;
           font-weight: 700;
           font-size: 13px;
       }
       
       .settlement-table .final-row td {
           padding-top: 6px;
           border-top: 2px solid #2c3e50;
       }
       
       .footer-section {
           margin-top: auto;
           padding-top: 15px;
           text-align: center;
           border-top: 1px solid #bdc3c7;
       }
       
       .prepared-info {
           margin-bottom: 12px;
       }
       
       .prepared-label {
           font-size: 8px;
           color: #7f8c8d;
           margin-bottom: 2px;
       }
       
       .prepared-company {
           font-size: 10px;
           font-weight: 600;
           color: #2c3e50;
       }
       
       .company-logo {
           margin-bottom: 15px;
       }
       
       .company-name {
           font-size: 16px;
           font-weight: 300;
           letter-spacing: 6px;
           color: #2c3e50;
           margin-bottom: 1px;
       }
       
       .company-tagline {
           font-size: 6px;
           color: #95a5a6;
           text-transform: uppercase;
           letter-spacing: 3px;
       }
       
       .footer-meta {
           display: flex;
           justify-content: space-between;
           align-items: center;
           font-size: 9px;
           color: #7f8c8d;
           margin-top: 10px;
       }
   </style>
</head>
<body>
   <div class="page-container">
       <div class="report-header">
           <h1 class="report-title">Monthly Report</h1>
           <h2 class="property-name">${this.modalData.propertyName}</h2>
           <div class="divider"></div>
           <div class="report-period">${monthYear.toUpperCase()}</div>
       </div>
       
       <div class="section-title">BOOKING OVERVIEW</div>
       
       <table class="data-table">
           <thead>
               <tr>
                   <th>Booking Name</th>
                   <th>Source</th>
                   <th>No. Nights</th>
                   <th>Amount</th>
                   <th>Fees</th>
                   <th>Card Fee</th>
                   <th>Net Income</th>
               </tr>
           </thead>
           <tbody>
               ${bookingsRows}
               <tr class="totals-row">
                   <td style="text-align: left;"><strong>Totals</strong></td>
                   <td></td>
                   <td style="text-align: center;"><strong>${totalNights}</strong></td>
                   <td style="text-align: right;"><strong>£${totalAmount.toFixed(2)}</strong></td>
                   <td style="text-align: right;"><strong>£${totalFees.toFixed(2)}</strong></td>
                   <td style="text-align: right;"><strong>£${totalCardFees.toFixed(2)}</strong></td>
                   <td style="text-align: right;"><strong>£${totalNetIncome.toFixed(2)}</strong></td>
               </tr>
           </tbody>
       </table>
       
       <div class="content-sections">
           <div class="summary-section">
               <div class="summary-title">INCOME & DIRECT EXPENSES</div>
               <table class="summary-table">
                   <tr>
                       <td class="label">TOTAL GUEST PAYMENTS</td>
                       <td class="value">£${calculations.totalRevenue.toFixed(2)}</td>
                   </tr>
                   <tr>
                       <td class="label">PLATFORM FEES</td>
                       <td class="value">£${calculations.totalPlatformFees.toFixed(2)}</td>
                   </tr>
                   <tr>
                       <td class="label">CARD PROCESSING FEES</td>
                       <td class="value">£${calculations.totalCardFees.toFixed(2)}</td>
                   </tr>
                   <tr>
                       <td class="label">CLEANING & LAUNDRY</td>
                       <td class="value">£${calculations.totalOutgoings.toFixed(2)}</td>
                   </tr>
                   <tr class="total-row">
                       <td class="label">GROSS PROFIT</td>
                       <td class="value">£${calculations.grossProfit.toFixed(2)}</td>
                   </tr>
               </table>
           </div>
           
       <div class="summary-section">
         <div class="summary-title">MANAGEMENT FEES & OTHER COSTS</div>
         <table class="summary-table">
             <tr>
                 <td class="label">${managementFeeLabel}</td>
                 <td class="value">£${calculations.managementFees.toFixed(2)}</td>
             </tr>
                   <tr>
                       <td class="label">VAT</td>
                       <td class="value">£${calculations.vat.toFixed(2)}</td>
                   </tr>
                   <tr>
                       <td class="label">CONSUMABLES</td>
                       <td class="value">£${calculations.consumables.toFixed(2)}</td>
                   </tr>
                   <tr>
                       <td class="label">MAINTENANCE</td>
                       <td class="value">£${calculations.maintenance.toFixed(2)}</td>
                   </tr>
                   ${this.propertyExpenses.length > 0 ? `
                   <tr>
                       <td class="label">OTHER EXPENSES</td>
                       <td class="value">£${calculations.additionalExpenses.toFixed(2)}</td>
                   </tr>
                   ${this.propertyExpenses.map(expense => `
                   <tr>
                       <td class="label" style="padding-left: 15px;">- ${expense.description}</td>
                       <td class="value">£${parseFloat(expense.amount).toFixed(2)}</td>
                   </tr>
                   `).join('')}` : ''}
               </table>
           </div>
       </div>
       
       <div class="settlement-section">
           <div class="settlement-title">SETTLEMENT SUMMARY</div>
           <table class="settlement-table">
               <tr>
                   <td class="label">GROSS PROFIT</td>
                   <td class="value">£${calculations.grossProfit.toFixed(2)}</td>
               </tr>
               <tr>
                   <td class="label">LESS MANAGEMENT FEE & OTHER COSTS</td>
                   <td class="value">£${(calculations.managementFees + calculations.vat + calculations.maintenance + calculations.consumables + calculations.additionalExpenses).toFixed(2)}</td>
               </tr>
               <tr class="final-row">
                   <td class="label">SETTLEMENT TO OWNER</td>
                   <td class="value">£${calculations.settlementAmount.toFixed(2)}</td>
               </tr>
           </table>
       </div>
       
       <div class="footer-section">
           <div class="prepared-info">
               <div class="prepared-label">Prepared by</div>
               <div class="prepared-company">Sojourn Executive</div>
           </div>
           
           <div class="company-logo">
               <div class="company-name">SOJOURN</div>
               <div class="company-tagline">EXECUTIVE</div>
           </div>
           
           <div class="footer-meta">
               <div>${new Date().getFullYear()}</div>
               <div>1</div>
           </div>
       </div>
   </div>
</body>
</html>
     `;
},

   // Méthodes helper
// Dans reservationsView.vue, remplacer les méthodes openReportModal() et getPropertySettings() par :

async openReportModal(displayName, period) {
  let data, propertyId;

  if (period === 'current') {
    data = this.groupedCurrentMonth[displayName];
    propertyId = this.selectedCurrentProperty;
  } else if (period === 'lastmonth') {
    data = this.groupedLastMonth[displayName];
    propertyId = this.selectedLastMonthProperty;
  } else if (period === 'historical') {
    data = this.searchResults;
    propertyId = this.selectedProperty || this.getPropertyIdByName(displayName);
  }

  if (!data || data.length === 0) {
    alert('No reservations found for this property and period.');
    return;
  }

  let periodLabel;
  if (period === 'current') {
    periodLabel = 'Current Month';
  } else if (period === 'lastmonth') {
    periodLabel = 'Last Month';
  } else if (period === 'historical') {
    // Use searchResultsMonth which stores the month that the current searchResults correspond to
    // This prevents showing wrong month when appliedFilters.month is outdated
    const monthToUse = this.searchResultsMonth || this.appliedFilters.month;
    const selectedMonthData = this.availableMonths.find(m => m.value === monthToUse);
    periodLabel = selectedMonthData ? selectedMonthData.label : 'Historical Month';
    console.log('🔍 Historical report - Using month:', monthToUse, 'Label:', periodLabel);
  }

  this.modalData = {
    propertyName: displayName,
    periodLabel: periodLabel,
    reservations: [...data],
    realPropertyId: propertyId
  };

  // ALWAYS reload settings from database to ensure they are up-to-date
  await this.reloadPropertySettingsFromDB(propertyId);

  // Now get the fresh settings from cache (just reloaded from DB)
  const savedSettings = this.getPropertySettings(propertyId);

  this.modalSettings = {
    managementFeeRate: parseFloat(savedSettings.managementFeeRate) || 16,
    managementFeeBase: savedSettings.managementFeeBase || 'net',
    managementFeeFixedAmount: parseFloat(savedSettings.managementFeeFixedAmount) || 500,
    vatRate: parseFloat(savedSettings.vatRate) || 20,
    maintenance: parseFloat(savedSettings.maintenance) || 0,
    useCustomMaintenance: false,
    customMaintenanceAmount: 0,
    consumables: parseFloat(savedSettings.consumables) || 20,
    useCustomConsumables: false,
    customConsumablesAmount: 0,
    laundry: parseFloat(savedSettings.laundry) || 0,
    laundryCalculationMode: savedSettings.laundryCalculationMode || 'fixed',
    laundryPerBooking: parseFloat(savedSettings.laundryPerBooking) || 15,
    otherExpenses: parseFloat(savedSettings.otherExpenses) || 0,
    useCustomOtherExpenses: false,
    customOtherExpensesAmount: 0,
    cleaningMode: savedSettings.cleaningMode || 'rentlio',
    bookingCleaningFee: parseFloat(savedSettings.bookingCleaningFee) || 150,
    otherCleaningFee: parseFloat(savedSettings.otherCleaningFee) || 120,
    cleaningFee: parseFloat(savedSettings.cleaningFee) || 0,
    cleaningPerBooking: parseFloat(savedSettings.cleaningPerBooking) || 150,
    cardFeeMode: savedSettings.cardFeeMode || 'rentlio',
    customCardFeeRate: parseFloat(savedSettings.customCardFeeRate) || 2.9,
    customCardFeeFixed: parseFloat(savedSettings.customCardFeeFixed) || 0.30,
    platformFeeMode: savedSettings.platformFeeMode || 'rentlio',
    applyCustomBookingCom: savedSettings.applyCustomBookingCom || false,
    bookingComCommissionRate: parseFloat(savedSettings.bookingComCommissionRate) || 15.00,
    applyCustomAirbnb: savedSettings.applyCustomAirbnb || false,
    airbnbCommissionRate: parseFloat(savedSettings.airbnbCommissionRate) || 3.00,
    applyCustomOther: savedSettings.applyCustomOther || false,
    otherPlatformCommissionRate: parseFloat(savedSettings.otherPlatformCommissionRate) || 10.00
  };

  this.originalSettings = { ...this.modalSettings };
  this.hasUnsavedChanges = false;

  // Vérifier s'il existe un montant fixe spécifique pour ce mois
  await this.checkMonthSpecificFee(propertyId, periodLabel);

  // Charger les dépenses supplémentaires (Other Maintenance) pour ce mois
  const [monthName, yearStr] = periodLabel.split(' ');
  const monthMap = {
    'January': 1, 'February': 2, 'March': 3, 'April': 4,
    'May': 5, 'June': 6, 'July': 7, 'August': 8,
    'September': 9, 'October': 10, 'November': 11, 'December': 12
  };

  let month, year;
  if (periodLabel === 'Current Month') {
    const now = new Date();
    month = now.getMonth() + 1;
    year = now.getFullYear();
  } else if (periodLabel === 'Last Month') {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    month = lastMonth.getMonth() + 1;
    year = lastMonth.getFullYear();
  } else if (monthName && yearStr) {
    month = monthMap[monthName];
    year = parseInt(yearStr);
  }

  if (month && year) {
    await this.loadPropertyExpenses(propertyId, year, month);

    // Auto-detect booking count and load monthly overrides if exist
    this.autoDetectBookingCount();
    await this.loadMonthlyLaundryOverride(propertyId, year, month);
    await this.loadMonthlyCleaningOverride(propertyId, year, month);
  }

  this.calculateMetrics();
  this.generatePreviewHTML();
  this.showReportModal = true;
},

// CORRECTION de getPropertyIdByName() pour garantir la correspondance:
getPropertyIdByName(propertyName) {
  console.log('Searching for property with name:', propertyName);
  console.log('Available properties:', this.properties.map(p => ({ id: p.id, name: p.name })));
  
  const property = this.properties.find(p => p.name === propertyName);
  const propertyId = property ? property.id : null;
  
  console.log('Found property ID:', propertyId, 'for name:', propertyName);
  
  if (!propertyId) {
    console.warn('Property not found! Available names:', this.properties.map(p => p.name));
  }
  
  return propertyId;
},

getPropertyAddress(propertyId) {
  if (!propertyId) return '';

  // Chercher la propriété dans la liste
  const property = this.properties.find(p => p.id == propertyId);
  if (!property) return '';

  // Rentlio retourne address, city, postalCode au premier niveau
  const parts = [];
  if (property.address) parts.push(property.address);
  if (property.city && property.postalCode) {
    parts.push(`${property.city} ${property.postalCode}`);
  } else if (property.city) {
    parts.push(property.city);
  }

  return parts.join(', ');
},

// CORRECTION de getPropertySettings() pour garantir le bon chargement:
// Load all property settings from API
async loadAllPropertySettings() {
  const token = localStorage.getItem('token');
  if (!token) return;

  const headers = { 'Authorization': `Bearer ${token}` };

  try {
    const response = await axios.get(
      `${this.baseUrl}/api/pms/settings/all/`,
      { headers }
    );

    if (response.data.success) {
      // Map backend data (snake_case) to frontend format (camelCase)
      const settingsData = response.data.data;
      this.allPropertySettings = {};

      for (const [propertyId, data] of Object.entries(settingsData)) {
        this.allPropertySettings[propertyId] = {
          managementFeeRate: parseFloat(data.management_fee_rate || 16),
          managementFeeBase: data.management_fee_base || 'net',
          managementFeeFixedAmount: parseFloat(data.management_fee_fixed_amount || 500),
          vatRate: parseFloat(data.vat_rate || 20),
          maintenance: parseFloat(data.maintenance || 0),
          consumables: parseFloat(data.consumables || 20),
          laundry: parseFloat(data.laundry_fee || 0),
          laundryCalculationMode: data.laundry_calculation_mode || 'fixed',
          laundryPerBooking: parseFloat(data.laundry_per_booking || 15),
          laundryVatApplicable: data.laundry_vat_applicable !== undefined ? data.laundry_vat_applicable : true,
          otherExpenses: parseFloat(data.other_expenses || 0),
          cleaningMode: data.cleaning_mode || 'rentlio',
          bookingCleaningFee: parseFloat(data.booking_cleaning_fee || 150),
          otherCleaningFee: parseFloat(data.other_cleaning_fee || 120),
          cleaningFee: parseFloat(data.cleaning_fee || 0),
          cleaningPerBooking: parseFloat(data.cleaning_per_booking || 150),
          cardFeeMode: data.card_fee_mode || 'rentlio',
          customCardFeeRate: parseFloat(data.custom_card_fee_rate || 2.9),
          customCardFeeFixed: parseFloat(data.custom_card_fee_fixed || 0.30),
          platformFeeMode: data.platform_fee_mode || 'rentlio',
          applyCustomBookingCom: data.apply_custom_booking_com || false,
          bookingComCommissionRate: parseFloat(data.booking_com_commission_rate || 15.00),
          applyCustomAirbnb: data.apply_custom_airbnb || false,
          airbnbCommissionRate: parseFloat(data.airbnb_commission_rate || 3.00),
          applyCustomOther: data.apply_custom_other || false,
          otherPlatformCommissionRate: parseFloat(data.other_platform_commission_rate || 10.00)
        };
      }

      console.log('Loaded property settings from API:', this.allPropertySettings);
    }
  } catch (error) {
    console.error('Error loading property settings from API:', error);
  }
},

// Reload settings for a specific property from database and update modal
async reloadPropertySettingsFromDB(propertyId) {
  const token = localStorage.getItem('token');
  if (!token) return;

  const headers = { 'Authorization': `Bearer ${token}` };

  try {
    const response = await axios.get(
      `${this.baseUrl}/api/pms/settings/${propertyId}/`,
      { headers }
    );

    if (response.data.success) {
      const data = response.data.data;

      // Update cache with fresh data from database
      this.allPropertySettings[propertyId] = {
        managementFeeRate: parseFloat(data.management_fee_rate || 16),
        managementFeeBase: data.management_fee_base || 'net',
        managementFeeFixedAmount: parseFloat(data.management_fee_fixed_amount || 500),
        vatRate: parseFloat(data.vat_rate || 20),
        maintenance: parseFloat(data.maintenance || 0),
        consumables: parseFloat(data.consumables || 20),
        laundry: parseFloat(data.laundry_fee || 0),
        laundryCalculationMode: data.laundry_calculation_mode || 'fixed',
        laundryPerBooking: parseFloat(data.laundry_per_booking || 15),
        laundryVatApplicable: data.laundry_vat_applicable !== undefined ? data.laundry_vat_applicable : true,
        otherExpenses: parseFloat(data.other_expenses || 0),
        cleaningMode: data.cleaning_mode || 'rentlio',
        bookingCleaningFee: parseFloat(data.booking_cleaning_fee || 150),
        otherCleaningFee: parseFloat(data.other_cleaning_fee || 120),
        cleaningFee: parseFloat(data.cleaning_fee || 0),
        cleaningPerBooking: parseFloat(data.cleaning_per_booking || 150),
        cardFeeMode: data.card_fee_mode || 'rentlio',
        customCardFeeRate: parseFloat(data.custom_card_fee_rate || 2.9),
        customCardFeeFixed: parseFloat(data.custom_card_fee_fixed || 0.30),
        platformFeeMode: data.platform_fee_mode || 'rentlio',
        applyCustomBookingCom: data.apply_custom_booking_com || false,
        bookingComCommissionRate: parseFloat(data.booking_com_commission_rate || 15.00),
        applyCustomAirbnb: data.apply_custom_airbnb || false,
        airbnbCommissionRate: parseFloat(data.airbnb_commission_rate || 3.00),
        applyCustomOther: data.apply_custom_other || false,
        otherPlatformCommissionRate: parseFloat(data.other_platform_commission_rate || 10.00)
      };

      // Update modal settings to reflect saved values
      this.modalSettings = { ...this.allPropertySettings[propertyId] };
      this.originalSettings = { ...this.allPropertySettings[propertyId] };

      console.log('✅ Reloaded settings from DB for property', propertyId);
    }
  } catch (error) {
    console.error('❌ Error reloading property settings from DB:', error);
  }
},

getPropertySettings(propertyId) {
  console.log('Getting settings for property ID:', propertyId);

  if (!propertyId) {
    console.log('No property ID, using defaults');
    return this.getDefaultSettings();
  }

  // Use settings from API if available
  if (this.allPropertySettings[propertyId]) {
    console.log('Using API settings for property', propertyId);
    return this.allPropertySettings[propertyId];
  }

  console.log('No settings found for this property, using defaults');
  return this.getDefaultSettings();
},

   getDefaultSettings() {
     return {
       managementFeeRate: 16.0,
       managementFeeBase: 'net',
       vatRate: 20.0,
       maintenance: 0.0,
       consumables: 20.0,
       laundry: 0.0,
       laundryCalculationMode: 'fixed',
       laundryPerBooking: 15.0,
       laundryVatApplicable: true,
       otherExpenses: 0.0,
       cleaningMode: 'rentlio',
       bookingCleaningFee: 150.0,
       otherCleaningFee: 120.0,
       cleaningFee: 0.0,
       cleaningPerBooking: 150.0,
       cardFeeMode: 'rentlio',
       customCardFeeRate: 2.9,
       customCardFeeFixed: 0.00,
       platformFeeMode: 'rentlio',
       applyCustomBookingCom: false,
       bookingComCommissionRate: 15.00,
       applyCustomAirbnb: false,
       airbnbCommissionRate: 3.00,
       applyCustomOther: false,
       otherPlatformCommissionRate: 10.00
     };
   },

   // Existing Excel methods
   downloadReportPropertyExcel(unitName, period) {
     let data;
     if (period === 'current') {
       data = this.groupedCurrentMonth[unitName];
     } else if (period === 'lastmonth') {
       data = this.groupedLastMonth[unitName];
     } else if (period === 'historical') {
       data = this.searchResults;
     }
     this.generateReportExcel(data, `${unitName}_${period}_report`, unitName);
   },

   generateReportExcel(reservations, filename, propertyName) {
     const propertyId = this.getPropertyIdByName(propertyName);
     const propertySettings = this.getPropertySettings(propertyId);
     
     const data = reservations.map(item => {
       const amount = parseFloat(item.totalReservationPrice || 0);
       const fees = this.calculateCustomCommission(item, this.modalSettings);
       
       let cardFee = 0;
       if (item.otaChannelName == 'Booking.com') {
         if (propertySettings.cardFeeMode === 'rentlio') {
           const totalWithFees = parseFloat(item.totalCost || item.totalReservationPrice || 0);
           const basePrice = parseFloat(item.accommodationCost || 0);
           const vatAmount = parseFloat(item.vatAmount || 0);
           cardFee = Math.max(0, totalWithFees - basePrice - vatAmount);
         } else {
           cardFee = (amount * propertySettings.customCardFeeRate / 100);
         }
       }
       
       let outgoings = 0;
       const isExtension = (item.guestName || '').toLowerCase().includes('extension');

       if (isExtension) {
         outgoings = 0;
       } else {
         if (propertySettings.cleaningMode === 'rentlio') {
           outgoings = parseFloat(item.totalServicesPrice || 0);
         } else {
           // Custom Rates: Appliquer les frais uniquement au dernier segment
           const isLastSegment = !item.isSegment ||
             (item.segmentInfo?.part === item.segmentInfo?.total);

           if (isLastSegment) {
             const platform = item.otaChannelName || '';
             if (platform === 'Booking.com') {
               outgoings = propertySettings.bookingCleaningFee || 150;
             } else {
               outgoings = propertySettings.otherCleaningFee || 120;
             }
           } else {
             // Segment intermédiaire, pas de frais de nettoyage
             outgoings = 0;
           }
         }
       }
       
       const netIncome = amount - fees - cardFee;
       const grossProfit = netIncome - outgoings;
       const perNight = item.totalNights > 0 ? amount / item.totalNights : 0;

       return [
         item.guestName || '',
         item.otaChannelName || 'Direct',
         item.totalNights || 0,
         amount.toFixed(2),
         fees.toFixed(2),
         cardFee.toFixed(2),
         netIncome.toFixed(2),
         outgoings.toFixed(2),
         grossProfit.toFixed(2),
         perNight.toFixed(2)
       ];
     });

     const totalNights = data.reduce((sum, r) => sum + r[2], 0);
     const totalAmount = data.reduce((sum, r) => sum + parseFloat(r[3]), 0);
     const correctPerNight = data.length > 0 ? (data.reduce((sum, r) => sum + parseFloat(r[9]), 0) / data.length).toFixed(2) : '0.00';

     const totals = [
       'Totals',
       '',
       totalNights,
       totalAmount.toFixed(2),
       data.reduce((sum, r) => sum + parseFloat(r[4]), 0).toFixed(2),
       data.reduce((sum, r) => sum + parseFloat(r[5]), 0).toFixed(2),
       data.reduce((sum, r) => sum + parseFloat(r[6]), 0).toFixed(2),
       data.reduce((sum, r) => sum + parseFloat(r[7]), 0).toFixed(2),
       data.reduce((sum, r) => sum + parseFloat(r[8]), 0).toFixed(2),
       correctPerNight
     ];

     const totalRevenue = parseFloat(totals[3]);
     const totalCardFees = parseFloat(totals[5]);
     const netIncomeTotal = parseFloat(totals[6]);
     const grossProfitTotal = parseFloat(totals[8]);
     
     let managementFee = 0;
     let managementFeeLabel = '';
     
     if (propertySettings.managementFeeBase === 'revenue') {
      managementFee = (totalRevenue * propertySettings.managementFeeRate) / 100;
      managementFeeLabel = `Management fee (${propertySettings.managementFeeRate}% on Total Revenue)`;
    } else if (propertySettings.managementFeeBase === 'gross') {
      managementFee = (grossProfitTotal * propertySettings.managementFeeRate) / 100;
      managementFeeLabel = `Management fee (${propertySettings.managementFeeRate}% on Gross Profit)`;
    } else {
      managementFee = (netIncomeTotal * propertySettings.managementFeeRate) / 100;
      managementFeeLabel = `Management fee (${propertySettings.managementFeeRate}% on Net Income)`;
    }

     const vat = (managementFee * propertySettings.vatRate) / 100;
     const maintenance = propertySettings.maintenance;
     const consumables = propertySettings.consumables;
     const otherExpenses = propertySettings.otherExpenses;
     const toClient = grossProfitTotal - managementFee - vat - maintenance - consumables - otherExpenses;

     const ws = XLSX.utils.aoa_to_sheet([
       [propertyName || 'Property Report'],
       [''],
       ['Booking Name', 'Source', 'No. Nights', 'Amount', 'Fees', 'Card Fee', 'Net Income', 'Outgoings', 'Gross Profit', 'Per Night'],
       ...data,
       totals,
       ['', '', '', '', '', '', '', '', '', ''],
       ['', '', '', '', '', '', '', 'Profit', '', grossProfitTotal.toFixed(2)],
       ['', '', '', '', '', '', '', '', '', ''],
       ['Days in month', '', 31, '', '', '', '', managementFeeLabel, '', managementFee.toFixed(2)],
       ['Occupancy', '', Math.round((totals[2] / 31) * 100) + '%', '', '', '', '', 'VAT', '', vat.toFixed(2)],
       ['', '', '', '', '', '', '', 'Maintenance', '', maintenance.toFixed(2)],
       ['', '', '', '', '', '', '', 'Consumables', '', consumables.toFixed(2)],
       ['', '', '', '', '', '', '', 'Other Expenses', '', otherExpenses.toFixed(2)],
       ['', '', '', '', '', '', '', '', '', ''],
       ['', '', '', '', '', '', '', 'To Client', '', toClient.toFixed(2)]
     ]);

     ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 9 } }];

     const wb = XLSX.utils.book_new();
     XLSX.utils.book_append_sheet(wb, ws, propertyName || 'Report');
     XLSX.writeFile(wb, `${filename}.xlsx`);
   },

   downloadPropertyExcel(unitName, period) {
     let data;
     if (period === 'current') {
       data = this.groupedCurrentMonth[unitName];
     } else if (period === 'lastmonth') {
       data = this.groupedLastMonth[unitName];
     } else if (period === 'historical') {
       data = this.searchResults;
     }
     this.generateExcelReport(data, `${unitName}_${period}_report`);
   },
   
   generateExcelReport(reservations, filename) {
     const data = reservations.map(item => ({
       'Booking Name': item.guestName,
       'Source': item.otaChannelName || 'Direct',
       'No. Nights': item.totalNights,
       'Amount': parseFloat(item.totalReservationPrice).toFixed(2),
       'Fees': this.calculateCustomCommission(item, this.modalSettings).toFixed(2),
       'Card Fee': parseFloat(item.cardFee || 0).toFixed(2),
       'Gross Income': parseFloat(item.totalReservationPrice).toFixed(2),
       'Outgoings': parseFloat(item.totalServicesPrice).toFixed(2),
       'Net Income': parseFloat(item.accommodationCost).toFixed(2),
       'Per Night': parseFloat(item.pricePerNight).toFixed(2),
       'Arrival Date': this.formatDate(item.arrivalDate),
       'Departure Date': this.formatDate(item.departureDate),
     }));

     const ws = XLSX.utils.json_to_sheet(data);
    
     const totalNightsExcel = reservations.reduce((sum, r) => sum + r.totalNights, 0);
     const totalAmountExcel = reservations.reduce((sum, r) => sum + parseFloat(r.totalReservationPrice), 0);
     const correctPerNightExcel = reservations.length > 0 ? (reservations.reduce((sum, r) => sum + parseFloat(r.pricePerNight), 0) / reservations.length).toFixed(2) : '0.00';

     XLSX.utils.sheet_add_aoa(ws, [
       ["", "", "", "", "", "", "", "", "", "", ""],
       ["Totals", "", 
        totalNightsExcel,
        totalAmountExcel.toFixed(2),
        reservations.reduce((sum, r) => sum + this.calculateCustomCommission(r, this.modalSettings), 0).toFixed(2),
        "",
        totalAmountExcel.toFixed(2),
        reservations.reduce((sum, r) => sum + parseFloat(r.totalServicesPrice), 0).toFixed(2),
        reservations.reduce((sum, r) => sum + parseFloat(r.accommodationCost), 0).toFixed(2),
        correctPerNightExcel
       ]
     ], { origin: -1 });

     const wb = XLSX.utils.book_new();
     XLSX.utils.book_append_sheet(wb, ws, 'Report');
     XLSX.writeFile(wb, `${filename}.xlsx`);
   },

   // Méthodes de données existantes
   async fetchData() {
     const token = localStorage.getItem('token');
     if (!token) {
       this.$router.push('/login');
       return;
     }

     // Get selected PMS configuration from localStorage
     const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig'));
     if (!selectedPms || !selectedPms.id) {
       alert('No PMS configuration selected. Please select a PMS from the Hub.');
       this.$router.push('/');
       return;
     }

     const pmsConfigId = selectedPms.id;
     const headers = { 'Authorization': `Bearer ${token}` };

     try {
       const propertiesRes = await axios.get(`${this.baseUrl}/api/pms/properties/`, {
         headers,
         params: { pms_config_id: pmsConfigId }
       });
       this.properties = propertiesRes.data.properties.data;

       this.loading = true;
       const currentRes = await axios.get(`${this.baseUrl}/api/pms/reports/current/`, {
         headers,
         params: { pms_config_id: pmsConfigId, property_id: this.selectedCurrentProperty }
       });
       this.currentMonthReservations = Array.isArray(currentRes.data.reservations)
         ? currentRes.data.reservations
         : Object.values(currentRes.data.reservations);
       this.loading = false;

       this.loadingLastMonth = true;
       const lastMonthRes = await axios.get(`${this.baseUrl}/api/pms/reports/lastmonth/`, {
         headers,
         params: { pms_config_id: pmsConfigId, property_id: this.selectedLastMonthProperty }
       });
       this.lastMonthReservations = Array.isArray(lastMonthRes.data.reservations)
         ? lastMonthRes.data.reservations
         : Object.values(lastMonthRes.data.reservations);
       this.loadingLastMonth = false;

     } catch (error) {
       console.error('Error fetching data:', error);
       if (error.response && error.response.status === 401) {
         localStorage.removeItem('token');
         this.$router.push('/login');
       }
     }
   },

   async onCurrentPropertyChange() {
     const token = localStorage.getItem('token');
     if (!token) return;

     const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig'));
     if (!selectedPms || !selectedPms.id) return;

     const pmsConfigId = selectedPms.id;
     const headers = { 'Authorization': `Bearer ${token}` };

     try {
       this.loading = true;
       const currentRes = await axios.get(`${this.baseUrl}/api/pms/reports/current/`, {
         headers,
         params: { pms_config_id: pmsConfigId, property_id: this.selectedCurrentProperty }
       });
       this.currentMonthReservations = Array.isArray(currentRes.data.reservations)
         ? currentRes.data.reservations
         : Object.values(currentRes.data.reservations);
       this.loading = false;
     } catch (error) {
       console.error('Error fetching current month data:', error);
       this.loading = false;
     }
   },

   async onLastMonthPropertyChange() {
     const token = localStorage.getItem('token');
     if (!token) return;

     const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig'));
     if (!selectedPms || !selectedPms.id) return;

     const pmsConfigId = selectedPms.id;
     const headers = { 'Authorization': `Bearer ${token}` };

     try {
       this.loadingLastMonth = true;
       const lastMonthRes = await axios.get(`${this.baseUrl}/api/pms/reports/lastmonth/`, {
         headers,
         params: { pms_config_id: pmsConfigId, property_id: this.selectedLastMonthProperty }
       });
       this.lastMonthReservations = Array.isArray(lastMonthRes.data.reservations)
         ? lastMonthRes.data.reservations
         : Object.values(lastMonthRes.data.reservations);
       this.loadingLastMonth = false;
     } catch (error) {
       console.error('Error fetching last month data:', error);
       this.loadingLastMonth = false;
     }
   },

   async applyFilters() {
     if (!this.selectedMonth) {
       alert('Please select a month');
       return;
     }

     if (!this.selectedProperty) {
       alert('Please select a property');
       return;
     }

     this.appliedFilters = {
       property: this.selectedProperty,
       month: this.selectedMonth
     };

     // Store the month for searchResults to ensure openReportModal uses the correct month
     this.searchResultsMonth = this.selectedMonth;

     this.loadingHistory = true;
     this.searchResults = [];

     const token = localStorage.getItem('token');
     if (!token) {
       this.$router.push('/login');
       return;
     }

     // Get selected PMS configuration
     const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig'));
     if (!selectedPms || !selectedPms.id) {
       alert('No PMS configuration selected. Please select a PMS from the Hub.');
       return;
     }
     const pmsConfigId = selectedPms.id;

     const headers = { 'Authorization': `Bearer ${token}` };
     
     try {
       console.log(`Fetching reservations for month: ${this.selectedMonth}, property: ${this.selectedProperty}`);

       // Trouver le type de mois sélectionné
       const selectedMonthData = this.availableMonths.find(m => m.value === this.selectedMonth);
       const monthType = selectedMonthData ? selectedMonthData.type : 'historical';

       let response;
       let url;
       
       if (monthType === 'current') {
         // Use the current reports endpoint
         console.log('Using current month report endpoint');
         url = `${this.baseUrl}/api/pms/reports/current/?pms_config_id=${pmsConfigId}&property_id=${this.selectedProperty}`;
         response = await axios.get(url, { headers });
         this.searchResults = response.data.reservations || [];
       } else if (monthType === 'lastmonth') {
         // Use the last month reports endpoint
         console.log('Using last month report endpoint');
         url = `${this.baseUrl}/api/pms/reports/lastmonth/?pms_config_id=${pmsConfigId}&property_id=${this.selectedProperty}`;
         response = await axios.get(url, { headers });
         this.searchResults = response.data.reservations || [];
       } else {
         // Pour les mois historiques, calculer les dates de début et fin du mois
         console.log('Using generic reservations endpoint for historical month');
         console.log('Selected Property ID:', this.selectedProperty);
         const year = parseInt(this.selectedMonth.substring(0, 4));
         const month = parseInt(this.selectedMonth.substring(5, 7)); // 1-12

         // Calculer le premier et dernier jour du mois
         const firstDay = 1;
         const lastDay = new Date(year, month, 0).getDate(); // Dernier jour du mois

         // Formater les dates en YYYY-MM-DD
         const dateFrom = `${year}-${month.toString().padStart(2, '0')}-01`;
         const dateTo = `${year}-${month.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;

         url = `${this.baseUrl}/api/pms/reservations/?date_from=${dateFrom}&date_to=${dateTo}&per_page=200&order_by=arrivalDate&order_direction=ASC&property_id=${this.selectedProperty}&pms_config_id=${pmsConfigId}`;
         console.log('Full URL being called:', url);

         response = await axios.get(url, { headers });
         console.log('Response data:', response.data);
         this.searchResults = response.data.reservations?.data || [];
         console.log('Search results after filtering:', this.searchResults.length, 'reservations');
       }

       this.currentPage = 1;
       console.log(`Final results: ${this.searchResults.length} reservations`);
       console.log('Used URL:', url);
       
     } catch (error) {
       console.error('Error fetching reservations:', error);
       alert('Error fetching reservations. Please try again.');
     } finally {
       this.loadingHistory = false;
     }
   },

   clearFilters() {
     this.selectedProperty = '';
     this.selectedMonth = '';
     this.appliedFilters = {
       property: '',
       month: ''
     };
     this.searchResultsMonth = '';
     this.searchResults = [];
     this.currentPage = 1;
     this.loadingHistory = false;
   },

   prevPage() {
     if (this.currentPage > 1) {
       this.currentPage--;
     }
   },
   
   nextPage() {
     if (this.currentPage < this.totalPages) {
       this.currentPage++;
     }
   },
   
 handleNumericInput(fieldName, event) {
    const value = event.target.value;
    
    // Si le champ est complètement vide, mettre 0
    if (value === '' || value === null || value === undefined) {
      this.modalSettings[fieldName] = 0;
      return;
    }
    
    // Convertir en nombre et s'assurer que c'est valide
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      this.modalSettings[fieldName] = 0;
    } else {
      this.modalSettings[fieldName] = numValue;
    }
  },
 

ensureMinimumValue(fieldName, minimumValue = 0) {
    // Vérifier quand l'utilisateur quitte le champ
    const currentValue = this.modalSettings[fieldName];
    
    if (currentValue === null || currentValue === undefined || currentValue === '' || isNaN(currentValue) || currentValue < minimumValue) {
      this.modalSettings[fieldName] = minimumValue;
      this.markAsModified();
      this.calculateMetrics();
    }
  
},

 // Contacts management methods
 async loadContacts() {
   const token = localStorage.getItem('token');
   if (!token) return;

   try {
     const response = await axios.get(`${this.baseUrl}/api/pms/contacts/`, {
       headers: { 'Authorization': `Bearer ${token}` }
     });

     if (response.data.status === 'success') {
       this.contacts = response.data.data;
     }
   } catch (error) {
     console.error('Error loading contacts:', error);
     if (error.response && error.response.status === 401) {
       this.$router.push('/login');
     }
   }
 },

 async addContact() {
   if (!this.newContact.name || !this.newContact.email) {
     alert('Please fill in both name and email');
     return;
   }

   // Validation du format d'email
   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
   if (!emailRegex.test(this.newContact.email)) {
     alert('Please enter a valid email address (e.g., user@domain.com)');
     return;
   }

   const token = localStorage.getItem('token');
   if (!token) {
     this.$router.push('/login');
     return;
   }

   try {
     const response = await axios.post(`${this.baseUrl}/api/pms/contacts/`, {
       name: this.newContact.name,
       email: this.newContact.email
     }, {
       headers: { 'Authorization': `Bearer ${token}` }
     });

     if (response.data.status === 'success') {
       // Ajouter le nouveau contact à la liste
       this.contacts.push(response.data.data);
       this.newContact = { name: '', email: '' };
       alert(`Contact "${response.data.data.name}" added successfully!`);
     }
   } catch (error) {
     console.error('Error adding contact:', error);
     if (error.response && error.response.status === 401) {
       this.$router.push('/login');
     } else if (error.response && error.response.data.errors) {
       const errorMsg = error.response.data.errors.email ?
         error.response.data.errors.email[0] :
         'Failed to add contact';
       alert(errorMsg);
     } else {
       alert('Failed to add contact. Please try again.');
     }
   }
 },

 editContact(contact) {
   this.editingContact = { ...contact };
 },

 async saveEditContact() {
   const token = localStorage.getItem('token');
   if (!token) {
     this.$router.push('/login');
     return;
   }

   try {
     const response = await axios.put(
       `${this.baseUrl}/api/pms/contacts/${this.editingContact.id}/`,
       {
         name: this.editingContact.name,
         email: this.editingContact.email
       },
       {
         headers: { 'Authorization': `Bearer ${token}` }
       }
     );

     if (response.data.status === 'success') {
       // Mettre à jour le contact dans la liste
       const index = this.contacts.findIndex(c => c.id === this.editingContact.id);
       if (index !== -1) {
         this.contacts[index] = response.data.data;
       }
       this.editingContact = null;
       alert('Contact updated successfully!');
     }
   } catch (error) {
     console.error('Error updating contact:', error);
     if (error.response && error.response.status === 401) {
       this.$router.push('/login');
     } else if (error.response && error.response.data.errors) {
       const errorMsg = error.response.data.errors.email ?
         error.response.data.errors.email[0] :
         'Failed to update contact';
       alert(errorMsg);
     } else {
       alert('Failed to update contact. Please try again.');
     }
   }
 },

 async deleteContact(contactId) {
   if (!confirm('Are you sure you want to delete this contact?')) {
     return;
   }

   const token = localStorage.getItem('token');
   if (!token) {
     this.$router.push('/login');
     return;
   }

   try {
     const response = await axios.delete(
       `${this.baseUrl}/api/pms/contacts/${contactId}/`,
       {
         headers: { 'Authorization': `Bearer ${token}` }
       }
     );

     if (response.data.status === 'success') {
       // Retirer le contact de la liste
       this.contacts = this.contacts.filter(c => c.id !== contactId);
       alert('Contact deleted successfully!');
     }
   } catch (error) {
     console.error('Error deleting contact:', error);
     if (error.response && error.response.status === 401) {
       this.$router.push('/login');
     } else {
       alert('Failed to delete contact. Please try again.');
     }
   }
 },

 openContactsModal() {
   this.showContactsModal = true;
 },

 closeContactsModal() {
   this.showContactsModal = false;
   this.editingContact = null;
   this.newContact = { name: '', email: '' };
 },

 // Méthodes pour gérer le dernier contact sélectionné
 getLastSelectedContact() {
   const lastContactId = localStorage.getItem('lastSelectedContactId');
   if (lastContactId) {
     return this.contacts.find(c => c.id.toString() === lastContactId);
   }
   return null;
 },

 setLastSelectedContact(contact) {
   if (contact && contact.id) {
     localStorage.setItem('lastSelectedContactId', contact.id.toString());
   }
 },

 
 formatContactDate(dateString) {
   const date = new Date(dateString);
   return date.toLocaleDateString('en-US', { 
     month: 'short', 
     day: 'numeric',
     year: 'numeric'
   });
 },

 async sendReportByEmail() {
   if (!this.emailData.contact) {
     alert('Please select a contact');
     return;
   }

   this.setLastSelectedContact(this.emailData.contact);
   this.sendingEmail = true;
   
   // Afficher immédiatement le popup de confirmation
   this.displayEmailSentPopup();
   
   try {
     const token = localStorage.getItem('token');
     if (!token) {
       this.$router.push('/login');
       return;
     }

     // Préparer le message du rapport mensuel en anglais
     const hasReceipts = this.propertyExpenses.some(expense => expense.receipt_file);
     const receiptsMessage = hasReceipts
       ? '<p style="background: #fff8e1; padding: 10px; border-left: 4px solid #ffd54f; margin: 15px 0;"><strong>📎 Note:</strong> Supporting documents (receipts) for additional maintenance expenses are attached to this email for your reference.</p>'
       : '';

     const message = `
       <h2>Dear ${this.emailData.contact.name},</h2>
       <p>Please find attached your monthly property report for <strong>${this.emailData.propertyName}</strong> for the period <strong>${this.emailData.period}</strong>.</p>

       ${receiptsMessage}

       <div style="background: #f8fafc; padding: 15px; border-radius: 8px; margin: 20px 0;">
         <h3 style="margin: 0 0 10px 0; color: #667eea;">📊 Report Summary</h3>
         <p style="margin: 5px 0;"><strong>Property:</strong> ${this.emailData.propertyName}</p>
         <p style="margin: 5px 0;"><strong>Period:</strong> ${this.emailData.period}</p>
         <p style="margin: 5px 0;"><strong>Report Type:</strong> ${this.emailData.reportType}</p>
       </div>

       <p>The report includes detailed information about:</p>
       <ul>
         <li>Booking overview and revenue analysis</li>
         <li>Income and direct expenses breakdown</li>
         <li>Management fees and operational costs</li>
         <li>Settlement summary</li>
       </ul>

       <p style="margin-top: 20px;">If you have any questions about this report, please don't hesitate to contact us.</p>

       <p>Best regards,<br>
       <strong>Sojourn Executive Team</strong></p>
     `;

     const emailPayload = {
       to: this.emailData.contact.email,
       subject: `Monthly Report - ${this.emailData.propertyName} - ${this.emailData.period}`,
       message: message,
       fromName: "Sojourn Reports"
     };

     // Use NEW VAT-aware endpoint for PDF generation and email sending
     if (this.emailOptions.includePDF) {
       try {
         console.log('Preparing report data for NEW VAT-aware PDF generation...');

         // Use the new prepareBookingsForBackend() function
         const reportData = this.prepareBookingsForBackend();

         // Add email configuration
         reportData.send_email = true;
         reportData.to_email = this.emailData.contact.email;
         reportData.subject = `Monthly Report - ${this.emailData.propertyName} - ${this.emailData.period}`;

         // Build email message
         reportData.email_message = message;

         // Add personal message if provided
         if (this.emailOptions.message && this.emailOptions.message.trim()) {
           reportData.email_message = `
             <div style="background: #e8f4f8; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
               <h4 style="margin: 0 0 10px 0; color: #2c5282;">Personal Message:</h4>
               <p style="margin: 0; font-style: italic;">${this.emailOptions.message}</p>
             </div>
             ${reportData.email_message}
           `;
         }

         console.log('Report data prepared, calling /api/pms/generate-report-vat/');

         // Validation de l'email
         const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
         if (!reportData.to_email || !emailRegex.test(reportData.to_email)) {
           throw new Error(`Invalid email address: "${reportData.to_email}"`);
         }

         // Vérifier le token avant l'envoi
         if (!token) {
           throw new Error('Authentication token is missing');
         }

         const response = await axios.post(`${this.baseUrl}/api/pms/generate-report-vat/`, reportData, {
           headers: {
             'Authorization': `Bearer ${token}`,
             'Content-Type': 'application/json'
           },
           timeout: 480000, // 8 minutes timeout
           maxContentLength: 50 * 1024 * 1024, // 50MB max
         });

         console.log('API Response:', response.data);

         if (response.data.success) {
           console.log('✅ Email sent successfully with VAT-aware PDF');
           // Pas d'alert car le popup s'affiche déjà
         } else {
           throw new Error(response.data.message || 'Failed to send email');
         }

       } catch (error) {
         console.error('Error sending email with VAT system:', error);
         throw error;
       }
     } else {
       // Send email without PDF using old endpoint
       const emailPayloadSimple = {
         to: this.emailData.contact.email,
         subject: `Monthly Report - ${this.emailData.propertyName} - ${this.emailData.period}`,
         message: message,
         fromName: "Sojourn Reports"
       };

       // Add personal message if provided
       if (this.emailOptions.message && this.emailOptions.message.trim()) {
         emailPayloadSimple.message = `
           <div style="background: #e8f4f8; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
             <h4 style="margin: 0 0 10px 0; color: #2c5282;">Personal Message:</h4>
             <p style="margin: 0; font-style: italic;">${this.emailOptions.message}</p>
           </div>
           ${emailPayloadSimple.message}
         `;
       }

       // Get selected PMS configuration
       const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig'));
       if (!selectedPms || !selectedPms.id) {
         throw new Error('No PMS configuration selected');
       }
       emailPayloadSimple.pms_config_id = selectedPms.id;

       const response = await axios.post(`${this.baseUrl}/api/pms/send-email/`, emailPayloadSimple, {
         headers: {
           'Authorization': `Bearer ${token}`,
           'Content-Type': 'application/json'
         }
       });

       if (!(response.status === 200 && response.data && response.data.status === 'success')) {
         throw new Error(response.data?.message || 'Email sending failed');
       }
     }

     // Success - email sent
     this.sendingEmail = false;
     this.savedReportData = null;
   } catch (error) {
     console.error('Error sending email:', error);
     this.sendingEmail = false;
     this.savedReportData = null; // Nettoyer en cas d'erreur aussi
     let errorMessage = 'Failed to send email. Please try again.';
     
     if (error.response?.data?.message) {
       errorMessage = error.response.data.message;
     } else if (error.message) {
       errorMessage = error.message;
     }
     
     // alert(`❌ Error: ${errorMessage}`);
     console.error('Email sending error:', errorMessage);
   } finally {
     this.sendingEmail = false;
   }
 },

 // Méthodes pour Email Modal avec contacts
 openEmailModal(reportType, propertyName, period) {
   // Recharger les contacts depuis localStorage au cas où ils auraient été modifiés
   this.loadContacts();
   
   // Essayer de pré-sélectionner le dernier contact utilisé
   const lastContact = this.getLastSelectedContact();
   
   // Configurer les données du modal email
   this.emailData = {
     contact: lastContact,
     reportType: reportType,
     propertyName: propertyName,
     period: period
   };
   
   // Réinitialiser les options email
   this.emailOptions = {
     includeExcel: false,
     includePDF: true,
     message: ''
   };
   
   // Ouvrir le modal
   this.showEmailModal = true;
 },

 closeEmailModal() {
   this.showEmailModal = false;
   this.emailData = {
     contact: null,
     reportType: '',
     propertyName: '',
     period: ''
   };
 },

 displayEmailSentPopup() {
   // Fermer le modal d'email
   this.showEmailModal = false;
   
   // Afficher le popup de confirmation
   this.showEmailSentPopup = true;
   
   // Fermer automatiquement après 8 secondes
   setTimeout(() => {
     this.showEmailSentPopup = false;
   }, 8000);
 },

 closeEmailSentPopup() {
   this.showEmailSentPopup = false;
 },

 async generatePDFForEmail(propertyName, period) {
   try {
     // Utiliser la même logique que downloadPDF mais créer un File object
     const jsPDF = (await import('jspdf')).default;
     const html2canvas = (await import('html2canvas')).default;

     // Utiliser EXACTEMENT la même logique que downloadPDF()
     const iframe = document.createElement('iframe');
     iframe.style.cssText = `
       position: absolute !important;
       top: -10000px !important;
       left: -10000px !important;
       width: 210mm !important;
       height: 297mm !important;
       border: none !important;
       opacity: 0 !important;
       pointer-events: none !important;
     `;
     
     document.body.appendChild(iframe);
     
     // Utiliser la même fonction que downloadPDF()
     const pdfHTML = this.generatePDFHTML();
     iframe.contentDocument.open();
     iframe.contentDocument.write(pdfHTML);
     iframe.contentDocument.close();
     
     await new Promise(resolve => {
       iframe.onload = resolve;
       setTimeout(resolve, 500);
     });

     const canvas = await html2canvas(iframe.contentDocument.body, {
       scale: 1,           // Réduit de 2 à 1 pour réduire la taille
       useCORS: true,
       backgroundColor: '#ffffff',
       width: 794,
       height: 1123,
       logging: false
     });

     const pdf = new jsPDF('p', 'mm', 'a4');
     const imgData = canvas.toDataURL('image/jpeg', 0.7);  // JPEG avec compression 70%
     
     const imgWidth = 210;
     const pageHeight = 295;
     const imgHeight = (canvas.height * imgWidth) / canvas.width;
     let heightLeft = imgHeight;
     let position = 0;

     pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
     heightLeft -= pageHeight;

     while (heightLeft >= 0) {
       position = heightLeft - imgHeight;
       pdf.addPage();
       pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
       heightLeft -= pageHeight;
     }

     document.body.removeChild(iframe);
     
     // Retourner le blob PDF pour l'email (même PDF que downloadPDF())
     return pdf.output('blob');

   } catch (error) {
     console.error('Error generating PDF for email:', error);
     throw error;
   }
 },

 // Nouvelle méthode pour envoyer l'email depuis le modal avec le PDF déjà généré
 sendReportByEmailFromModal() {
   console.log('=== DEBUT sendReportByEmailFromModal ===');
   console.log('modalData AVANT sauvegarde:', this.modalData);
   console.log('Nombre de reservations:', this.modalData?.reservations?.length);

   // IMPORTANT: Sauvegarder les données du rapport AVANT d'ouvrir le modal d'email
   // car le modal de rapport peut se fermer et vider this.modalData
   // FIX: Sauvegarder calculatedMetrics pour envoyer EXACTEMENT les mêmes valeurs que l'aperçu
   this.savedReportData = {
     modalData: JSON.parse(JSON.stringify(this.modalData)),
     modalSettings: JSON.parse(JSON.stringify(this.modalSettings)),
     propertyExpenses: JSON.parse(JSON.stringify(this.propertyExpenses)),
     totalAdditionalExpenses: this.totalAdditionalExpenses,
     calculatedMetrics: JSON.parse(JSON.stringify(this.calculatedMetrics))
   };

   console.log('savedReportData APRES sauvegarde:', this.savedReportData);
   console.log('Nombre de reservations sauvegardées:', this.savedReportData?.modalData?.reservations?.length);
   console.log('calculatedMetrics sauvegardés:', this.savedReportData?.calculatedMetrics);

   // MILESTONE 2: Ouvrir le Lock Report Modal au lieu de l'ancien Email Modal
   this.openLockReportModal();
 },

 // Générer le PDF à partir du contenu actuel du modal (même que downloadPDF)
 async generatePDFFromModal() {
   try {
     // Utiliser exactement la même logique que downloadPDF mais retourner un blob
     const jsPDF = (await import('jspdf')).default;
     const html2canvas = (await import('html2canvas')).default;

     const previewContent = document.querySelector('.pdf-preview-wrapper');
     if (!previewContent) {
       throw new Error('Preview content not found');
     }

     const canvas = await html2canvas(previewContent, {
       scale: 2,
       useCORS: true,
       backgroundColor: '#ffffff',
       logging: false
     });

     const pdf = new jsPDF('p', 'mm', 'a4');
     const imgData = canvas.toDataURL('image/png');
     
     const imgWidth = 210;
     const pageHeight = 295;
     const imgHeight = (canvas.height * imgWidth) / canvas.width;
     let heightLeft = imgHeight;
     let position = 0;

     pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
     heightLeft -= pageHeight;

     while (heightLeft >= 0) {
       position = heightLeft - imgHeight;
       pdf.addPage();
       pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
       heightLeft -= pageHeight;
     }

     return pdf.output('blob');
   } catch (error) {
     console.error('Error generating PDF from modal:', error);
     throw error;
   }
 },

 // ========================================
 // MILESTONE 2: LOCK REPORT MODAL METHODS
 // ========================================

 async openLockReportModal() {
   try {
     const propertyId = this.modalData.realPropertyId;

     if (!propertyId) {
       this.showNotification(
         'error',
         'Property Not Found',
         'Property ID not found. Please try again.'
       );
       return;
     }

     // DEBUG: Vérifier baseUrl
     console.log('🔍 DEBUG baseUrl:', this.baseUrl);
     console.log('🔍 DEBUG propertyId:', propertyId);
     const fullUrl = `${this.baseUrl}/api/pms/property-owner/${propertyId}/`;
     console.log('🔍 DEBUG Full URL:', fullUrl);
     console.log('🔍 DEBUG Token:', localStorage.getItem('token') ? 'EXISTS' : 'MISSING');

     // 1. Récupérer les informations du propriétaire (Property Owner)
     const ownerResponse = await fetch(
       fullUrl,
       {
         headers: {
           'Authorization': `Bearer ${localStorage.getItem('token')}`,
           'Content-Type': 'application/json'
         }
       }
     );

     if (!ownerResponse.ok) {
       if (ownerResponse.status === 404) {
         this.showNotification(
           'warning',
           'Owner Not Configured',
           'No owner configured for this property. Please configure owner information in Property Settings first.'
         );
         // Optionnel: rediriger vers Settings
         // this.$router.push(`/settings?property_id=${propertyId}`);
         return;
       }
       throw new Error(`Failed to fetch owner: ${ownerResponse.status}`);
     }

     const ownerData = await ownerResponse.json();

     if (!ownerData.success || !ownerData.data) {
       this.showNotification(
         'warning',
         'Owner Not Configured',
         'No owner configured for this property. Please configure owner information in Property Settings first.'
       );
       return;
     }

     // 2. Remplir les données du Lock Modal
     this.lockModalData = {
       propertyName: this.modalData.propertyName,
       monthYear: this.modalData.periodLabel,
       ownerName: ownerData.data.full_legal_name,
       ownerEmail: ownerData.data.primary_email,
       propertyId: propertyId
     };

     // 3. Afficher le modal
     this.showLockModal = true;

   } catch (error) {
     console.error('Error opening Lock Report Modal:', error);
     this.showNotification(
       'error',
       'Error Loading Owner Information',
       error.message || 'An unexpected error occurred while loading owner information.'
     );
   }
 },

 closeLockModal() {
   this.showLockModal = false;
   this.lockModalData = {
     propertyName: '',
     monthYear: '',
     ownerName: '',
     ownerEmail: '',
     propertyId: null
   };
 },

 // Notification Modal Methods
 showNotification(type, title, message, details = null) {
   const icons = {
     success: '✅',
     error: '❌',
     warning: '⚠️',
     info: 'ℹ️'
   };

   this.notificationData = {
     type: type,
     icon: icons[type] || icons.info,
     title: title,
     message: message,
     details: details
   };

   this.showNotificationModal = true;
 },

 closeNotificationModal() {
   this.showNotificationModal = false;
 },

 async confirmLockReport() {
   try {
     // 1. Préparer les données du rapport (utiliser savedReportData)
     if (!this.savedReportData || !this.savedReportData.modalData) {
       this.showNotification(
         'error',
         'Report Data Not Found',
         'Report data not found. Please regenerate the report before locking.'
       );
       return;
     }

     const reportData = this.prepareBookingsForBackend();

     // 2. Appeler l'endpoint lock-report
     const response = await fetch(`${this.baseUrl}/api/pms/lock-report/`, {
       method: 'POST',
       headers: {
         'Authorization': `Bearer ${localStorage.getItem('token')}`,
         'Content-Type': 'application/json'
       },
       body: JSON.stringify(reportData)
     });

     const result = await response.json();

     if (!response.ok || !result.success) {
       this.showNotification(
         'error',
         'Failed to Lock Report',
         result.message || 'An unknown error occurred while locking the report. Please try again.'
       );
       return;
     }

     // 3. Succès !
     this.showNotification(
       'success',
       'Report Locked Successfully!',
       'This report will now appear in the Month-End screen as "Ready to send".',
       {
         'Property': result.data.owner_name,
         'Email': result.data.owner_email
       }
     );

     // 4. Fermer les modals
     this.closeLockModal();
     this.closeReportModal();

   } catch (error) {
     console.error('Error confirming lock report:', error);
     this.showNotification(
       'error',
       'Error Locking Report',
       error.message || 'An unexpected error occurred. Please try again.'
     );
   }
 },

 // Fonction pour calculer la commission personnalisée selon les paramètres
 calculateCustomCommission(reservation, settings) {
   const platformName = reservation.otaChannelName || '';
   const amount = parseFloat(reservation.totalReservationPrice || 0);

   // Si pas de settings ou mode custom non activé, utiliser Rentlio
   if (!settings || settings.platformFeeMode !== 'custom') {
     return parseFloat(reservation.channelCommissionNominal || 0);
   }

   // Identifier la plateforme et appliquer le taux si activé
   if (platformName.toLowerCase().includes('booking')) {
     if (settings.applyCustomBookingCom) {
       return amount * (parseFloat(settings.bookingComCommissionRate) / 100);
     }
   } else if (platformName.toLowerCase().includes('airbnb')) {
     if (settings.applyCustomAirbnb) {
       return amount * (parseFloat(settings.airbnbCommissionRate) / 100);
     }
   } else {
     if (settings.applyCustomOther) {
       return amount * (parseFloat(settings.otherPlatformCommissionRate) / 100);
     }
   }

   // Sinon, utiliser les données Rentlio
   return parseFloat(reservation.channelCommissionNominal || 0);
 },

 // Préparer les données pour le nouveau endpoint generate-report-vat
 prepareBookingsForBackend() {
   // FIX: Utiliser les données sauvegardées pour envoyer EXACTEMENT les mêmes valeurs que l'aperçu
   // Sans aucun recalcul qui pourrait créer des différences
   const source = this.savedReportData || {
     modalData: this.modalData,
     modalSettings: this.modalSettings,
     propertyExpenses: this.propertyExpenses,
     totalAdditionalExpenses: this.totalAdditionalExpenses,
     calculatedMetrics: this.calculatedMetrics
   };

   const reservations = source.modalData.reservations || [];
   const propertyId = source.modalData.realPropertyId;
   const propertyName = source.modalData.propertyName;
   const periodLabel = source.modalData.periodLabel;
   const settings = source.modalSettings;
   const metrics = source.calculatedMetrics;

   // Extraire year et month du periodLabel
   let year, month;
   if (periodLabel === 'Current Month') {
     const now = new Date();
     month = now.getMonth() + 1;
     year = now.getFullYear();
   } else if (periodLabel === 'Last Month') {
     const now = new Date();
     const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
     month = lastMonth.getMonth() + 1;
     year = lastMonth.getFullYear();
   } else {
     const [monthName, yearStr] = periodLabel.split(' ');
     const monthMap = {
       'January': 1, 'February': 2, 'March': 3, 'April': 4,
       'May': 5, 'June': 6, 'July': 7, 'August': 8,
       'September': 9, 'October': 10, 'November': 11, 'December': 12
     };
     month = monthMap[monthName];
     year = parseInt(yearStr);
   }

   // Fonction pour calculer cleaning fee selon le mode
   const calculateCleaningFee = (res) => {
     const isExtension = (res.guestName || '').toLowerCase().includes('extension');
     if (isExtension) return 0;

     if (settings.cleaningMode === 'rentlio') {
       return parseFloat(res.totalServicesPrice || 0);
     } else {
       // Custom Rates: Appliquer les frais uniquement au dernier segment
       const isLastSegment = !res.isSegment || (res.segmentInfo?.part === res.segmentInfo?.total);
       if (isLastSegment) {
         const platform = res.otaChannelName || '';
         if (platform === 'Booking.com') {
           return settings.bookingCleaningFee || 0;
         } else {
           return settings.otherCleaningFee || 0;
         }
       } else {
         return 0;
       }
     }
   };

   // Formater les bookings pour le backend avec calculs custom
   const bookings = reservations.map(res => {
     const amount = parseFloat(res.totalReservationPrice || 0);
     const commission = this.calculateCustomCommission(res, settings);
     const totalNights = parseInt(res.totalNights || 1);

     // Calculate card fee - EXACTLY like in preview (line 1866-1872)
     let cardFee = 0;
     if (settings.cardFeeMode === 'rentlio') {
       cardFee = (res.otaChannelName === 'Booking.com') ? parseFloat(res.cardFee || 0) : 0;
     } else if (settings.cardFeeMode === 'custom') {
       cardFee = (res.otaChannelName === 'Booking.com') ?
         (amount * parseFloat(settings.customCardFeeRate || 0) / 100) : 0;
     }

     const cleaningFee = calculateCleaningFee(res);

     // Calculate exactly like in preview (lines 1901-1903)
     const netIncome = amount - commission - cardFee;
     const grossProfit = netIncome - cleaningFee;
     const pricePerNight = totalNights > 0 ? amount / totalNights : 0;

     return {
       guest_name: res.guestName,
       channel_name: res.otaChannelName,
       total_nights: totalNights,
       total_amount: amount,
       channel_commission: commission,
       card_fee: cardFee,
       cleaning_fee: cleaningFee,
       net_income: netIncome,
       total_services_price: cleaningFee,
       gross_profit: grossProfit,
       price_per_night: pricePerNight,
       other_costs: ''
     };
   });

  const propertyAddressResult = this.getPropertyAddress(propertyId);

  // FIX: NE PLUS RECALCULER - Utiliser directement les valeurs de calculatedMetrics
  // pour garantir que l'email contient EXACTEMENT les mêmes données que l'aperçu
  console.log('prepareBookingsForBackend: Using saved calculatedMetrics', metrics);

  // Récupérer les property expenses avec reçus depuis source
  const expensesWithReceipts = source.propertyExpenses.map(expense => ({
    id: expense.id,
    description: expense.description,
    amount: expense.amount,
    receipt_url: expense.receipt_url,
    receipt_filename: expense.receipt_filename,
    has_receipt: !!expense.receipt_file
  }));

   return {
     property_id: propertyId,
     year: year,
     month: month,
     property_name: propertyName,
     property_address: propertyAddressResult,
     bookings: bookings,

     // FIX: Utiliser DIRECTEMENT les valeurs de calculatedMetrics (pas de recalcul)
     totals: {
       total_guest_payments: metrics.totalRevenue,
       platform_fees: metrics.totalPlatformFees,
       card_fees: metrics.totalCardFees,
       cleaning_fees: metrics.totalOutgoings,
       net_income: metrics.netIncome,
       gross_profit: metrics.grossProfit,
       management_fees: metrics.managementFees,
       vat: metrics.vat,
       consumables: metrics.consumables,
       laundry: metrics.laundry,
       maintenance: metrics.maintenance,
       other_expenses: metrics.otherExpenses,
       additional_expenses: metrics.additionalExpenses,
       settlement_amount: metrics.settlementAmount
     },

     // Envoyer les settings pour le backend (pour info)
     settings: {
       management_fee_base: settings.managementFeeBase,
       management_fee_rate: settings.managementFeeRate,
       management_fee_fixed_amount: settings.managementFeeFixedAmount || 0,
       vat_rate: settings.vatRate,
       vat_business_model: 'agent', // Default
       use_custom_maintenance: settings.useCustomMaintenance || false,
       use_custom_consumables: settings.useCustomConsumables || false,
       use_custom_other_expenses: settings.useCustomOtherExpenses || false
     },

     // Envoyer les expenses avec justificatifs
     expenses: expensesWithReceipts
   };
 },

 // Nouvelle fonction pour préparer les données de rapport pour le backend
 generateReportData() {
   try {
     console.log('=== DEBUT generateReportData ===');
     console.log('savedReportData:', this.savedReportData);
     console.log('modalData:', this.modalData);

     // Utiliser les données sauvegardées si disponibles (pour l'envoi d'email)
     // Sinon utiliser les données du modal actuel
     const sourceData = this.savedReportData || {
       modalData: this.modalData,
       modalSettings: this.modalSettings,
       propertyExpenses: this.propertyExpenses,
       totalAdditionalExpenses: this.totalAdditionalExpenses
     };

     console.log('sourceData utilisé:', sourceData);
     console.log('Nombre de reservations dans sourceData:', sourceData.modalData?.reservations?.length);

     if (sourceData.modalData && sourceData.modalData.reservations) {
       const reservations = sourceData.modalData.reservations;
       const propertyName = sourceData.modalData.propertyName;
       const period = sourceData.modalData.periodLabel;
       const settings = sourceData.modalSettings;

       console.log('Property:', propertyName);
       console.log('Period:', period);
       console.log('Reservations count:', reservations.length);

       // Calculer les outgoings correctement selon le mode de nettoyage
       const calculateOutgoings = (res) => {
         const isExtension = (res.guestName || '').toLowerCase().includes('extension');
         if (isExtension) return 0;

         if (settings.cleaningMode === 'rentlio') {
           return parseFloat(res.totalServicesPrice || 0);
         } else {
           // Custom Rates: Appliquer les frais uniquement au dernier segment
           const isLastSegment = !res.isSegment ||
             (res.segmentInfo?.part === res.segmentInfo?.total);

           if (isLastSegment) {
             const platform = res.otaChannelName || '';
             if (platform === 'Booking.com') {
               return settings.bookingCleaningFee || 150;
             } else {
               return settings.otherCleaningFee || 120;
             }
           } else {
             return 0;
           }
         }
       };

       // Préparer les données des réservations avec outgoings calculés correctement
       const bookings = reservations.map(res => {
         const amount = res.totalReservationPrice || 0;
         const commission = this.calculateCustomCommission(res, settings);
         const cardFee = res.cardFee || 0;
         const outgoings = calculateOutgoings(res);
         const netIncome = amount - commission - cardFee;
         const grossProfit = netIncome - outgoings;

         // IMPORTANT: Utiliser snake_case pour le backend Python
         return {
           guest_name: res.guestName || 'Unknown Guest',
           channel_name: res.otaChannelName || 'Direct',
           total_nights: res.totalNights || 0,
           total_amount: amount,
           channel_commission: commission,
           card_fee: cardFee,
           net_income: netIncome,
           total_services_price: outgoings,
           gross_profit: grossProfit,
           price_per_night: res.pricePerNight || 0,
           net_per_night: res.totalNights > 0 ? netIncome / res.totalNights : 0
         };
       });

       // Calculer les totaux des colonnes (utiliser snake_case car bookings utilise snake_case)
       const totalNights = bookings.reduce((sum, b) => sum + b.total_nights, 0);
       const totalAmount = bookings.reduce((sum, b) => sum + b.total_amount, 0);
       const totalFees = bookings.reduce((sum, b) => sum + b.channel_commission, 0);
       const totalCardFees = bookings.reduce((sum, b) => sum + b.card_fee, 0);
       const totalNetIncome = bookings.reduce((sum, b) => sum + b.net_income, 0);
       const totalOutgoings = bookings.reduce((sum, b) => sum + b.total_services_price, 0);
       const totalGrossProfitBookings = bookings.reduce((sum, b) => sum + b.gross_profit, 0);

       const totalGuestPayments = totalAmount;
       const platformFees = totalFees;
       const cardFees = totalCardFees;
       const cleaningFees = totalOutgoings;
       const grossProfit = totalGrossProfitBookings;

       // Utiliser les settings du modal au lieu de valeurs en dur
       let managementFees = 0;
       if (settings.managementFeeBase === 'revenue') {
         managementFees = totalAmount * (settings.managementFeeRate / 100);
       } else if (settings.managementFeeBase === 'gross') {
         managementFees = grossProfit * (settings.managementFeeRate / 100);
       } else if (settings.managementFeeBase === 'grossFixed') {
         managementFees = parseFloat(settings.managementFeeFixedAmount || 0);
       } else {
         managementFees = totalNetIncome * (settings.managementFeeRate / 100);
       }

       const vat = managementFees * (settings.vatRate / 100);
       const maintenance = settings.useCustomMaintenance
         ? parseFloat(settings.customMaintenanceAmount || 0)
         : parseFloat(settings.maintenance || 0);
       const consumables = settings.useCustomConsumables
         ? parseFloat(settings.customConsumablesAmount || 0)
         : parseFloat(settings.consumables || 0);
       const otherExpenses = settings.useCustomOtherExpenses
         ? parseFloat(settings.customOtherExpensesAmount || 0)
         : parseFloat(settings.otherExpenses || 0);

       const additionalExpensesTotal = sourceData.totalAdditionalExpenses;
       const totalDeductions = managementFees + vat + maintenance + consumables + otherExpenses + additionalExpensesTotal;
       const settlementAmount = grossProfit - totalDeductions;

       // Ajouter les dépenses supplémentaires avec justificatifs
       const expensesWithReceipts = sourceData.propertyExpenses.map(expense => ({
         id: expense.id,
         description: expense.description,
         amount: expense.amount,
         receipt_url: expense.receipt_url,
         receipt_filename: expense.receipt_filename,
         has_receipt: !!expense.receipt_file
       }));

       // IMPORTANT: Retourner les données en snake_case pour le backend Python
      const propertyAddressResult = this.getPropertyAddress(sourceData.modalData.realPropertyId);
       const reportData = {
         property_id: sourceData.modalData.realPropertyId,
         property_name: propertyName,
         property_address: propertyAddressResult,
         report_period: period,
         total_guest_payments: totalGuestPayments,
         platform_fees: platformFees,
         card_fees: cardFees,
         cleaning_fees: cleaningFees,
         gross_profit: grossProfit,
         management_fee_rate: settings.managementFeeRate,
         management_fees: managementFees,
         vat: vat,
         consumables: consumables,
         maintenance: maintenance,
         other_expenses: otherExpenses,
         additionalExpenses: expensesWithReceipts,  // Nouvelles dépenses avec justificatifs
         bookings: bookings,
         total_nights: totalNights,
         total_amount: totalAmount,
         total_fees: totalFees,
         total_card_fees: totalCardFees,
         total_net_income: totalNetIncome,
         total_outgoings: totalOutgoings,
         total_gross_profit: totalGrossProfitBookings,
         avg_per_night: totalNights > 0 ? totalAmount / totalNights : 0,
         avg_net_per_night: totalNights > 0 ? totalNetIncome / totalNights : 0,
         total_deductions: totalDeductions,
         settlement_amount: settlementAmount,
         current_year: new Date().getFullYear()
       };

       console.log('=== REPORT DATA GENERATED ===');
       console.log('Bookings:', reportData.bookings.length);
       console.log('Property:', reportData.property_name);
       console.log('Period:', reportData.report_period);
       console.log('Total amount:', reportData.total_amount);
       console.log('Full reportData:', reportData);

       return reportData;
     } else {
       console.error('No modal data available - sourceData:', sourceData);
       throw new Error('No modal data available for report generation');
     }
   } catch (error) {
     console.error('Error generating report data:', error);
     throw error;
   }
 },

 async prepareReportData(propertyName, period) {
   // Simuler la préparation des données - ajuster selon la logique existante
   const token = localStorage.getItem('token');
   const headers = { 'Authorization': `Bearer ${token}` };
   
   // Récupérer les données selon la période
   let reservations = [];
   if (period === 'Current Month') {
     const response = await axios.get(`${this.baseUrl}/reports/current`, { headers });
     reservations = Object.values(response.data.reservations);
   } else if (period === 'Last Month') {
     const response = await axios.get(`${this.baseUrl}/reports/lastmonth`, { headers });
     reservations = Object.values(response.data.reservations);
   }

   return {
     propertyName,
     periodLabel: period,
     reservations
   };
 },

 generatePDFHTMLForProperty(reportData) {
   // Utiliser la même logique que generatePDFHTML mais avec les données spécifiques
   // Pour simplifier, on retourne un HTML basique - à améliorer selon le besoin
   return `
     <!DOCTYPE html>
     <html>
     <head>
       <title>Report - ${reportData.propertyName}</title>
       <style>
         body { font-family: Arial, sans-serif; padding: 20px; }
         .header { text-align: center; margin-bottom: 30px; }
         .title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
         .subtitle { font-size: 18px; color: #666; }
       </style>
     </head>
     <body>
       <div class="header">
         <div class="title">Monthly Report</div>
         <div class="subtitle">${reportData.propertyName}</div>
         <div class="subtitle">${reportData.periodLabel}</div>
       </div>
       <div class="content">
         <p>Number of reservations: ${reportData.reservations.length}</p>
         <p>Report generated on: ${new Date().toLocaleDateString('en-US')}</p>
       </div>
     </body>
     </html>
   `;
 },

 closeEmailSender() {
   this.showEmailSender = false;
   this.currentPDFFile = null;
   this.currentReportData = {};
 }
 },

 mounted() {
   this.loadContacts();
   this.fetchData();
   this.loadAllPropertySettings(); // Load property settings from API

   // Check if should open contacts modal from URL parameter
   if (this.$route.query.openContacts === 'true') {
     this.openContactsModal();
   }
 }
};

</script>

<style scoped>
/* Styles des segments déplacés en haut */

/* Styles pour les segments de réservations */
.segment-row {
  background: #f8fafc !important;
  border-left: 3px solid #667eea;
}

.segment-row:hover {
  background: #f1f5f9 !important;
}

.segment-badge {
  display: inline-block;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 9px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: help;
}

.segment-badge:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: scale(1.05);
}

/* Sidebar styles removed - using component Sidebar.vue styles */

/* Main Content (minimal styles only) */
.content-header {
 display: flex;
 justify-content: space-between;
 align-items: center;
 margin-bottom: 30px;
}

.content-header h1 {
 font-size: 32px;
 font-weight: 700;
 color: #1a202c;
 margin: 0;
}

.user-info {
 display: flex;
 align-items: center;
 gap: 15px;
}

.logout-btn {
 background: #e53e3e;
 color: white;
 border: none;
 padding: 8px 16px;
 border-radius: 6px;
 cursor: pointer;
 font-weight: 500;
 transition: background 0.3s ease;
}

.logout-btn:hover {
 background: #c53030;
}

/* Section Cards */
.section-card {
 background: white;
 border-radius: 16px;
 box-shadow: 0 4px 25px rgba(0,0,0,0.08);
 margin-bottom: 30px;
 overflow: hidden;
 border: 1px solid #e2e8f0;
}

.section-header {
 display: flex;
 justify-content: space-between;
 align-items: center;
 padding: 25px 30px;
 border-bottom: 1px solid #e2e8f0;
 background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.section-header h2 {
 margin: 0;
 font-size: 20px;
 font-weight: 600;
 color: #1a202c;
}

.section-title-container {
 display: flex;
 flex-direction: column;
 gap: 10px;
}

.property-selector {
 display: flex;
 align-items: center;
 gap: 8px;
}

.property-selector label {
 font-size: 13px;
 font-weight: 600;
 color: #4a5568;
 min-width: 60px;
}

.property-select-small {
 padding: 6px 12px;
 border: 1px solid #d1d5db;
 border-radius: 6px;
 font-size: 13px;
 font-weight: 500;
 min-width: 150px;
 transition: border-color 0.3s ease;
 background: white;
}

.properties-section {
 padding: 0;
}

.property-group {
 border-bottom: 1px solid #e2e8f0;
}

.property-group:last-child {
 border-bottom: none;
}

.property-header {
 display: flex;
 justify-content: space-between;
 align-items: center;
 padding: 20px 30px;
 background: #f8fafc;
 border-bottom: 1px solid #e2e8f0;
}

.property-header h3 {
 margin: 0;
 font-size: 18px;
 font-weight: 600;
 color: #2b6cb0;
}

.property-stats {
 display: flex;
 gap: 20px;
 align-items: center;
}

.stat {
 font-size: 14px;
 color: #64748b;
 font-weight: 500;
}

.stat.revenue {
 font-weight: 700;
 color: #22543d;
 font-size: 16px;
}

.property-actions {
 display: flex;
 gap: 8px;
}

/* Buttons */
.btn {
 display: inline-flex;
 align-items: center;
 justify-content: center;
 padding: 10px 20px;
 border-radius: 8px;
 text-decoration: none;
 font-weight: 600;
 font-size: 14px;
 transition: all 0.3s ease;
 border: 2px solid transparent;
 cursor: pointer;
 min-width: 120px;
}

.btn:disabled {
 opacity: 0.6;
 cursor: not-allowed;
}

.btn-sm {
 padding: 6px 12px;
 font-size: 12px;
 min-width: 70px;
}

.btn-excel {
 background-color: #80CBC4;
 color: white;
 border: 1px solid #80CBC4;
}

.btn-excel:hover:not(:disabled) {
 background-color: #6bb3ad;
 border-color: #6bb3ad;
 transform: translateY(-1px);
}

.btn-report-excel {
 background-color: #FBF8EF;
 color: #333;
 border: 1px solid #d6d2c4;
}

.btn-report-excel:hover:not(:disabled) {
 background-color: #f0ece3;
 border-color: #c9c4b7;
 transform: translateY(-1px);
}

.btn-report {
 background-color: #667eea;
 color: white;
 border: 1px solid #667eea;
}

.btn-report:hover:not(:disabled) {
 background-color: #5a67d8;
 border-color: #5a67d8;
 transform: translateY(-1px);
}

.btn-search {
 background-color: #48BB78;
 color: white;
 border: 1px solid #48BB78;
}

.btn-search:hover:not(:disabled) {
 background-color: #38A169;
 border-color: #38A169;
 transform: translateY(-1px);
}

.btn-outline {
 background: transparent;
 color: #667eea;
 border-color: #667eea;
}

.btn-outline:hover:not(:disabled) {
 background: #667eea;
 color: white;
}

.btn-primary {
 background: #667eea;
 color: white;
 border-color: #667eea;
}

.btn-primary:hover:not(:disabled) {
 background: #5a67d8;
 transform: translateY(-1px);
}

.btn-pdf {
 background-color: #FFB433;
 color: white;
 border: 1px solid #FFB433;
}

.btn-pdf:hover:not(:disabled) {
 background-color: #e5a02b;
 border-color: #e5a02b;
 transform: translateY(-1px);
}

.btn-lock-report {
 background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
 color: white;
 border: none;
}

.btn-lock-report:hover:not(:disabled) {
 background: linear-gradient(135deg, #5568d3 0%, #6a4190 100%);
 transform: translateY(-1px);
 box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* Table Styles */
.table-container {
 overflow-x: auto;
}

.data-table {
 width: 100%;
 border-collapse: collapse;
}

.data-table th {
 background: #f8fafc;
 padding: 16px 20px;
 text-align: left;
 font-weight: 600;
 color: #4a5568;
 font-size: 13px;
 text-transform: uppercase;
 letter-spacing: 0.5px;
 border-bottom: 2px solid #e2e8f0;
}

.data-table td {
 padding: 16px 20px;
 border-bottom: 1px solid #e2e8f0;
 color: #2d3748;
 vertical-align: middle;
}

.data-table tbody tr:hover {
 background: #f7fafc;
}

.property-name {
 font-weight: 600;
 color: #2b6cb0;
}

.guest-name {
 font-weight: 500;
 color: #2d3748;
}

.amount {
 text-align: right;
 font-weight: 600;
 color: #22543d;
}

.text-center {
 text-align: center;
}

.source-badge {
 display: inline-block;
 padding: 4px 8px;
 background: #e2e8f0;
 color: #4a5568;
 border-radius: 12px;
 font-size: 11px;
 font-weight: 600;
 text-transform: uppercase;
}

.loading-state {
 display: flex;
 flex-direction: column;
 align-items: center;
 justify-content: center;
 padding: 60px 20px;
 color: #64748b;
}

.spinner {
 width: 40px;
 height: 40px;
 border: 4px solid #e2e8f0;
 border-top: 4px solid #667eea;
 border-radius: 50%;
 animation: spin 1s linear infinite;
 margin-bottom: 15px;
}

@keyframes spin {
 0% { transform: rotate(0deg); }
 100% { transform: rotate(360deg); }
}

.empty-state {
 padding: 60px 20px;
 text-align: center;
 color: #64748b;
}

.empty-state p {
 font-size: 16px;
 margin: 0;
}

/* Filters */
.filters-section {
 display: flex;
 align-items: end;
 gap: 20px;
 padding: 25px 30px;
 background: #f8fafc;
 border-bottom: 1px solid #e2e8f0;
 flex-wrap: wrap;
}

.filter-group {
 display: flex;
 flex-direction: column;
 gap: 5px;
}

.filter-group label {
 font-size: 13px;
 font-weight: 600;
 color: #4a5568;
 text-transform: uppercase;
 letter-spacing: 0.5px;
}

.filter-select,
.filter-input {
 padding: 8px 12px;
 border: 1px solid #d1d5db;
 border-radius: 6px;
 font-size: 14px;
 min-width: 150px;
 transition: border-color 0.3s ease;
}

.filter-actions {
 display: flex;
 gap: 10px;
 align-items: end;
}

/* Pagination */
.pagination {
 display: flex;
 justify-content: center;
 align-items: center;
 gap: 20px;
 padding: 25px 30px;
 border-top: 1px solid #e2e8f0;
 background: #f8fafc;
}

.pagination-btn {
 background: #667eea;
 color: white;
 border: none;
 padding: 8px 16px;
 border-radius: 6px;
 cursor: pointer;
 font-weight: 500;
 transition: background 0.3s ease;
}

.pagination-btn:hover:not(:disabled) {
 background: #5a67d8;
}

.pagination-btn:disabled {
 background: #cbd5e0;
 cursor: not-allowed;
}

.pagination-info {
 font-size: 14px;
 color: #64748b;
 font-weight: 500;
}

/* Report Modal Styles */
.report-modal-overlay {
 position: fixed;
 top: 0;
 left: 0;
 right: 0;
 bottom: 0;
 background: rgba(0, 0, 0, 0.8);
 display: flex;
 align-items: center;
 justify-content: center;
 z-index: 10000;
 padding: 20px;
}

.report-modal {
 background: white;
 border-radius: 16px;
 width: 95vw;
 height: 90vh;
 max-width: 1600px;
 box-shadow: 0 25px 60px rgba(0, 0, 0, 0.3);
 display: flex;
 flex-direction: column;
 overflow: hidden;
}

.modal-header {
 display: flex;
 justify-content: space-between;
 align-items: flex-start;
 padding: 25px 30px;
 border-bottom: 1px solid #e2e8f0;
 background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
 color: white;
}

.modal-title h2 {
 margin: 0 0 5px 0;
 font-size: 24px;
 font-weight: 700;
}

.modal-subtitle {
 font-size: 14px;
 display: flex;
 align-items: center;
 gap: 6px;
 opacity: 0.9;
}

.unsaved-indicator {
 color: #fbbf24;
 font-size: 12px;
 animation: pulse 2s infinite;
}

.saved-indicator {
 color: #10b981;
 font-size: 12px;
}

@keyframes pulse {
 0%, 100% { opacity: 1; }
 50% { opacity: 0.5; }
}

.close-btn {
 background: none;
 border: none;
 color: white;
 font-size: 30px;
 cursor: pointer;
 padding: 0;
 width: 40px;
 height: 40px;
 display: flex;
 align-items: center;
 justify-content: center;
 border-radius: 8px;
 transition: background 0.3s ease;
}

.close-btn:hover {
 background: rgba(255, 255, 255, 0.2);
}

.modal-content {
 display: flex;
 flex: 1;
 overflow: hidden;
}

.settings-panel {
 width: 400px;
 background: #f8fafc;
 border-right: 1px solid #e2e8f0;
 display: flex;
 flex-direction: column;
 overflow-y: auto;
}

.settings-header {
 padding: 25px 25px 20px;
 border-bottom: 1px solid #e2e8f0;
 background: white;
}

.settings-header h3 {
 margin: 0 0 5px 0;
 font-size: 18px;
 font-weight: 600;
 color: #1a202c;
}

.settings-header p {
 margin: 0;
 font-size: 14px;
 color: #64748b;
}

.settings-form {
 flex: 1;
 padding: 25px;
}

.setting-group {
 margin-bottom: 30px;
}

.subsection-title {
 font-size: 16px;
 font-weight: 600;
 color: #1a202c;
 margin: 0 0 15px 0;
 padding-bottom: 8px;
 border-bottom: 2px solid #e2e8f0;
}

.form-row {
 margin-bottom: 20px;
}

.form-label {
 display: block;
 font-size: 14px;
 font-weight: 600;
 color: #374151;
 margin-bottom: 8px;
}

.input-group {
 position: relative;
 display: flex;
 align-items: center;
}

.input-prefix,
.input-suffix {
 position: absolute;
 color: #6b7280;
 font-weight: 500;
 z-index: 2;
 pointer-events: none;
 font-size: 13px;
}

.input-prefix {
 left: 12px;
}

.input-suffix {
 right: 12px;
}

.form-input {
 width: 100%;
 padding: 12px 16px;
 border: 2px solid #e5e7eb;
 border-radius: 8px;
 font-size: 14px;
 font-weight: 500;
 transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.form-input:focus {
 outline: none;
 border-color: #667eea;
 box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.input-prefix + .form-input {
 padding-left: 35px;
}

.form-input + .input-suffix {
 right: 12px;
}

.radio-group {
 display: flex;
 flex-direction: column;
 gap: 10px;
 margin-top: 8px;
}

.radio-option {
 display: flex;
 align-items: center;
 gap: 8px;
 padding: 12px 14px;
 border: 2px solid #e5e7eb;
 border-radius: 8px;
 cursor: pointer;
 transition: all 0.3s ease;
 font-size: 14px;
}

.radio-option:hover {
 border-color: #667eea;
 background: #f8fafc;
}

.radio-option input[type="radio"] {
 margin: 0;
}

.radio-option input[type="radio"]:checked + span {
 font-weight: 600;
 color: #667eea;
}

.cleaning-custom-rates,
.card-custom-rates {
 margin-top: 15px;
 padding: 15px;
 background: white;
 border-radius: 8px;
 border: 1px solid #e2e8f0;
}

.settings-actions {
 padding: 20px 25px;
 border-top: 1px solid #e2e8f0;
 background: white;
 display: flex;
 gap: 12px;
}

.settings-actions .btn {
 flex: 1;
 min-width: auto;
}

.preview-panel {
 flex: 1;
 display: flex;
 flex-direction: column;
 background: white;
 overflow: hidden;
}

.preview-header {
 display: flex;
 justify-content: space-between;
 align-items: center;
 padding: 25px 30px;
 border-bottom: 1px solid #e2e8f0;
 background: white;
}

.preview-header h3 {
 margin: 0;
 font-size: 18px;
 font-weight: 600;
 color: #1a202c;
}

.preview-actions {
 display: flex;
 gap: 12px;
}

.preview-content {
 flex: 1;
 overflow-y: auto;
 padding: 30px;
 background: #f8f9fa;
}

/* Ajouter ces styles dans la section <style scoped> */

/* PDF Preview Styles - EXACTEMENT IDENTIQUES AU PDF */
.pdf-preview-container {
  font-family: 'Playfair Display', 'Times New Roman', serif;
  background: #f8f9f9;
  color: #2c3e50;
  font-size: 12px;
  line-height: 1.4;
}

.pdf-preview-container .page-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  position: relative;
  background: white;
}

.pdf-preview-container .report-header {
  margin-bottom: 25px;
}

.pdf-preview-container .report-title {
  font-size: 38px;
  font-weight: 400;
  color: #2c3e50;
  margin-bottom: 5px;
  letter-spacing: 1px;
}

.pdf-preview-container .property-name {
  font-size: 22px;
  font-weight: 300;
  color: #34495e;
  margin-bottom: 15px;
  letter-spacing: 0.5px;
}

.pdf-preview-container .divider {
  width: 100%;
  height: 1px;
  background: #bdc3c7;
  margin: 15px 0 12px 0;
}

.pdf-preview-container .report-period {
  font-size: 13px;
  font-weight: 400;
  color: #7f8c8d;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.pdf-preview-container .section-title {
  font-size: 10px;
  font-weight: 600;
  color: #2c3e50;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 20px 0 10px 0;
}

.pdf-preview-container .data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 8px;
  margin-bottom: 15px;
  font-family: 'Arial', sans-serif;
}

.pdf-preview-container .data-table th {
  background: #ecf0f1;
  padding: 5px 3px;
  text-align: center;
  font-weight: 600;
  border: 1px solid #bdc3c7;
  font-size: 7px;
  line-height: 1.2;
  color: #2c3e50;
}

.pdf-preview-container .data-table td {
  padding: 3px 2px;
  border: 1px solid #d5dbdb;
  font-size: 7px;
  vertical-align: middle;
  color: #2c3e50;
}

.pdf-preview-container .data-table .totals-row {
  background: #f8f9fa;
  font-weight: 700;
  border-top: 2px solid #34495e;
}

.pdf-preview-container .data-table .totals-row td {
  border: 1px solid #34495e;
  font-weight: 700;
  color: #2c3e50;
}

.pdf-preview-container .content-sections {
  margin: 15px 0;
}

.pdf-preview-container .summary-section {
  margin-bottom: 20px;
  margin-top: 20px;
}

.pdf-preview-container .summary-title {
  font-size: 10px;
  font-weight: 700;
  margin-bottom: 8px;
  text-transform: uppercase;
  color: #2c3e50;
  letter-spacing: 1px;
}

.pdf-preview-container .summary-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Arial', sans-serif;
}

.pdf-preview-container .summary-table td {
  padding: 2px 0;
  font-size: 8px;
  color: #2c3e50;
  border: none;
}

.pdf-preview-container .summary-table .label {
  text-align: left;
  width: 70%;
}

.pdf-preview-container .summary-table .value {
  text-align: right;
  width: 30%;
  font-weight: 600;
}

.pdf-preview-container .summary-table .total-row {
  border-top: 1px solid #34495e;
  padding-top: 4px;
  margin-top: 4px;
  font-weight: 700;
  font-size: 11px;
}

.pdf-preview-container .summary-table .total-row td {
  padding-top: 4px;
  border-top: 1px solid #34495e;
}

.pdf-preview-container .settlement-section {
  margin: 25px 0;
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  text-align: center;
}

.pdf-preview-container .settlement-title {
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #2c3e50;
}

.pdf-preview-container .settlement-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Arial', sans-serif;
}

.pdf-preview-container .settlement-table td {
  padding: 3px 0;
  font-size: 11px;
  color: #2c3e50;
  border: none;
}

.pdf-preview-container .settlement-table .label {
  text-align: left;
  width: 70%;
}

.pdf-preview-container .settlement-table .value {
  text-align: right;
  width: 30%;
  font-weight: 600;
}

.pdf-preview-container .settlement-table .final-row {
  border-top: 2px solid #2c3e50;
  padding-top: 6px;
  margin-top: 6px;
  font-weight: 700;
  font-size: 13px;
}

.pdf-preview-container .settlement-table .final-row td {
  padding-top: 6px;
  border-top: 2px solid #2c3e50;
}

.pdf-preview-container .footer-section {
  margin-top: auto;
  padding-top: 15px;
  text-align: center;
  border-top: 1px solid #bdc3c7;
}

.pdf-preview-container .prepared-info {
  margin-bottom: 12px;
}

.pdf-preview-container .prepared-label {
  font-size: 8px;
  color: #7f8c8d;
  margin-bottom: 2px;
}

.pdf-preview-container .prepared-company {
  font-size: 10px;
  font-weight: 600;
  color: #2c3e50;
}

.pdf-preview-container .company-logo {
  margin-bottom: 15px;
}

.pdf-preview-container .company-name {
  font-size: 16px;
  font-weight: 300;
  letter-spacing: 6px;
  color: #2c3e50;
  margin-bottom: 1px;
}

.pdf-preview-container .company-tagline {
  font-size: 6px;
  color: #95a5a6;
  text-transform: uppercase;
  letter-spacing: 3px;
}

.pdf-preview-container .footer-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 9px;
  color: #7f8c8d;
  margin-top: 10px;
}



.report-preview .report-header {
 margin-bottom: 30px;
 text-align: center;
}

.report-preview .report-title {
 font-size: 32px;
 font-weight: 700;
 color: #1a202c;
 margin: 0 0 10px 0;
 letter-spacing: 1px;
}

.report-preview .property-name {
 font-size: 24px;
 font-weight: 300;
 color: #34495e;
 margin: 0 0 15px 0;
 letter-spacing: 0.5px;
}

.report-preview .report-period {
 font-size: 14px;
 font-weight: 600;
 color: #7f8c8d;
 text-transform: uppercase;
 letter-spacing: 2px;
}

.report-preview .section-title {
 font-size: 14px;
 font-weight: 600;
 color: #2c3e50;
 text-transform: uppercase;
 letter-spacing: 1px;
 margin: 25px 0 15px 0;
 padding-bottom: 8px;
 border-bottom: 2px solid #e2e8f0;
}

.report-preview .data-table {
 width: 100%;
 border-collapse: collapse;
 font-size: 12px;
 margin-bottom: 20px;
 font-family: Arial, sans-serif;
}

.report-preview .data-table th {
 background: #ecf0f1;
 padding: 10px 8px;
 text-align: center;
 font-weight: 600;
 border: 1px solid #bdc3c7;
 font-size: 11px;
 line-height: 1.2;
 color: #2c3e50;
}

.report-preview .data-table td {
 padding: 8px;
 border: 1px solid #d5dbdb;
 font-size: 11px;
 vertical-align: middle;
 color: #2c3e50;
}

.report-preview .totals-row {
 background: #f8f9fa;
 font-weight: 700;
 border-top: 2px solid #34495e;
}

.report-preview .totals-row td {
 border: 1px solid #34495e;
 font-weight: 700;
 color: #2c3e50;
}

.report-preview .summary-section {
 margin: 25px 0;
}

.report-preview .summary-title {
 font-size: 16px;
 font-weight: 700;
 margin-bottom: 15px;
 text-transform: uppercase;
 color: #2c3e50;
 letter-spacing: 1px;
}

.report-preview .summary-grid {
 display: flex;
 flex-direction: column;
 gap: 8px;
}

.report-preview .summary-item {
 display: flex;
 justify-content: space-between;
 align-items: center;
 padding: 8px 0;
 font-size: 14px;
 color: #2c3e50;
}

.report-preview .summary-item.total {
 border-top: 2px solid #34495e;
 padding-top: 12px;
 margin-top: 10px;
 font-weight: 700;
 font-size: 16px;
}

.report-preview .summary-label {
 font-weight: 500;
}

.report-preview .summary-value {
 font-weight: 600;
 color: #27ae60;
}

.report-preview .settlement-section {
 margin: 30px 0;
 padding: 20px;
 background: #f8f9fa;
 border: 1px solid #e9ecef;
 border-radius: 8px;
 text-align: center;
}

.report-preview .settlement-title {
 font-size: 18px;
 font-weight: 700;
 margin-bottom: 20px;
 text-transform: uppercase;
 letter-spacing: 1px;
 color: #2c3e50;
}

.report-preview .settlement-content {
 display: flex;
 flex-direction: column;
 gap: 12px;
}

.report-preview .settlement-item {
 display: flex;
 justify-content: space-between;
 align-items: center;
 font-size: 14px;
 color: #2c3e50;
}

.report-preview .settlement-item.final {
 border-top: 2px solid #2c3e50;
 padding-top: 15px;
 margin-top: 15px;
 font-weight: 700;
 font-size: 18px;
 color: #27ae60;
}

.report-preview .settlement-label {
 font-weight: 500;
}


.report-preview .settlement-value {
 font-weight: 600;
}

.report-preview .report-footer {
 margin-top: 40px;
 padding-top: 20px;
 border-top: 1px solid #bdc3c7;
 text-align: center;
}

.report-preview .prepared-info {
 margin-bottom: 15px;
}

.report-preview .prepared-label {
 font-size: 12px;
 color: #7f8c8d;
 margin-bottom: 2px;
}

.report-preview .prepared-company {
 font-size: 16px;
 font-weight: 600;
 color: #2c3e50;
}

.report-preview .generation-date {
 font-size: 12px;
 color: #95a5a6;
}

/* Responsive Design */
@media (max-width: 1200px) {
 .report-modal {
   width: 98vw;
   height: 95vh;
 }

 .modal-content {
   flex-direction: column;
 }

 .settings-panel {
   width: 100%;
   height: 50%;
   border-right: none;
   border-bottom: 1px solid #e2e8f0;
 }

 .preview-panel {
   height: 50%;
 }
}

@media (max-width: 768px) {
 .sidebar {
   transform: translateX(-100%);
 }
 
 .main-content {
   margin-left: 0;
   padding: 20px 15px;
 }
 
 .content-header {
   flex-direction: column;
   align-items: flex-start;
   gap: 15px;
 }
 
 .section-header {
   padding: 20px;
   flex-direction: column;
   align-items: flex-start;
   gap: 15px;
 }
 
 .property-header {
   flex-direction: column;
   align-items: flex-start;
   gap: 15px;
   padding: 15px 20px;
 }
 
 .property-stats {
   flex-direction: column;
   align-items: flex-start;
   gap: 8px;
 }
 
 .filters-section {
   flex-direction: column;
   align-items: stretch;
   gap: 15px;
   padding: 20px;
 }
 
 .filter-select,
 .filter-input {
   min-width: 100%;
 }
 
 .filter-actions {
   flex-direction: column;
   width: 100%;
 }
 
 .filter-actions .btn {
   width: 100%;
 }
 
 .data-table {
   font-size: 12px;
 }
 
 .data-table th,
 .data-table td {
   padding: 8px 6px;
 }
 
 .btn {
   width: 100%;
 }

 .report-modal {
   width: 100vw;
   height: 100vh;
   border-radius: 0;
   padding: 0;
 }

 .modal-header {
   padding: 15px 20px;
 }

 .modal-title h2 {
   font-size: 18px;
 }

 .settings-panel {
   height: 60%;
 }

 .preview-panel {
   height: 40%;
 }

 .settings-form {
   padding: 20px;
 }

 .preview-content {
   padding: 20px;
 }

 .report-preview {
   padding: 20px;
 }

 .section-title-container {
   gap: 8px;
 }
 
 .property-selector {
   flex-direction: column;
   align-items: flex-start;
   gap: 5px;
 }
 
 .property-select-small {
   width: 100%;
   min-width: 100%;
 }
}

/* Professional Email Modal Styles */
.email-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.email-modal-container {
  background: white;
  border-radius: 20px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  width: 90%;
  max-width: 650px;
  max-height: 90vh;
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from { 
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Header */
.email-modal-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 20px;
}

.header-icon .icon-circle {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.email-icon {
  font-size: 24px;
}

.header-text h2 {
  margin: 0 0 5px 0;
  font-size: 24px;
  font-weight: 600;
}

.header-text p {
  margin: 0;
  opacity: 0.9;
  font-size: 14px;
}

.modal-close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  transition: all 0.3s ease;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

/* Body */
.email-modal-body {
  padding: 30px;
  max-height: 60vh;
  overflow-y: auto;
}

/* Report Preview Card */
.report-preview-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  margin-bottom: 25px;
  overflow: hidden;
}

.card-header {
  background: white;
  padding: 20px 25px;
  border-bottom: 1px solid #e2e8f0;
}

.card-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
}

.card-content {
  padding: 25px;
}

.report-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 15px;
}

.detail-icon {
  width: 45px;
  height: 45px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 16px;
  color: #1a202c;
  font-weight: 600;
}

/* Recipient Section */
.recipient-section {
  margin-bottom: 25px;
}

.section-title {
  margin-bottom: 20px;
}

.section-title h3 {
  margin: 0 0 5px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
}

.section-title p {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

.recipient-selector {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
}

.contact-dropdown {
  flex: 1;
}

.professional-select {
  width: 100%;
  padding: 15px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #1a202c;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.professional-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.manage-contacts-btn {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px solid #e2e8f0;
  color: #4a5568;
  padding: 15px 20px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.manage-contacts-btn:hover {
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%);
  transform: translateY(-1px);
}

/* Selected Contact Preview */
.selected-contact-preview {
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.contact-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  color: #1a202c;
}

.contact-avatar {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
}

.contact-info {
  flex: 1;
}

.contact-name {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 2px;
}

.contact-email {
  font-size: 14px;
  opacity: 0.9;
}

.status-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  backdrop-filter: blur(10px);
}

/* Email Options */
.email-options-section {
  margin-bottom: 25px;
}

.options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.option-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
}

.option-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.option-icon {
  font-size: 18px;
}

.option-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a202c;
}

.option-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Custom Checkbox */
.checkbox-container {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #4a5568;
  position: relative;
}

.checkbox-container input[type="checkbox"] {
  opacity: 0;
  position: absolute;
}

.checkmark {
  width: 18px;
  height: 18px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.checkbox-container input[type="checkbox"]:checked + .checkmark {
  background: #667eea;
  border-color: #667eea;
}

.checkbox-container input[type="checkbox"]:checked + .checkmark::after {
  content: "✓";
  color: white;
  font-size: 12px;
  font-weight: bold;
}

/* Message Textarea */
.message-textarea {
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.3s ease;
}

.message-textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Footer */
.email-modal-footer {
  background: #f8fafc;
  padding: 25px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e2e8f0;
}

.footer-info {
  display: flex;
  align-items: center;
}

.info-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #64748b;
}

.info-icon {
  font-size: 16px;
}

.footer-actions {
  display: flex;
  gap: 15px;
}

.cancel-btn {
  background: transparent;
  border: 2px solid #e2e8f0;
  color: #4a5568;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e0;
}

.send-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.send-btn:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.send-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-icon {
  font-size: 16px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .email-modal-container {
    width: 95%;
    margin: 20px;
  }
  
  .header-content {
    gap: 15px;
  }
  
  .header-icon .icon-circle {
    width: 50px;
    height: 50px;
  }
  
  .email-modal-body {
    padding: 20px;
  }
  
  .options-grid {
    grid-template-columns: 1fr;
  }
  
  .recipient-selector {
    flex-direction: column;
  }
  
  .email-modal-footer {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
  
  .footer-actions {
    width: 100%;
    justify-content: center;
  }
}

/* Professional Contacts Management Modal Styles */
.contacts-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
}

.contacts-modal-container {
  background: white;
  border-radius: 20px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

/* Header */
.contacts-modal-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.contacts-icon {
  font-size: 24px;
}

/* Body */
.contacts-modal-body {
  padding: 30px;
  max-height: 60vh;
  overflow-y: auto;
}

/* Add Contact Card */
.add-contact-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  margin-bottom: 25px;
  overflow: hidden;
}

.add-contact-card .card-header {
  background: white;
  padding: 20px 25px;
  border-bottom: 1px solid #e2e8f0;
}

.add-contact-card .card-header h3 {
  margin: 0 0 5px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
}

.add-contact-card .card-header p {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

.add-contact-form {
  padding: 25px;
}

.input-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 25px;
}

.input-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.professional-input {
  padding: 15px 20px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #1a202c;
  background: white;
  transition: all 0.3s ease;
}

.professional-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.add-contact-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  padding: 15px 30px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  align-self: flex-start;
}

.add-contact-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.add-contact-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Contacts List Card */
.contacts-list-card {
  background: #f8fafc;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.contacts-list-card .card-header {
  background: white;
  padding: 20px 25px;
  border-bottom: 1px solid #e2e8f0;
}

.header-with-count {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-with-count h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
}

.contact-count {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

/* Empty State */
.empty-contacts-state {
  padding: 60px 20px;
  text-align: center;
  color: #64748b;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.empty-contacts-state h4 {
  margin: 0 0 10px 0;
  font-size: 18px;
  font-weight: 600;
  color: #374151;
}

.empty-contacts-state p {
  margin: 0;
  font-size: 14px;
}

/* Contacts Grid */
.contacts-grid {
  padding: 25px;
  display: grid;
  gap: 15px;
}

.contact-item {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s ease;
}

.contact-item:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.contact-item.editing {
  border-color: #f59e0b;
  background: #fffbeb;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
}

.contact-item .contact-avatar {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

.contact-details {
  flex: 1;
  min-width: 0;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.contact-name {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
}

.contact-email {
  font-size: 14px;
  color: #64748b;
  word-break: break-all;
}

.contact-meta {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
}

/* Edit Form */
.edit-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.edit-input {
  padding: 10px 15px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #1a202c;
  background: white;
  transition: all 0.3s ease;
}

.edit-input:focus {
  outline: none;
  border-color: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
}

/* Contact Actions */
.contact-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.action-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.3s ease;
}

.edit-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
}

.edit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(59, 130, 246, 0.4);
}

.delete-btn {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.delete-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(239, 68, 68, 0.4);
}

.save-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(102, 126, 234, 0.4);
}

.cancel-btn {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  color: white;
}

.cancel-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(107, 114, 128, 0.4);
}

/* Footer */
.contacts-modal-footer {
  background: #f8fafc;
  padding: 25px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e2e8f0;
}

.close-modal-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.close-modal-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

/* Responsive Design for Contacts Modal */
@media (max-width: 768px) {
  .contacts-modal-container {
    width: 95%;
    margin: 20px;
  }
  
  .contacts-modal-body {
    padding: 20px;
  }
  
  .input-group {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .contact-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .contact-details {
    width: 100%;
  }
  
  .contact-actions {
    align-self: flex-end;
  }
  
  .contacts-modal-footer {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
}

/* Email Sent Popup Styles */
.email-sent-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(5px);
}

.email-sent-popup {
  background: white;
  border-radius: 20px;
  padding: 40px;
  max-width: 450px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: popupSlideIn 0.4s ease-out;
}

@keyframes popupSlideIn {
  from {
    opacity: 0;
    transform: scale(0.7) translateY(-50px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.popup-icon {
  margin-bottom: 25px;
}

.success-checkmark {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: block;
  stroke-width: 3;
  stroke: #28a745;
  stroke-miterlimit: 10;
  margin: 0 auto 20px;
  box-shadow: inset 0px 0px 0px #28a745;
  animation: fill 0.4s ease-in-out 0.4s forwards, scale 0.3s ease-in-out 0.9s both;
}

.success-checkmark .check-icon {
  width: 80px;
  height: 80px;
  position: relative;
  border-radius: 50%;
  box-sizing: border-box;
  border: 3px solid #28a745;
}

.success-checkmark .check-icon::before {
  top: 3px;
  left: -2px;
  width: 30px;
  transform-origin: 100% 50%;
  border-radius: 100px 0 0 100px;
}

.success-checkmark .check-icon::after {
  top: 0;
  left: 30px;
  width: 60px;
  transform-origin: 0 50%;
  border-radius: 0 100px 100px 0;
  animation: rotate-circle 4.25s ease-in;
}

.success-checkmark .check-icon::before,
.success-checkmark .check-icon::after {
  content: '';
  height: 100px;
  position: absolute;
  background: white;
  transform: rotate(-45deg);
}

.success-checkmark .check-icon .icon-line {
  height: 3px;
  background-color: #28a745;
  display: block;
  border-radius: 2px;
  position: absolute;
  z-index: 10;
}

.success-checkmark .check-icon .icon-line.line-tip {
  top: 46px;
  left: 14px;
  width: 25px;
  transform: rotate(45deg);
  animation: icon-line-tip 0.75s;
}

.success-checkmark .check-icon .icon-line.line-long {
  top: 38px;
  right: 8px;
  width: 47px;
  transform: rotate(-45deg);
  animation: icon-line-long 0.75s;
}

.success-checkmark .check-icon .icon-circle {
  top: -3px;
  left: -3px;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  position: absolute;
  box-sizing: content-box;
  border: 3px solid #28a745;
}

.success-checkmark .check-icon .icon-fix {
  top: 8px;
  width: 5px;
  left: 26px;
  z-index: 1;
  height: 85px;
  position: absolute;
  transform: rotate(-45deg);
  background-color: white;
}

@keyframes rotate-circle {
  0% {
    transform: rotate(-45deg);
  }
  5% {
    transform: rotate(-45deg);
  }
  12% {
    transform: rotate(-405deg);
  }
  100% {
    transform: rotate(-405deg);
  }
}

@keyframes icon-line-tip {
  0% {
    width: 0;
    left: 1px;
    top: 19px;
  }
  54% {
    width: 0;
    left: 1px;
    top: 19px;
  }
  70% {
    width: 50px;
    left: -8px;
    top: 37px;
  }
  84% {
    width: 17px;
    left: 21px;
    top: 48px;
  }
  100% {
    width: 25px;
    left: 14px;
    top: 45px;
  }
}

@keyframes icon-line-long {
  0% {
    width: 0;
    right: 46px;
    top: 54px;
  }
  65% {
    width: 0;
    right: 46px;
    top: 54px;
  }
  84% {
    width: 55px;
    right: 0px;
    top: 35px;
  }
  100% {
    width: 47px;
    right: 8px;
    top: 38px;
  }
}

.popup-content h2 {
  color: #28a745;
  margin-bottom: 15px;
  font-size: 28px;
  font-weight: 600;
}

.popup-content p {
  color: #555;
  margin-bottom: 10px;
  font-size: 16px;
  line-height: 1.5;
}

.popup-content .delivery-info {
  color: #667eea;
  font-weight: 500;
  background: #f8faff;
  padding: 12px;
  border-radius: 8px;
  margin-top: 15px;
}

.popup-close-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 25px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.popup-close-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
}

.popup-close-btn:active {
  transform: translateY(0);
}

/* Checkbox styles for custom maintenance */
.checkbox-row {
  margin: 8px 0 12px 0;
}

.checkbox-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8fafc;
}

.checkbox-option:hover {
  border-color: #667eea;
  background: #f1f5f9;
}

.checkbox-option input[type="checkbox"] {
  margin: 0;
  width: 16px;
  height: 16px;
}

.checkbox-option input[type="checkbox"]:checked + span {
  font-weight: 600;
  color: #667eea;
}

.setting-description {
  font-size: 12px;
  color: #6b7280;
  margin: 4px 0 0 0;
  font-style: italic;
  line-height: 1.4;
}

/* ========== Property Expenses (Other Maintenance) Styles ========== */

/* Add Expense Button */
.btn-add-expense {
  background: #10b981;
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  margin-top: 12px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-add-expense:hover {
  background: #059669;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.2);
}

/* Expenses List */
.expenses-list {
  background: #f9fafb;
  padding: 15px;
  border-radius: 8px;
  margin-top: 15px;
  border: 1px solid #e5e7eb;
}

.expense-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: white;
  margin-bottom: 8px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
}

.expense-item:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-color: #d1d5db;
}

.expense-desc {
  flex: 1;
  font-weight: 500;
  color: #1f2937;
  font-size: 13px;
}

.expense-amount {
  font-weight: 600;
  color: #059669;
  margin: 0 12px;
  font-size: 14px;
}

.expense-receipt {
  color: #f59e0b;
  font-size: 16px;
  margin: 0 8px;
}

.btn-delete {
  background: #ef4444;
  color: white;
  border: none;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.btn-delete:hover {
  background: #dc2626;
  transform: scale(1.1);
}

.expense-total {
  text-align: right;
  padding-top: 12px;
  border-top: 2px solid #e5e7eb;
  margin-top: 10px;
  color: #1f2937;
  font-size: 14px;
}

/* Expense Modal */
.expense-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(2px);
}

.expense-modal {
  background: white;
  padding: 0;
  border-radius: 16px;
  width: 500px;
  max-width: 90%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.expense-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 30px;
  border-bottom: 1px solid #e5e7eb;
}

.expense-modal-header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 20px;
  font-weight: 700;
}

.expense-modal-header .close-btn {
  background: none;
  border: none;
  font-size: 32px;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.expense-modal-header .close-btn:hover {
  background: #f3f4f6;
  color: #1f2937;
}

.expense-modal-body {
  padding: 30px;
}

.expense-modal-body .form-group {
  margin-bottom: 20px;
}

.expense-modal-body .form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #374151;
  font-size: 14px;
}

.expense-modal-body .form-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.expense-modal-body .form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.expense-modal-body .form-input-file {
  width: 100%;
  padding: 10px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f9fafb;
}

.expense-modal-body .form-input-file:hover {
  border-color: #667eea;
  background: #f3f4f6;
}

.file-selected {
  margin-top: 8px;
  color: #059669;
  font-size: 13px;
  font-weight: 500;
}

.expense-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 30px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 16px 16px;
}

.expense-modal-footer .btn {
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.expense-modal-footer .btn-outline {
  background: transparent;
  color: #6b7280;
  border-color: #d1d5db;
}

.expense-modal-footer .btn-outline:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.expense-modal-footer .btn-primary {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.expense-modal-footer .btn-primary:hover:not(:disabled) {
  background: #5a67d8;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
}

.expense-modal-footer .btn-primary:disabled {
  background: #d1d5db;
  border-color: #d1d5db;
  cursor: not-allowed;
  opacity: 0.6;
}

/* VAT Toggle in expense modal */
.vat-toggle-group {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.vat-toggle-option {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  color: #374151;
  background: #f9fafb;
  text-align: center;
  transition: all 0.15s;
  user-select: none;
}

.vat-toggle-option:hover {
  border-color: #6b7280;
  background: #f3f4f6;
}

.vat-toggle-option.active {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 600;
}

.form-hint {
  font-size: 11px;
  color: #6b7280;
  margin-top: 4px;
  font-style: italic;
}

.form-row-two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.expense-modal-body .input-group {
  display: flex;
  align-items: center;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow: hidden;
}

.expense-modal-body .input-prefix {
  padding: 8px 10px;
  background: #f3f4f6;
  border-right: 1px solid #d1d5db;
  color: #6b7280;
  font-size: 13px;
}

.expense-modal-body .input-group .form-input {
  border: none;
  border-radius: 0;
}

.expense-modal-body .input-group .form-input:focus {
  outline: none;
  box-shadow: none;
}

/* Laundry Per-Booking Styles */
.laundry-per-booking-container {
  margin-top: 15px;
  padding: 15px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.form-sub-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #6b7280;
  font-size: 13px;
}

.booking-counter-group {
  margin-top: 15px;
}

.counter-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.counter-btn {
  width: 40px;
  height: 40px;
  border: 2px solid #3b82f6;
  background: white;
  color: #3b82f6;
  font-size: 22px;
  font-weight: bold;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  line-height: 1;
  padding: 0;
}

.counter-btn:hover:not(:disabled) {
  background: #3b82f6;
  color: white;
  transform: scale(1.05);
}

.counter-btn:disabled {
  border-color: #d1d5db;
  color: #d1d5db;
  cursor: not-allowed;
  opacity: 0.5;
}

.counter-input {
  width: 80px;
  height: 40px;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  border: 2px solid #3b82f6;
  border-radius: 8px;
  padding: 0 10px;
}

.counter-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.counter-info {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.info-icon {
  margin-right: 6px;
}

.adjustment-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  background: #fef3c7;
  color: #92400e;
  border-radius: 4px;
  font-weight: 600;
  font-size: 12px;
}

.total-display {
  padding: 12px;
  background: white;
  border-radius: 6px;
  border: 2px solid #10b981;
  font-size: 14px;
  color: #047857;
}

.total-display strong {
  color: #065f46;
  font-size: 15px;
}

.calculation-hint {
  display: inline-block;
  margin-left: 8px;
  color: #6b7280;
  font-size: 13px;
}

.radio-group {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
}

.radio-option input[type="radio"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.radio-option:hover {
  color: #1f2937;
}

/* ========================================
   MILESTONE 2: LOCK REPORT MODAL STYLES
   ======================================== */

.lock-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.lock-modal-container {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.lock-modal-header {
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.lock-modal-header .header-icon {
  flex-shrink: 0;
}

.lock-modal-header .icon-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.lock-modal-header .header-text {
  flex: 1;
}

.lock-modal-header h2 {
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

.lock-modal-header p {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
}

.lock-modal-header .modal-close-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.lock-modal-header .modal-close-btn:hover {
  background: #f3f4f6;
  color: #4b5563;
}

.lock-modal-body {
  padding: 24px;
}

.report-summary-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e5e7eb;
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-label {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.summary-value {
  font-size: 14px;
  color: #1f2937;
  font-weight: 600;
}

.summary-value.email-highlight {
  color: #667eea;
  font-weight: 700;
}

.warning-box {
  background: #fef3c7;
  border: 2px solid #fbbf24;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  gap: 12px;
}

.warning-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.warning-content h3 {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 700;
  color: #92400e;
}

.warning-content ul {
  margin: 0;
  padding-left: 20px;
  color: #92400e;
}

.warning-content li {
  margin-bottom: 6px;
  font-size: 14px;
  line-height: 1.5;
}

.warning-content strong {
  font-weight: 700;
}

.lock-modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.lock-modal-footer .cancel-btn {
  padding: 10px 20px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #374151;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.lock-modal-footer .cancel-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.lock-modal-footer .confirm-btn {
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 6px;
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.lock-modal-footer .confirm-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* ===================================
   NOTIFICATION MODAL (Replaces Alerts)
   =================================== */

.notification-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease;
}

.notification-modal-container {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: slideUp 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Header Styles */
.notification-modal-header {
  padding: 32px 24px 24px 24px;
  position: relative;
  text-align: center;
}

.notification-modal-header.success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.notification-modal-header.error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.notification-modal-header.warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.notification-modal-header.info {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.notification-icon-circle {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 3px solid rgba(255, 255, 255, 0.3);
}

.notification-icon {
  font-size: 36px;
  line-height: 1;
}

.notification-modal-header .modal-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.notification-modal-header .modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

/* Body Styles */
.notification-modal-body {
  padding: 24px;
  text-align: center;
}

.notification-title {
  margin: 0 0 12px 0;
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}

.notification-message {
  margin: 0 0 20px 0;
  font-size: 16px;
  color: #6b7280;
  line-height: 1.5;
}

.notification-details {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  text-align: left;
}

.notification-details .detail-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #e5e7eb;
}

.notification-details .detail-row:last-child {
  border-bottom: none;
}

.notification-details .detail-label {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.notification-details .detail-value {
  font-size: 14px;
  color: #111827;
  font-weight: 600;
}

/* Footer Styles */
.notification-modal-footer {
  padding: 16px 24px 24px 24px;
  display: flex;
  justify-content: center;
}

.notification-btn {
  padding: 12px 40px;
  border-radius: 8px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  color: white;
  min-width: 120px;
}

.notification-btn.success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.notification-btn.error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.notification-btn.warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.notification-btn.info {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.notification-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.notification-btn:active {
  transform: translateY(0);
}

/* Responsive */
@media (max-width: 640px) {
  .notification-modal-container {
    width: 95%;
    margin: 20px;
  }

  .notification-title {
    font-size: 20px;
  }

  .notification-message {
    font-size: 14px;
  }

  .notification-icon-circle {
    width: 60px;
    height: 60px;
  }

  .notification-icon {
    font-size: 30px;
  }
}

/* ── MAIN ── */
.dashboard-container {
  background: #f5f3ff !important;
}

.main {
  margin-left: 218px;
  flex: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ── TOPBAR ── */
.topbar {
  background: white;
  border-bottom: 1px solid #ebe9f8;
  padding: 0 28px;
  height: 60px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 50;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 14px;
}

.topbar-date {
  font-size: 0.8rem;
  color: #9896b8;
}

.topbar-month {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f5f3ff;
  border: 1px solid #ebe9f8;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #4a4870;
  cursor: pointer;
}

.topbar-month svg {
  opacity: 0.5;
}

.content {
  padding: 28px 36px;
  flex: 1;
}

</style>